import React, { useState, useEffect, useMemo } from "react";
import { 
  TrendingUp, 
  TrendingDown,
  FileText,
  CheckCircle,
  DollarSign,
  Loader2,
  AlertTriangle,
  BarChart3,
  Users,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Database,
  Clock,
  Sparkles,
  Brain,
  Calendar,
  Save,
  Settings2,
  Trash2,
  FolderOpen,
  Info,
  Target,
  AlertCircle,
  PoundSterling,
  Percent,
  Calculator
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { ExportXlsxButton } from "@/pages/sales/ExportXlsxButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Separator } from "@/components/ui/separator";
import { getSalesAnalysis, triggerSalesSync, getSalesSyncStatus, getAISalesInsights, getLatestAISalesInsights, analyzeSalesPlan, getSalesTargets, updateSalesTargets, getSavedScenarios, saveScenario, deleteScenario, getCostOfSales } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart,
  Area,
  ReferenceLine,
  Cell
} from "recharts";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function SalesAnalysis({ defaultTab = "planning" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [expandedBroker, setExpandedBroker] = useState(null);
  const [activeTab, setActiveTab] = useState(defaultTab);
  
  // AI Insights state
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiExpanded, setAiExpanded] = useState(true);
  
  // Plan Analysis state (separate from general AI insights)
  const [planAnalysis, setPlanAnalysis] = useState(null);
  const [planAnalysisLoading, setPlanAnalysisLoading] = useState(false);
  
  // Sales targets state
  const [salesTargets, setSalesTargets] = useState({});
  const [topupPct, setTopupPct] = useState({});
  const [editingTargets, setEditingTargets] = useState(false);
  const [targetsSaving, setTargetsSaving] = useState(false);
  
  // Scenario modeling state - "What-If" levers (APPLIED state - affects page)
  const [scenario, setScenario] = useState({
    approvalRate: null,  // null = use trend, number = override
    rejectionRate: null, // null = use trend, number = override
    appsGrowth: null,    // null = use trend, number = override monthly growth
    avgDealSize: null,   // null = use current, number = override
  });
  
  // Draft scenario state - local edits before applying
  const [draftScenario, setDraftScenario] = useState({
    approvalRate: null,
    rejectionRate: null,
    appsGrowth: null,
    avgDealSize: null,
  });
  const [scenarioHasChanges, setScenarioHasChanges] = useState(false);
  
  // Apply draft scenario to main state
  const applyScenario = () => {
    setScenario({...draftScenario});
    setScenarioHasChanges(false);
  };
  
  // Broker-level scenario overrides
  const [brokerOverrides, setBrokerOverrides] = useState({});
  const [brokerEditorOpen, setBrokerEditorOpen] = useState(false);
  
  // Saved scenarios
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [savingScenario, setSavingScenario] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const [loadedScenarioId, setLoadedScenarioId] = useState(null);
  
  // Year selector for Master Plan
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const availableYears = useMemo(() => {
    const year = new Date().getFullYear();
    return [String(year - 1), String(year), String(year + 1)];
  }, []);
  
  // Cost of Sales data
  const [costOfSalesData, setCostOfSalesData] = useState(null);
  const [costOfSalesLoading, setCostOfSalesLoading] = useState(false);
  
  // Financial Year selector - UK FY runs July to June
  // e.g., FY 2025/26 = July 2025 - June 2026
  const getCurrentFinancialYear = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0-indexed (July = 6)
    // If we're in July-Dec, FY started this year. If Jan-June, FY started last year
    return month >= 6 ? year : year - 1;
  };
  
  // Per-tab FY selectors
  const [planningFY, setPlanningFY] = useState(getCurrentFinancialYear());
  const [performanceFY, setPerformanceFY] = useState(getCurrentFinancialYear());
  const [selectedFY, setSelectedFY] = useState(getCurrentFinancialYear()); // Cost of Sales tab
  
  // Generate FY options (last 3 years + current + next)
  const fyOptions = useMemo(() => {
    const currentFY = getCurrentFinancialYear();
    return [currentFY - 2, currentFY - 1, currentFY, currentFY + 1].map(fy => ({
      value: fy,
      label: `FY ${fy}/${(fy + 1).toString().slice(-2)}` // e.g., "FY 2025/26"
    }));
  }, []);
  
  // Helper to get FY date range
  const getFYDateRange = (fyStartYear) => ({
    start: new Date(fyStartYear, 6, 1), // July 1st
    end: new Date(fyStartYear + 1, 5, 30, 23, 59, 59), // June 30th
    label: `FY ${fyStartYear}/${String(fyStartYear + 1).slice(-2)}`,
    startKey: `${fyStartYear}-07`,
    endKey: `${fyStartYear + 1}-06`
  });
  
  // Broker breakdown period selector - "fy" for FY to date, or specific month key like "2026-02"
  const [brokerBreakdownPeriod, setBrokerBreakdownPeriod] = useState('latest'); // 'fy', 'latest', or month key
  
  // Compute broker breakdown data based on selected period
  const selectedBrokerBreakdown = useMemo(() => {
    if (!costOfSalesData) return { data: [], label: '', period: '' };
    
    if (brokerBreakdownPeriod === 'fy') {
      // FY to Date - use the fy.broker_breakdown
      return {
        data: costOfSalesData.fy?.broker_breakdown || [],
        label: `${costOfSalesData.fy?.label || 'FY'} to Date`,
        period: 'fy',
        totalFunded: costOfSalesData.fy?.funded || 0,
        totalComms: costOfSalesData.fy?.comms || 0,
        totalDeals: costOfSalesData.fy?.count || 0
      };
    } else if (brokerBreakdownPeriod === 'latest') {
      // Latest month (default)
      const latestMonth = costOfSalesData.summary?.latest_month;
      return {
        data: latestMonth?.broker_breakdown || [],
        label: latestMonth?.month_name || 'Current Month',
        period: latestMonth?.month || '',
        totalFunded: latestMonth?.funded || 0,
        totalComms: latestMonth?.comms || 0,
        totalDeals: latestMonth?.count || 0
      };
    } else {
      // Specific month selected
      const selectedMonth = costOfSalesData.monthly?.find(m => m.month === brokerBreakdownPeriod);
      return {
        data: selectedMonth?.broker_breakdown || [],
        label: selectedMonth?.month_name || brokerBreakdownPeriod,
        period: brokerBreakdownPeriod,
        totalFunded: selectedMonth?.funded || 0,
        totalComms: selectedMonth?.comms || 0,
        totalDeals: selectedMonth?.count || 0
      };
    }
  }, [costOfSalesData, brokerBreakdownPeriod]);
  
  // What-If Calculator state - Applied deals (committed to page)
  const [whatIfDeals, setWhatIfDeals] = useState([]);
  // What-If Calculator draft state - Local edits before applying
  const [draftWhatIfDeals, setDraftWhatIfDeals] = useState([
    { id: 1, dealSize: 50000, commsPct: 9, description: '' }
  ]);
  const [whatIfHasChanges, setWhatIfHasChanges] = useState(false);
  
  // Apply draft deals to main state
  const applyWhatIfDeals = () => {
    setWhatIfDeals([...draftWhatIfDeals]);
    setWhatIfHasChanges(false);
  };
  
  // Clear applied deals
  const clearWhatIfDeals = () => {
    setWhatIfDeals([]);
    setDraftWhatIfDeals([{ id: Date.now(), dealSize: 50000, commsPct: 9, description: '' }]);
    setWhatIfHasChanges(false);
  };
  
  // Offer Patterns filters - dynamic year based on current date
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth(); // 0-indexed
  const offerPatternsYears = [currentYear - 1, currentYear, currentYear + 1].map(String);
  
  // Section 1: Offer Rate by Application Date (Monday.com) filters
  const [offerPatternsYear, setOfferPatternsYear] = useState(String(currentYear));
  const [offerPatternsMonth, setOfferPatternsMonth] = useState('all'); // 'all' or '0'-'11'
  const [offerPatternsPeriod, setOfferPatternsPeriod] = useState('daily'); // daily, weekly, monthly
  
  // Section 2: Offer Volume by Offer Date (Airtable) filters - INDEPENDENT
  const [offerVolumeYear, setOfferVolumeYear] = useState(String(currentYear));
  const [offerVolumeMonth, setOfferVolumeMonth] = useState('all'); // 'all' or '0'-'11'
  const [offerVolumePeriod, setOfferVolumePeriod] = useState('daily'); // daily, weekly, monthly
  
  // Funding tab filters
  const [fundingYear, setFundingYear] = useState(String(currentYear));
  const [fundingMonth, setFundingMonth] = useState('all'); // 'all' or '0'-'11'
  const [fundingPeriod, setFundingPeriod] = useState('daily'); // daily, weekly, monthly
  
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const monthNamesShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  // ===== GLOBAL DATE CONTEXT =====
  // Dynamic date references - use these instead of hardcoded values
  const currentDate = useMemo(() => {
    const now = new Date();
    const month = now.getMonth(); // 0-indexed
    const year = now.getFullYear();
    const fyStartYear = month >= 6 ? year : year - 1; // FY starts in July
    
    return {
      // Current month info
      monthIndex: month,
      monthName: monthNames[month],
      monthNameShort: monthNamesShort[month],
      year: year,
      yearShort: String(year).slice(-2),
      
      // Month key for data lookups (YYYY-MM format)
      monthKey: `${year}-${String(month + 1).padStart(2, '0')}`,
      
      // Previous month info
      prevMonthIndex: month === 0 ? 11 : month - 1,
      prevMonthName: monthNames[month === 0 ? 11 : month - 1],
      prevMonthNameShort: monthNamesShort[month === 0 ? 11 : month - 1],
      prevMonthYear: month === 0 ? year - 1 : year,
      
      // Financial year info
      fyStartYear: fyStartYear,
      fyEndYear: fyStartYear + 1,
      fyLabel: `FY ${fyStartYear}/${String(fyStartYear + 1).slice(-2)}`,
      fyStartDate: new Date(fyStartYear, 6, 1), // July 1st
      fyEndDate: new Date(fyStartYear + 1, 5, 30), // June 30th
      
      // Display helpers
      thisMonthLabel: `${monthNamesShort[month]} ${year}`, // e.g., "Feb 2026"
      prevMonthLabel: `${monthNamesShort[month === 0 ? 11 : month - 1]} ${month === 0 ? year - 1 : year}`,
    };
  }, []);
  
  // ===== END GLOBAL DATE CONTEXT =====
  
  // Expanded broker row in NB Forecast table
  const [expandedBrokerRow, setExpandedBrokerRow] = useState(null);
  // Expanded broker row in Broker Performance tab
  const [expandedPerfBroker, setExpandedPerfBroker] = useState(null);
  
  // View toggle states for different sections
  const [offerBucketView, setOfferBucketView] = useState("thisMonth"); // thisMonth, allTime, monthly
  const [brokerOfferView, setBrokerOfferView] = useState("allTime"); // thisMonth, allTime
  const [declineView, setDeclineView] = useState("thisMonth"); // thisMonth, allTime
  const [brokerView, setBrokerView] = useState("all"); // all, newBusiness, topups, thisMonth
  const [underwriterView, setUnderwriterView] = useState("allTime"); // allTime, thisMonth
  const [dailyActivityView, setDailyActivityView] = useState("thisMonth"); // thisMonth, lastMonth
  
  // Standardized period view states for Performance sub-tabs (Phase 3)
  const [overviewPeriod, setOverviewPeriod] = useState('monthly'); // daily, weekly, monthly
  const [funnelPeriod, setFunnelPeriod] = useState('monthly'); // daily, weekly, monthly  
  const [velocityPeriod, setVelocityPeriod] = useState('monthly'); // daily, weekly, monthly
  const [brokersPeriod, setBrokersPeriod] = useState('monthly'); // daily, weekly, monthly
  const [leakagePeriod, setLeakagePeriod] = useState('monthly'); // daily, weekly, monthly
  const [topupsPeriod, setTopupsPeriod] = useState('monthly'); // daily, weekly, monthly
  
  // Period view options for the selector
  const periodOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  // Extract data with defaults for useMemo hooks (must be before any conditional returns)
  const daily_activity_for_memo = data?.daily_activity || {};
  const offer_submissions_for_memo = data?.offer_submissions || null;
  const monthly_trends_for_memo = data?.monthly_trends || [];
  const broker_stats_for_memo = data?.broker_stats_full || [];

  // Filter data by FY for Planning tab
  const planningFYRange = useMemo(() => getFYDateRange(planningFY), [planningFY]);
  const planningMonthlyTrends = useMemo(() => {
    if (!monthly_trends_for_memo || monthly_trends_for_memo.length === 0) return [];
    return monthly_trends_for_memo.filter(m => {
      return m.month_key >= planningFYRange.startKey && m.month_key <= planningFYRange.endKey;
    });
  }, [monthly_trends_for_memo, planningFYRange]);

  // Filter data by FY for Performance tab
  const performanceFYRange = useMemo(() => getFYDateRange(performanceFY), [performanceFY]);
  const performanceMonthlyTrends = useMemo(() => {
    if (!monthly_trends_for_memo || monthly_trends_for_memo.length === 0) return [];
    return monthly_trends_for_memo.filter(m => {
      return m.month_key >= performanceFYRange.startKey && m.month_key <= performanceFYRange.endKey;
    });
  }, [monthly_trends_for_memo, performanceFYRange]);
  
  // Filter broker stats by FY for Performance tab
  const performanceBrokerStats = useMemo(() => {
    if (!broker_stats_for_memo || broker_stats_for_memo.length === 0) return [];
    // broker_stats_full contains aggregated data, we filter monthly_nb arrays if present
    return broker_stats_for_memo.map(broker => ({
      ...broker,
      // If broker has monthly data, filter it by FY
      monthly_nb: (broker.monthly_nb || []).filter(m => 
        m.month_key >= performanceFYRange.startKey && m.month_key <= performanceFYRange.endKey
      )
    }));
  }, [broker_stats_for_memo, performanceFYRange]);

  // Filter offer data based on year, month and period selection
  const filteredOfferData = useMemo(() => {
    if (!daily_activity_for_memo || Object.keys(daily_activity_for_memo).length === 0) {
      return { chartData: [], weekly_offer_rates: [], offer_rate_by_day_of_month: [], xKey: 'day' };
    }
    
    // Filter by year and month
    const filterByYearAndMonth = (dataArr, dateKey = 'day') => {
      if (!dataArr) return [];
      return dataArr.filter(item => {
        const dateStr = item[dateKey];
        if (!dateStr) return false;
        if (dateStr.startsWith('W')) return true;
        try {
          let date;
          if (dateStr.includes('-')) {
            date = new Date(dateStr);
          } else {
            date = new Date(dateStr + ', ' + offerPatternsYear);
          }
          const year = date.getFullYear().toString();
          const month = date.getMonth();
          
          if (year !== offerPatternsYear) return false;
          if (offerPatternsMonth !== 'all' && month !== parseInt(offerPatternsMonth)) return false;
          return true;
        } catch {
          return true;
        }
      });
    };

    const aggregateToWeekly = (dailyData) => {
      if (!dailyData || dailyData.length === 0) return [];
      const weeklyMap = {};
      dailyData.forEach(item => {
        const dateStr = item.day;
        if (!dateStr) return;
        try {
          const date = new Date(dateStr.includes('-') ? dateStr : dateStr + ', ' + offerPatternsYear);
          const weekNum = Math.ceil((date.getDate()) / 7);
          const monthName = date.toLocaleString('en-GB', { month: 'short' });
          const weekKey = `${monthName} W${weekNum}`;
          if (!weeklyMap[weekKey]) {
            weeklyMap[weekKey] = { week: weekKey, apps: 0, offers: 0 };
          }
          weeklyMap[weekKey].apps += item.apps || 0;
          weeklyMap[weekKey].offers += item.offers || 0;
        } catch {}
      });
      return Object.values(weeklyMap).map(w => ({
        ...w,
        offer_rate: w.apps > 0 ? Math.round((w.offers / w.apps) * 1000) / 10 : 0
      }));
    };

    const aggregateToMonthly = (dailyData) => {
      if (!dailyData || dailyData.length === 0) return [];
      const monthlyMap = {};
      dailyData.forEach(item => {
        const dateStr = item.day;
        if (!dateStr) return;
        try {
          const date = new Date(dateStr.includes('-') ? dateStr : dateStr + ', ' + offerPatternsYear);
          const monthKey = date.toLocaleString('en-GB', { month: 'short', year: '2-digit' });
          if (!monthlyMap[monthKey]) {
            monthlyMap[monthKey] = { month: monthKey, apps: 0, offers: 0 };
          }
          monthlyMap[monthKey].apps += item.apps || 0;
          monthlyMap[monthKey].offers += item.offers || 0;
        } catch {}
      });
      return Object.values(monthlyMap).map(m => ({
        ...m,
        offer_rate: m.apps > 0 ? Math.round((m.offers / m.apps) * 1000) / 10 : 0
      }));
    };
    
    // Aggregate to day-of-month pattern (1-31) from filtered daily data
    const aggregateToDayOfMonth = (dailyData) => {
      if (!dailyData || dailyData.length === 0) return [];
      const dayOfMonthMap = {};
      dailyData.forEach(item => {
        const dateStr = item.day;
        if (!dateStr) return;
        try {
          const date = new Date(dateStr.includes('-') ? dateStr : dateStr + ', ' + offerPatternsYear);
          const dom = date.getDate();
          if (!dayOfMonthMap[dom]) {
            dayOfMonthMap[dom] = { day: dom, apps: 0, offers: 0 };
          }
          dayOfMonthMap[dom].apps += item.apps || 0;
          dayOfMonthMap[dom].offers += item.offers || 0;
        } catch {}
      });
      // Return array for days 1-31
      return Array.from({ length: 31 }, (_, i) => {
        const d = dayOfMonthMap[i + 1] || { day: i + 1, apps: 0, offers: 0 };
        return {
          ...d,
          offer_rate: d.apps > 0 ? Math.round((d.offers / d.apps) * 1000) / 10 : 0
        };
      });
    };

    let filteredDaily = filterByYearAndMonth(daily_activity_for_memo.offers_per_day || [], 'day');
    
    // Calculate weekly and day-of-month from the filtered daily data
    const weeklyFromFiltered = aggregateToWeekly(filteredDaily);
    const dayOfMonthFromFiltered = aggregateToDayOfMonth(filteredDaily);
    
    if (offerPatternsPeriod === 'weekly') {
      return {
        chartData: weeklyFromFiltered,
        xKey: 'week',
        weekly_offer_rates: weeklyFromFiltered,
        offer_rate_by_day_of_month: dayOfMonthFromFiltered
      };
    } else if (offerPatternsPeriod === 'monthly') {
      return {
        chartData: aggregateToMonthly(filteredDaily),
        xKey: 'month',
        weekly_offer_rates: weeklyFromFiltered,
        offer_rate_by_day_of_month: dayOfMonthFromFiltered
      };
    }
    
    return {
      chartData: filteredDaily,
      xKey: 'day',
      weekly_offer_rates: weeklyFromFiltered,
      offer_rate_by_day_of_month: dayOfMonthFromFiltered
    };
  }, [daily_activity_for_memo, offerPatternsYear, offerPatternsMonth, offerPatternsPeriod]);

  // Filter offer submissions (Airtable) data - uses INDEPENDENT filters
  const filteredOfferSubmissions = useMemo(() => {
    if (!offer_submissions_for_memo) return null;
    
    const filterByYearAndMonth = (dataArr, dateKey = 'date') => {
      if (!dataArr) return [];
      return dataArr.filter(item => {
        const dateStr = item[dateKey];
        if (!dateStr) return false;
        if (dateStr.startsWith('W')) return true;
        try {
          const date = new Date(dateStr);
          const year = date.getFullYear().toString();
          const month = date.getMonth();
          
          if (year !== offerVolumeYear) return false;
          if (offerVolumeMonth !== 'all' && month !== parseInt(offerVolumeMonth)) return false;
          return true;
        } catch {
          return true;
        }
      });
    };

    const aggregateToWeekly = (dailyData) => {
      if (!dailyData || dailyData.length === 0) return [];
      const weeklyMap = {};
      dailyData.forEach(item => {
        const dateStr = item.date;
        if (!dateStr) return;
        try {
          const date = new Date(dateStr);
          const weekNum = Math.ceil(date.getDate() / 7);
          const monthName = date.toLocaleString('en-GB', { month: 'short' });
          const weekKey = `${monthName} W${weekNum}`;
          if (!weeklyMap[weekKey]) {
            weeklyMap[weekKey] = { week: weekKey, submissions: 0, new_customer: 0, renewal: 0 };
          }
          weeklyMap[weekKey].submissions += item.submissions || 0;
          weeklyMap[weekKey].new_customer += item.new_customer || 0;
          weeklyMap[weekKey].renewal += item.renewal || 0;
        } catch {}
      });
      return Object.values(weeklyMap);
    };

    const aggregateToMonthly = (dailyData) => {
      if (!dailyData || dailyData.length === 0) return [];
      const monthlyMap = {};
      dailyData.forEach(item => {
        const dateStr = item.date;
        if (!dateStr) return;
        try {
          const date = new Date(dateStr);
          const monthKey = date.toLocaleString('en-GB', { month: 'short', year: '2-digit' });
          if (!monthlyMap[monthKey]) {
            monthlyMap[monthKey] = { month: monthKey, submissions: 0, new_customer: 0, renewal: 0 };
          }
          monthlyMap[monthKey].submissions += item.submissions || 0;
          monthlyMap[monthKey].new_customer += item.new_customer || 0;
          monthlyMap[monthKey].renewal += item.renewal || 0;
        } catch {}
      });
      return Object.values(monthlyMap);
    };

    const filteredDaily = filterByYearAndMonth(offer_submissions_for_memo.daily_submissions || [], 'date');
    
    if (offerVolumePeriod === 'weekly') {
      return {
        ...offer_submissions_for_memo,
        chartData: aggregateToWeekly(filteredDaily),
        xKey: 'week',
        dataKey: 'submissions'
      };
    } else if (offerVolumePeriod === 'monthly') {
      return {
        ...offer_submissions_for_memo,
        chartData: aggregateToMonthly(filteredDaily),
        xKey: 'month',
        dataKey: 'submissions'
      };
    }
    
    return {
      ...offer_submissions_for_memo,
      chartData: filteredDaily,
      xKey: 'date',
      dataKey: 'submissions'
    };
  }, [offer_submissions_for_memo, offerVolumeYear, offerVolumeMonth, offerVolumePeriod]);

  // Filter funding data based on year, month and period selection
  const all_funded_per_day_for_memo = data?.daily_activity?.all_funded_per_day || [];
  
  const filteredFundingData = useMemo(() => {
    if (!all_funded_per_day_for_memo || all_funded_per_day_for_memo.length === 0) {
      return { chartData: [], weeklyData: [], dayOfMonthData: [], xKey: 'day' };
    }
    
    // Filter by year and month
    const filterByYearAndMonth = (dataArr) => {
      if (!dataArr) return [];
      return dataArr.filter(item => {
        const dateStr = item.day;
        if (!dateStr) return false;
        try {
          const date = new Date(dateStr);
          const year = date.getFullYear().toString();
          const month = date.getMonth();
          
          if (year !== fundingYear) return false;
          if (fundingMonth !== 'all' && month !== parseInt(fundingMonth)) return false;
          return true;
        } catch {
          return true;
        }
      });
    };

    const aggregateToWeekly = (dailyData) => {
      if (!dailyData || dailyData.length === 0) return [];
      const weeklyMap = {};
      dailyData.forEach(item => {
        const dateStr = item.day;
        if (!dateStr) return;
        try {
          const date = new Date(dateStr);
          const weekNum = Math.ceil(date.getDate() / 7);
          const monthName = date.toLocaleString('en-GB', { month: 'short' });
          const weekKey = `${monthName} W${weekNum}`;
          if (!weeklyMap[weekKey]) {
            weeklyMap[weekKey] = { week: weekKey, count: 0, value: 0 };
          }
          weeklyMap[weekKey].count += item.count || 0;
          weeklyMap[weekKey].value += item.value || 0;
        } catch {}
      });
      return Object.values(weeklyMap);
    };

    const aggregateToMonthly = (dailyData) => {
      if (!dailyData || dailyData.length === 0) return [];
      const monthlyMap = {};
      dailyData.forEach(item => {
        const dateStr = item.day;
        if (!dateStr) return;
        try {
          const date = new Date(dateStr);
          const monthKey = date.toLocaleString('en-GB', { month: 'short', year: '2-digit' });
          if (!monthlyMap[monthKey]) {
            monthlyMap[monthKey] = { month: monthKey, count: 0, value: 0 };
          }
          monthlyMap[monthKey].count += item.count || 0;
          monthlyMap[monthKey].value += item.value || 0;
        } catch {}
      });
      return Object.values(monthlyMap);
    };
    
    // Aggregate to day-of-month pattern (1-31) from filtered daily data
    const aggregateToDayOfMonth = (dailyData) => {
      if (!dailyData || dailyData.length === 0) return [];
      const dayOfMonthMap = {};
      dailyData.forEach(item => {
        const dateStr = item.day;
        if (!dateStr) return;
        try {
          const date = new Date(dateStr);
          const dom = date.getDate();
          if (!dayOfMonthMap[dom]) {
            dayOfMonthMap[dom] = { day: dom, count: 0, value: 0 };
          }
          dayOfMonthMap[dom].count += item.count || 0;
          dayOfMonthMap[dom].value += item.value || 0;
        } catch {}
      });
      return Array.from({ length: 31 }, (_, i) => dayOfMonthMap[i + 1] || { day: i + 1, count: 0, value: 0 });
    };

    const filteredDaily = filterByYearAndMonth(all_funded_per_day_for_memo);
    const weeklyFromFiltered = aggregateToWeekly(filteredDaily);
    const dayOfMonthFromFiltered = aggregateToDayOfMonth(filteredDaily);
    
    // Calculate totals for the filtered period
    const totalCount = filteredDaily.reduce((sum, d) => sum + (d.count || 0), 0);
    const totalValue = filteredDaily.reduce((sum, d) => sum + (d.value || 0), 0);
    const last5DaysCount = dayOfMonthFromFiltered.slice(26).reduce((sum, d) => sum + (d.count || 0), 0);
    const last5DaysValue = dayOfMonthFromFiltered.slice(26).reduce((sum, d) => sum + (d.value || 0), 0);
    const restCount = dayOfMonthFromFiltered.slice(0, 26).reduce((sum, d) => sum + (d.count || 0), 0);
    const restValue = dayOfMonthFromFiltered.slice(0, 26).reduce((sum, d) => sum + (d.value || 0), 0);
    
    if (fundingPeriod === 'weekly') {
      return {
        chartData: weeklyFromFiltered,
        xKey: 'week',
        weeklyData: weeklyFromFiltered,
        dayOfMonthData: dayOfMonthFromFiltered,
        totalCount,
        totalValue,
        monthEndAnalysis: {
          last5Days: { count: last5DaysCount, value: last5DaysValue },
          restOfMonth: { count: restCount, value: restValue },
          surgeIndicator: restCount > 0 ? Math.round(((last5DaysCount / 5) / (restCount / 26) - 1) * 100) : 0
        }
      };
    } else if (fundingPeriod === 'monthly') {
      return {
        chartData: aggregateToMonthly(filteredDaily),
        xKey: 'month',
        weeklyData: weeklyFromFiltered,
        dayOfMonthData: dayOfMonthFromFiltered,
        totalCount,
        totalValue,
        monthEndAnalysis: {
          last5Days: { count: last5DaysCount, value: last5DaysValue },
          restOfMonth: { count: restCount, value: restValue },
          surgeIndicator: restCount > 0 ? Math.round(((last5DaysCount / 5) / (restCount / 26) - 1) * 100) : 0
        }
      };
    }
    
    return {
      chartData: filteredDaily,
      xKey: 'day',
      weeklyData: weeklyFromFiltered,
      dayOfMonthData: dayOfMonthFromFiltered,
      totalCount,
      totalValue,
      monthEndAnalysis: {
        last5Days: { count: last5DaysCount, value: last5DaysValue },
        restOfMonth: { count: restCount, value: restValue },
        surgeIndicator: restCount > 0 ? Math.round(((last5DaysCount / 5) / (restCount / 26) - 1) * 100) : 0
      }
    };
  }, [all_funded_per_day_for_memo, fundingYear, fundingMonth, fundingPeriod]);

  useEffect(() => {
    loadData();
    loadSyncStatus();
    loadTargets();
    loadSavedScenarios();
    loadCostOfSales();
  }, []);

  const loadCostOfSales = async (fyYear = selectedFY) => {
    try {
      setCostOfSalesLoading(true);
      const result = await getCostOfSales(fyYear);
      setCostOfSalesData(result);
    } catch (error) {
      console.error("Failed to load cost of sales:", error);
    } finally {
      setCostOfSalesLoading(false);
    }
  };
  
  // Reload Cost of Sales when FY changes
  useEffect(() => {
    if (activeTab === 'cost-of-sales') {
      loadCostOfSales(selectedFY);
    }
  }, [selectedFY]);

  const loadTargets = async () => {
    try {
      const result = await getSalesTargets();
      setSalesTargets(result.targets || {});
      setTopupPct(result.topup_pct || {});
    } catch (error) {
      console.error("Failed to load targets:", error);
    }
  };

  const loadSavedScenarios = async () => {
    try {
      const result = await getSavedScenarios();
      setSavedScenarios(result.scenarios || []);
    } catch (error) {
      console.error("Failed to load scenarios:", error);
    }
  };

  const handleSaveScenario = async () => {
    if (!scenarioName.trim()) {
      toast.error("Please enter a scenario name");
      return;
    }
    try {
      setSavingScenario(true);
      // Filter out null values from global_overrides - backend expects Dict[str, float]
      const filteredGlobalOverrides = Object.fromEntries(
        Object.entries(scenario).filter(([_, v]) => v !== null && v !== undefined)
      );
      const scenarioData = {
        name: scenarioName,
        description: `Created ${new Date().toLocaleDateString("en-GB")}`,
        global_overrides: filteredGlobalOverrides,
        broker_overrides: Object.entries(brokerOverrides)
          .filter(([_, v]) => Object.values(v).some(val => val !== null))
          .map(([broker_name, overrides]) => ({
            broker_name,
            // Also filter null values in broker overrides
            ...Object.fromEntries(Object.entries(overrides).filter(([_, v]) => v !== null && v !== undefined))
          }))
      };
      const result = await saveScenario(scenarioData);
      toast.success("Scenario saved successfully");
      setScenarioName('');
      setLoadedScenarioId(result.scenario.id);
      await loadSavedScenarios();
    } catch (error) {
      console.error("Failed to save scenario:", error);
      toast.error("Failed to save scenario");
    } finally {
      setSavingScenario(false);
    }
  };

  const handleLoadScenario = (savedScenario) => {
    // Apply global overrides
    const loadedOverrides = savedScenario.global_overrides || {
      approvalRate: null,
      rejectionRate: null,
      appsGrowth: null,
      avgDealSize: null
    };
    setScenario(loadedOverrides);
    setDraftScenario(loadedOverrides);
    setScenarioHasChanges(false);
    
    // Apply broker overrides
    const newBrokerOverrides = {};
    (savedScenario.broker_overrides || []).forEach(bo => {
      newBrokerOverrides[bo.broker_name] = {
        approval_rate: bo.approval_rate,
        rejection_rate: bo.rejection_rate,
        apps_growth: bo.apps_growth,
        avg_deal_size: bo.avg_deal_size
      };
    });
    setBrokerOverrides(newBrokerOverrides);
    setLoadedScenarioId(savedScenario.id);
    toast.success(`Loaded scenario: ${savedScenario.name}`);
  };

  const handleDeleteScenario = async (scenarioId, e) => {
    e.stopPropagation();
    if (!confirm('Delete this scenario?')) return;
    try {
      await deleteScenario(scenarioId);
      toast.success("Scenario deleted");
      if (loadedScenarioId === scenarioId) {
        setLoadedScenarioId(null);
      }
      await loadSavedScenarios();
    } catch (error) {
      toast.error("Failed to delete scenario");
    }
  };

  const resetScenario = () => {
    setScenario({ approvalRate: null, rejectionRate: null, appsGrowth: null, avgDealSize: null });
    setDraftScenario({ approvalRate: null, rejectionRate: null, appsGrowth: null, avgDealSize: null });
    setScenarioHasChanges(false);
    setBrokerOverrides({});
    setLoadedScenarioId(null);
  };

  const saveTargets = async () => {
    try {
      setTargetsSaving(true);
      await updateSalesTargets(salesTargets, topupPct);
      toast.success("Sales targets updated successfully");
      setEditingTargets(false);
    } catch (error) {
      console.error("Failed to save targets:", error);
      toast.error("Failed to save targets");
    } finally {
      setTargetsSaving(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const result = await getSalesAnalysis();
      setData(result);
      
      // If no data, prompt for sync
      if (result.needs_sync) {
        toast.info("No data available. Click 'Sync Data' to fetch from Monday.com");
      }
    } catch (error) {
      console.error("Failed to load sales analysis:", error);
      toast.error("Failed to load sales analysis data");
    } finally {
      setLoading(false);
    }
  };

  const loadAIInsights = async () => {
    try {
      setAiLoading(true);
      const result = await getAISalesInsights();
      setAiInsights(result);
      toast.success("AI insights generated successfully");
    } catch (error) {
      console.error("Failed to load AI insights:", error);
      toast.error(error?.message || "Failed to generate AI insights");
    } finally {
      setAiLoading(false);
    }
  };

  // Show the last generated overview straight away (generation runs server-side)
  useEffect(() => {
    getLatestAISalesInsights()
      .then((d) => { if (d?.status === "completed" && d.insights) setAiInsights(d); })
      .catch(() => {});
  }, []);

  const analyzePlan = async () => {
    try {
      setPlanAnalysisLoading(true);
      const scenarioData = {
        global_overrides: scenario,
        broker_overrides: brokerOverrides,
        targets: salesTargets,
        topup_pct: topupPct
      };
      const result = await analyzeSalesPlan(scenarioData);
      setPlanAnalysis(result);
      toast.success("Plan analysis complete");
    } catch (error) {
      console.error("Failed to analyze plan:", error);
      toast.error("Failed to analyze plan");
    } finally {
      setPlanAnalysisLoading(false);
    }
  };

  const loadSyncStatus = async () => {
    try {
      const status = await getSalesSyncStatus();
      setSyncStatus(status);
    } catch (error) {
      console.error("Failed to load sync status:", error);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      await triggerSalesSync();
      toast.success("Data sync started! This may take a few minutes...");
      
      // Poll for completion
      const pollInterval = setInterval(async () => {
        const status = await getSalesSyncStatus();
        setSyncStatus(status);
        
        if (status.status === "completed") {
          clearInterval(pollInterval);
          setSyncing(false);
          toast.success(`Sync complete! ${status.items_synced} items synced.`);
          loadData();
        } else if (status.status === "failed") {
          clearInterval(pollInterval);
          setSyncing(false);
          toast.error("Sync failed: " + status.error);
        }
      }, 5000);
      
      // Stop polling after 5 minutes max
      setTimeout(() => {
        clearInterval(pollInterval);
        if (syncing) {
          setSyncing(false);
          toast.info("Sync is still running in background. Refresh to check status.");
        }
      }, 300000);
      
    } catch (error) {
      console.error("Failed to trigger sync:", error);
      toast.error("Failed to start sync");
      setSyncing(false);
    }
  };

  const formatLastSync = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMins = Math.floor((now - date) / 60000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} mins ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return date.toLocaleDateString("en-GB");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading data from MongoDB...</span>
      </div>
    );
  }

  if (!data || data.needs_sync) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Database className="w-12 h-12 text-blue-500 mb-4" />
        <p className="text-lg font-medium mb-2">No Data Available</p>
        <p className="text-muted-foreground mb-4">Sync data from Monday.com to get started</p>
        <Button onClick={handleSync} disabled={syncing}>
          {syncing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Sync Data from Monday.com
            </>
          )}
        </Button>
        {syncStatus && (
          <p className="text-xs text-muted-foreground mt-4">
            Last sync: {formatLastSync(syncStatus.last_sync)} 
            {syncStatus.items_synced > 0 && ` (${syncStatus.items_synced} items)`}
          </p>
        )}
      </div>
    );
  }

  const { 
    summary, 
    current_month, 
    monthly_trends, 
    top_brokers, 
    problem_brokers,
    daily_activity = {},
    underwriter_stats = [],
    underwriter_this_month = [],
    broker_stats_full = [],
    decline_reasons = [],
    broker_this_month = [],
    top_brokers_ytd = [],
    weekly_trends = [],
    offer_analytics = {},
    topup_forecast = {},
    deal_velocity = {},
    rejection_analysis = {},
    offer_submissions = null
  } = data;

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="font-semibold">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value.toLocaleString()}
              {entry.name.includes("Rate") || entry.name.includes("%") ? "%" : ""}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div data-testid="sales-analysis-page">
      <PageHeader
        title="Sales Planning & Analytics"
        description="Plan your sales targets and track performance"
      >
        <div className="flex items-center gap-2">
          {/* Sync status indicator */}
          {syncStatus?.last_sync && (
            <div className="flex items-center text-xs text-muted-foreground mr-2" data-testid="sales-sync-health"
              title={`${syncStatus.items_synced?.toLocaleString?.() || 0} Monday items on file${syncStatus.duration_seconds ? ` · last run ${Math.round(syncStatus.duration_seconds / 60)} min` : ""}${syncStatus.items_removed ? ` · ${syncStatus.items_removed} removed` : ""} · one row per Monday item (unique index)`}>
              <Clock className="w-3 h-3 mr-1" />
              {formatLastSync(syncStatus.last_sync)}
              {syncStatus.items_synced > 0 && <span className="ml-1 font-mono">· {syncStatus.items_synced.toLocaleString()} items</span>}
              {syncStatus.status === "running" && <span className="ml-2 text-amber-600">• sync running{syncStatus.items_so_far ? ` (${syncStatus.items_so_far.toLocaleString()})` : ""}</span>}
              {syncStatus.status === "failed" && <span className="ml-2 text-red-600" title={syncStatus.error || ""}>• last sync failed</span>}
              {syncStatus.next_scheduled_sync && (
                <span className="ml-2 text-blue-600" title={`Next auto-sync: ${new Date(syncStatus.next_scheduled_sync).toLocaleString("en-GB")}`}>
                  • Auto-sync 6am GMT
                </span>
              )}
            </div>
          )}
            <ExportXlsxButton sheets="all" fy={performanceFY} />
            <Button onClick={handleSync} variant="outline" size="sm" disabled={syncing}>
              {syncing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Database className="w-4 h-4 mr-2" />
                  Sync Now
                </>
              )}
            </Button>
            <Button onClick={loadData} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
      </PageHeader>

      {/* AI Insights Widget - Collapsible */}
      <Card className={`mb-6 border-purple-200 ${aiExpanded ? 'bg-gradient-to-r from-purple-50 to-indigo-50' : 'bg-purple-50/50'}`}>
        <CardHeader className={aiExpanded ? "pb-2" : "pb-0"}>
          <div className="flex items-center justify-between">
            <div 
              className="flex items-center gap-2 cursor-pointer flex-1"
              onClick={() => setAiExpanded(!aiExpanded)}
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-lg flex items-center gap-2">
                  AI Performance Overview
                  <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs">GPT-5.5</Badge>
                  {aiInsights && !aiExpanded && (
                    <Badge variant="outline" className="text-xs text-purple-600">Click to expand</Badge>
                  )}
                </CardTitle>
                {aiExpanded && <CardDescription>AI-powered analysis of your sales performance data</CardDescription>}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); setAiExpanded(!aiExpanded); }}
                className="ml-2"
              >
                {aiExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={(e) => { e.stopPropagation(); loadAIInsights(); }} 
                variant="outline" 
                size="sm" 
                disabled={aiLoading}
                className="border-purple-300 hover:bg-purple-100"
              >
                {aiLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {aiInsights ? 'Refresh' : 'Generate Insights'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {aiExpanded && (
          <CardContent>
            {!aiInsights && !aiLoading && (
              <div className="text-center py-8 text-muted-foreground">
                <Sparkles className="w-12 h-12 mx-auto mb-3 text-purple-300" />
                <p>Click "Generate Insights" to get AI-powered analysis of your sales data</p>
                <p className="text-xs mt-1">Uses OpenAI GPT-5.5 to analyze trends, identify issues, and recommend actions</p>
              </div>
            )}
            {aiLoading && (
              <div className="text-center py-8">
                <Loader2 className="w-12 h-12 mx-auto mb-3 animate-spin text-purple-500" />
                <p className="text-muted-foreground">Analyzing your sales data with AI...</p>
                <p className="text-xs text-muted-foreground mt-1">This may take 10-20 seconds</p>
              </div>
            )}
            {aiInsights && !aiLoading && (
              <div className="prose prose-sm max-w-none dark:prose-invert">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({node, ...props}) => <h1 className="text-xl font-bold mt-4 mb-2 text-purple-900" {...props} />,
                    h2: ({node, ...props}) => <h2 className="text-lg font-semibold mt-4 mb-2 text-purple-800 border-b border-purple-200 pb-1" {...props} />,
                    h3: ({node, ...props}) => <h3 className="text-base font-semibold mt-3 mb-1 text-purple-700" {...props} />,
                    p: ({node, ...props}) => <p className="my-2 text-slate-700" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc pl-5 my-2 space-y-1" {...props} />,
                    ol: ({node, ...props}) => <ol className="list-decimal pl-5 my-2 space-y-1" {...props} />,
                    li: ({node, ...props}) => <li className="text-slate-700" {...props} />,
                    strong: ({node, ...props}) => <strong className="font-semibold text-purple-900" {...props} />,
                    em: ({node, ...props}) => <em className="text-purple-700" {...props} />,
                    table: ({node, ...props}) => <div className="overflow-x-auto my-3"><table className="text-xs border-collapse w-auto min-w-[50%]" {...props} /></div>,
                    th: ({node, ...props}) => <th className="border border-purple-200 bg-purple-50 px-2 py-1 text-left font-semibold text-purple-900" {...props} />,
                    td: ({node, ...props}) => <td className="border border-purple-100 px-2 py-1 text-slate-700" {...props} />,
                  }}
                >
                  {aiInsights.insights}
                </ReactMarkdown>
                <div className="mt-4 pt-3 border-t border-purple-200 flex items-center justify-between text-xs text-muted-foreground">
                  <span>Generated: {new Date(aiInsights.generated_at).toLocaleString("en-GB")}</span>
                  <span>Data as of: {aiInsights.data_as_of ? new Date(aiInsights.data_as_of).toLocaleString("en-GB") : 'Unknown'}</span>
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Summary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{currentDate.monthNameShort} {currentDate.year} Apps</p>
                <p className="text-3xl font-bold">{current_month.applications}</p>
                <p className={`text-sm ${current_month.vs_prev_apps >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {current_month.vs_prev_apps >= 0 ? '+' : ''}{current_month.vs_prev_apps} vs {currentDate.prevMonthNameShort}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{currentDate.monthNameShort} {currentDate.year} Offer Rate</p>
                <p className="text-3xl font-bold">{current_month.offer_rate}%</p>
                <p className="text-sm text-muted-foreground">
                  {current_month.offers} offers / {current_month.applications} apps
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{currentDate.monthNameShort} {currentDate.year} Funded</p>
                <p className="text-3xl font-bold">{current_month.funded}</p>
                <p className={`text-sm ${current_month.vs_prev_funded >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {current_month.vs_prev_funded >= 0 ? '+' : ''}{current_month.vs_prev_funded} vs {currentDate.prevMonthNameShort}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{currentDate.monthNameShort} {currentDate.year} Conv. Rate</p>
                <p className="text-3xl font-bold">{current_month.conversion_rate}%</p>
                <p className="text-sm text-muted-foreground">
                  Offers → Funded
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alert if conversion is low despite high approval */}
      {current_month.offer_rate > 30 && current_month.conversion_rate < 30 && (
        <Card className="mb-6 border-amber-300 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-amber-600 mt-1" />
              <div>
                <h3 className="font-semibold text-amber-800">Conversion Gap Detected</h3>
                <p className="text-sm text-amber-700 mt-1">
                  Your offer rate ({current_month.offer_rate}%) is healthy, but conversion to funded ({current_month.conversion_rate}%) is low.
                  This suggests deals are being approved but not completing. Check: Doc signing delays, customer dropouts, or funding bottlenecks.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ==================== OVERVIEW DASHBOARD ==================== */}
      <Card className="mb-6 border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50/50">
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="text-center p-3 bg-white rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-slate-800">{current_month.applications?.toLocaleString() || 0}</div>
              <div className="text-xs text-slate-500">Apps {currentDate.monthNameShort}</div>
              <div className={`text-xs mt-1 ${(current_month.applications - (monthly_trends[monthly_trends.length - 2]?.applications || 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {current_month.applications - (monthly_trends[monthly_trends.length - 2]?.applications || 0) >= 0 ? '+' : ''}
                {current_month.applications - (monthly_trends[monthly_trends.length - 2]?.applications || 0)} vs {currentDate.prevMonthNameShort}
              </div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-blue-600">{current_month.offer_rate?.toFixed(1) || 0}%</div>
              <div className="text-xs text-slate-500">Offer Rate</div>
              <div className="text-xs text-slate-400 mt-1">{current_month.offers || 0} offers</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-emerald-600">£{((current_month.funded_value || 0) / 1000000).toFixed(2)}M</div>
              <div className="text-xs text-slate-500">Funded {currentDate.monthNameShort}</div>
              <div className="text-xs text-slate-400 mt-1">{current_month.funded || 0} deals</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-purple-600">{current_month.conversion_rate?.toFixed(1) || 0}%</div>
              <div className="text-xs text-slate-500">Conversion Rate</div>
              <div className="text-xs text-slate-400 mt-1">Offer → Funded</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-amber-600">£{((current_month.funded_value || 0) / Math.max(current_month.funded || 1, 1) / 1000).toFixed(0)}k</div>
              <div className="text-xs text-slate-500">Avg Deal Size</div>
              <div className="text-xs text-slate-400 mt-1">{currentDate.monthNameShort}</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg shadow-sm border-2 border-emerald-200">
              <div className="text-2xl font-bold text-emerald-700">£{(Object.values(salesTargets)[0] / 1000000 || 2.5).toFixed(1)}M</div>
              <div className="text-xs text-slate-500">Monthly Target</div>
              <div className={`text-xs mt-1 font-medium ${(current_month.funded_value || 0) >= (Object.values(salesTargets)[0] || 2500000) ? 'text-emerald-600' : 'text-red-600'}`}>
                {(((current_month.funded_value || 0) / (Object.values(salesTargets)[0] || 2500000)) * 100).toFixed(0)}% achieved
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ==================== MAIN TABS ==================== */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="h-auto gap-2 p-1 bg-slate-100">
          <TabsTrigger 
            value="planning" 
            className="px-6 py-2.5 text-sm font-medium data-[state=active]:bg-emerald-600 data-[state=active]:text-white"
          >
            <Target className="w-4 h-4 mr-2" />
            Planning
          </TabsTrigger>
          <TabsTrigger 
            value="performance"
            className="px-6 py-2.5 text-sm font-medium data-[state=active]:bg-blue-600 data-[state=active]:text-white"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Performance & Reporting
          </TabsTrigger>
          <TabsTrigger 
            value="cost-of-sales"
            className="px-6 py-2.5 text-sm font-medium data-[state=active]:bg-rose-600 data-[state=active]:text-white"
          >
            <PoundSterling className="w-4 h-4 mr-2" />
            Cost of Sales
          </TabsTrigger>
        </TabsList>

        {/* ==================== PLANNING TAB ==================== */}
        <TabsContent value="planning" className="space-y-6">
          {/* FY Selector for Planning Tab */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                <Calendar className="w-3 h-3 mr-1" />
                {planningFYRange.label}
              </Badge>
              <span className="text-xs text-muted-foreground">
                (Jul {planningFY} - Jun {planningFY + 1})
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Financial Year:</span>
              <Select value={String(planningFY)} onValueChange={(val) => setPlanningFY(parseInt(val))}>
                <SelectTrigger className="w-[140px]" data-testid="planning-fy-selector">
                  <SelectValue placeholder="Select FY" />
                </SelectTrigger>
                <SelectContent>
                  {fyOptions.map(opt => (
                    <SelectItem key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Sub-tabs for Master Plan, NB Planning, TU Planning, AI Analysis */}
          <Tabs defaultValue="master-plan" className="space-y-4">
            <TabsList className="bg-emerald-50 border border-emerald-200">
              <TabsTrigger value="master-plan" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
                <Target className="w-4 h-4 mr-1" />
                Master Plan
              </TabsTrigger>
              <TabsTrigger value="nb-planning" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                New Business
              </TabsTrigger>
              <TabsTrigger value="tu-planning" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                Top-ups
              </TabsTrigger>
              <TabsTrigger value="ai-analysis" className="data-[state=active]:bg-slate-600 data-[state=active]:text-white">
                <Brain className="w-4 h-4 mr-1" />
                AI Plan Analysis
              </TabsTrigger>
            </TabsList>

            {/* MASTER PLAN Tab - Combined Overview */}
            <TabsContent value="master-plan" className="space-y-6">
          {/* Funding Forecast with Editable Top-up % */}
          <Card className="border-emerald-300 bg-emerald-50/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-emerald-600" />
                    Master Funding Plan
                  </CardTitle>
                  <CardDescription>
                    Combined NB + TU forecast vs monthly targets. Completed months show actual performance.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {/* Year Selector */}
                  <Select value={selectedYear} onValueChange={setSelectedYear}>
                    <SelectTrigger className="w-24">
                      <Calendar className="w-4 h-4 mr-1" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableYears.map(year => (
                        <SelectItem key={year} value={year}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editingTargets ? (
                    <>
                      <Button variant="outline" size="sm" onClick={() => { setEditingTargets(false); loadTargets(); }}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={saveTargets} disabled={targetsSaving}>
                        {targetsSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Save
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setEditingTargets(true)}>
                      Edit Targets
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Stacked Forecast Chart - Full Year */}
              <div className="mb-6">
                <h4 className="text-sm font-medium mb-3">
                  {selectedYear} Funding Plan: Target = Expected Top-ups + NB Required
                </h4>
                <ResponsiveContainer width="100%" height={350}>
                  <ComposedChart data={(() => {
                    // Build data for all 12 months of selected year
                    const currentMonth = new Date().toISOString().slice(0, 7);
                    const months = [];
                    for (let m = 1; m <= 12; m++) {
                      months.push(`${selectedYear}-${String(m).padStart(2, '0')}`);
                    }
                    
                    return months.map(key => {
                      const target = salesTargets[key] || 2000000;
                      const tuPct = (topupPct[key] || 55) / 100;
                      const expectedTU = target * tuPct;
                      const nbNeeded = target - expectedTU;
                      const actual = monthly_trends.find(m => m.month_key === key);
                      const monthLabel = new Date(key + '-01').toLocaleDateString('en-GB', { month: 'short' });
                      const isComplete = key < currentMonth;
                      const isCurrent = key === currentMonth;
                      
                      return {
                        month: monthLabel,
                        month_key: key,
                        target,
                        expectedTU: isComplete ? 0 : expectedTU,  // Don't show expected for completed months
                        nbNeeded: isComplete ? 0 : nbNeeded,
                        actualFunded: actual?.funded_value || 0,
                        actualTU: actual?.topup_value || 0,
                        actualNB: (actual?.funded_value || 0) - (actual?.topup_value || 0),
                        tuPct: (topupPct[key] || 55),
                        isComplete,
                        isCurrent
                      };
                    });
                  })()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(v) => `£${(v/1000000).toFixed(1)}m`} />
                    <Tooltip 
                      formatter={(value, name) => [`£${(value/1000).toFixed(0)}k`, name]}
                      labelFormatter={(label) => {
                        const item = (() => {
                          const currentMonth = new Date().toISOString().slice(0, 7);
                          const months = [];
                          for (let m = 1; m <= 12; m++) {
                            months.push(`${selectedYear}-${String(m).padStart(2, '0')}`);
                          }
                          return months.find(key => {
                            const monthLabel = new Date(key + '-01').toLocaleDateString('en-GB', { month: 'short' });
                            return monthLabel === label;
                          });
                        })();
                        const currentMonth = new Date().toISOString().slice(0, 7);
                        if (item && item < currentMonth) return `${label} (Complete)`;
                        if (item === currentMonth) return `${label} (In Progress)`;
                        return `${label} (Forecast)`;
                      }}
                    />
                    <Legend />
                    {/* Actual values for completed months */}
                    <Bar dataKey="actualTU" stackId="actual" fill="#a78bfa" name="Actual Top-ups" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="actualNB" stackId="actual" fill="#60a5fa" name="Actual NB" radius={[0, 0, 0, 0]} />
                    {/* Expected values for future months */}
                    <Bar dataKey="expectedTU" stackId="forecast" fill="#8b5cf6" fillOpacity={0.5} name="Expected Top-ups" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="nbNeeded" stackId="forecast" fill="#3b82f6" fillOpacity={0.5} name="NB Required" radius={[4, 4, 0, 0]} />
                    {/* Target line */}
                    <Line type="monotone" dataKey="target" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Target" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {/* Editable Planning Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-100">
                      <th className="text-left py-3 px-3 font-semibold">Month</th>
                      <th className="text-right py-3 px-3 font-semibold">Target</th>
                      <th className="text-right py-3 px-3 font-semibold bg-purple-50">Top-up %</th>
                      <th className="text-right py-3 px-3 font-semibold bg-purple-50">Expected TU</th>
                      <th className="text-right py-3 px-3 font-semibold bg-blue-50">NB Required</th>
                      <th className="text-right py-3 px-3 font-semibold">Actual Funded</th>
                      <th className="text-right py-3 px-3 font-semibold">Actual TU</th>
                      <th className="text-right py-3 px-3 font-semibold">Actual NB</th>
                      <th className="text-right py-3 px-3 font-semibold">Variance</th>
                      <th className="text-center py-3 px-3 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const currentMonth = new Date().toISOString().slice(0, 7);
                      const months = [];
                      for (let m = 1; m <= 12; m++) {
                        months.push(`${selectedYear}-${String(m).padStart(2, '0')}`);
                      }
                      return months;
                    })().map((key, idx) => {
                      const currentMonth = new Date().toISOString().slice(0, 7);
                      const target = salesTargets[key] || 2000000;
                      const tuPctVal = topupPct[key] || 55;
                      const expectedTU = target * (tuPctVal / 100);
                      const nbNeeded = target - expectedTU;
                      const actual = monthly_trends.find(m => m.month_key === key);
                      const actualFunded = actual?.funded_value || 0;
                      const actualTU = actual?.topup_value || 0;
                      const actualNB = actualFunded - actualTU;
                      const variance = actualFunded - target;
                      const monthLabel = new Date(key + '-01').toLocaleDateString('en-GB', { month: 'short' });
                      const isComplete = key < currentMonth;
                      const isCurrent = key === currentMonth;
                      const isFuture = key > currentMonth;
                      
                      return (
                        <tr 
                          key={key} 
                          className={`
                            ${isComplete ? 'bg-emerald-50/50' : isCurrent ? 'bg-amber-50/50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
                            ${isCurrent ? 'border-l-4 border-l-amber-400' : ''}
                          `}
                        >
                          <td className={`py-3 px-3 font-medium ${isComplete ? 'text-emerald-700' : isCurrent ? 'text-amber-700' : ''}`}>
                            {monthLabel}
                          </td>
                          <td className="text-right py-3 px-3">
                            {editingTargets ? (
                              <Input
                                type="number"
                                value={salesTargets[key] || ''}
                                onChange={(e) => setSalesTargets({...salesTargets, [key]: parseFloat(e.target.value) || 0})}
                                className="h-8 w-28 text-right"
                              />
                            ) : (
                              `£${(target/1000).toFixed(0)}k`
                            )}
                          </td>
                          <td className="text-right py-3 px-3 bg-purple-50/50">
                            {editingTargets ? (
                              <Input
                                type="number"
                                value={topupPct[key] || 55}
                                onChange={(e) => setTopupPct({...topupPct, [key]: parseFloat(e.target.value) || 55})}
                                className="h-8 w-20 text-right"
                                min={0}
                                max={100}
                              />
                            ) : (
                              <span className="font-medium text-purple-700">{tuPctVal}%</span>
                            )}
                          </td>
                          <td className="text-right py-3 px-3 bg-purple-50/50 text-purple-600">
                            {isFuture ? `£${(expectedTU/1000).toFixed(0)}k` : '-'}
                          </td>
                          <td className="text-right py-3 px-3 bg-blue-50/50 text-blue-600 font-medium">
                            {isFuture ? `£${(nbNeeded/1000).toFixed(0)}k` : '-'}
                          </td>
                          <td className={`text-right py-3 px-3 font-medium ${isComplete ? 'text-emerald-700' : ''}`}>
                            {(isComplete || isCurrent) && actualFunded > 0 ? `£${(actualFunded/1000).toFixed(0)}k` : isFuture ? '-' : '£0'}
                          </td>
                          <td className={`text-right py-3 px-3 ${isComplete ? 'text-purple-600' : ''}`}>
                            {(isComplete || isCurrent) && actualTU > 0 ? `£${(actualTU/1000).toFixed(0)}k` : isFuture ? '-' : '£0'}
                          </td>
                          <td className={`text-right py-3 px-3 ${isComplete ? 'text-blue-600' : ''}`}>
                            {(isComplete || isCurrent) && actualNB > 0 ? `£${(actualNB/1000).toFixed(0)}k` : isFuture ? '-' : '£0'}
                          </td>
                          <td className="text-right py-3 px-3">
                            {(isComplete || isCurrent) && actualFunded > 0 ? (
                              <Badge className={variance >= 0 ? 'bg-green-500' : 'bg-red-500'}>
                                {variance >= 0 ? '+' : ''}£{(variance/1000).toFixed(0)}k
                              </Badge>
                            ) : '-'}
                          </td>
                          <td className="text-center py-3 px-3">
                            {isComplete ? (
                              <Badge className="bg-emerald-500">Complete</Badge>
                            ) : isCurrent ? (
                              <Badge className="bg-amber-500">In Progress</Badge>
                            ) : (
                              <Badge variant="outline" className="text-slate-500">Forecast</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
            </TabsContent>

            {/* NB PLANNING Tab */}
            <TabsContent value="nb-planning" className="space-y-6">
          {/* Scenario Modeling with Master Overrides */}
          <Card className="border-blue-300 bg-blue-50/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-blue-600" />
                    New Business Scenario Planner
                  </CardTitle>
                  <CardDescription>
                    Model different scenarios using master overrides or edit individual broker metrics below.
                  </CardDescription>
                </div>
                {/* Save Scenario - Prominent Position */}
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Scenario name..."
                    value={scenarioName}
                    onChange={(e) => setScenarioName(e.target.value)}
                    className="w-48 h-9"
                  />
                  <Button 
                    onClick={handleSaveScenario}
                    disabled={savingScenario || !scenarioName.trim()}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {savingScenario ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Save
                  </Button>
                  {savedScenarios.length > 0 && (
                    <div className="flex gap-1">
                      {savedScenarios.slice(0, 3).map(s => (
                        <Button
                          key={s.id}
                          variant={loadedScenarioId === s.id ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleLoadScenario(s)}
                          className="text-xs"
                        >
                          {s.name}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Master Override Controls - Compact Grid */}
              <div className="mb-6 p-4 bg-white rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-blue-800 flex items-center gap-2">
                    <Settings2 className="w-4 h-4" />
                    Master Overrides
                    {scenarioHasChanges && (
                      <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-300 text-xs">
                        Unsaved changes
                      </Badge>
                    )}
                  </h4>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => {
                        setDraftScenario({ approvalRate: null, rejectionRate: null, appsGrowth: null, avgDealSize: null });
                        setScenario({ approvalRate: null, rejectionRate: null, appsGrowth: null, avgDealSize: null });
                        setScenarioHasChanges(false);
                      }}
                      className="text-xs"
                    >
                      Reset All
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={applyScenario}
                      disabled={!scenarioHasChanges}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Apply to Forecast
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Approval Rate */}
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-slate-600">Approval Rate (%)</label>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={() => { setDraftScenario(s => ({...s, approvalRate: Math.max(5, (s.approvalRate || 20) - 1)})); setScenarioHasChanges(true); }}
                      >-</Button>
                      <Input
                        type="number"
                        min="5" max="50" step="0.5"
                        value={draftScenario.approvalRate ?? ''}
                        onChange={(e) => { setDraftScenario(s => ({...s, approvalRate: e.target.value === '' ? null : parseFloat(e.target.value)})); setScenarioHasChanges(true); }}
                        placeholder="Auto"
                        className="h-8 w-20 text-center"
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={() => { setDraftScenario(s => ({...s, approvalRate: Math.min(50, (s.approvalRate || 20) + 1)})); setScenarioHasChanges(true); }}
                      >+</Button>
                    </div>
                  </div>
                  {/* Rejection Rate */}
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-slate-600">Rejection Rate (%)</label>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={() => { setDraftScenario(s => ({...s, rejectionRate: Math.max(20, (s.rejectionRate || 50) - 5)})); setScenarioHasChanges(true); }}
                      >-</Button>
                      <Input
                        type="number"
                        min="20" max="90" step="1"
                        value={draftScenario.rejectionRate ?? ''}
                        onChange={(e) => { setDraftScenario(s => ({...s, rejectionRate: e.target.value === '' ? null : parseFloat(e.target.value)})); setScenarioHasChanges(true); }}
                        placeholder="Auto"
                        className="h-8 w-20 text-center"
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={() => { setDraftScenario(s => ({...s, rejectionRate: Math.min(90, (s.rejectionRate || 50) + 5)})); setScenarioHasChanges(true); }}
                      >+</Button>
                    </div>
                  </div>
                  {/* Apps Growth */}
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-slate-600">Apps Growth/mo</label>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={() => { setDraftScenario(s => ({...s, appsGrowth: (s.appsGrowth || 0) - 10})); setScenarioHasChanges(true); }}
                      >-</Button>
                      <Input
                        type="number"
                        min="-100" max="200" step="10"
                        value={draftScenario.appsGrowth ?? ''}
                        onChange={(e) => { setDraftScenario(s => ({...s, appsGrowth: e.target.value === '' ? null : parseFloat(e.target.value)})); setScenarioHasChanges(true); }}
                        placeholder="Auto"
                        className="h-8 w-20 text-center"
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={() => { setDraftScenario(s => ({...s, appsGrowth: (s.appsGrowth || 0) + 10})); setScenarioHasChanges(true); }}
                      >+</Button>
                    </div>
                  </div>
                  {/* Avg Deal Size */}
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-slate-600">Avg Deal (£k)</label>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={() => { setDraftScenario(s => ({...s, avgDealSize: Math.max(5000, (s.avgDealSize || 19000) - 1000)})); setScenarioHasChanges(true); }}
                      >-</Button>
                      <Input
                        type="number"
                        min="5" max="50" step="1"
                        value={draftScenario.avgDealSize ? Math.round(draftScenario.avgDealSize / 1000) : ''}
                        onChange={(e) => { setDraftScenario(s => ({...s, avgDealSize: e.target.value === '' ? null : parseFloat(e.target.value) * 1000})); setScenarioHasChanges(true); }}
                        placeholder="Auto"
                        className="h-8 w-20 text-center"
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={() => { setDraftScenario(s => ({...s, avgDealSize: (s.avgDealSize || 19000) + 1000})); setScenarioHasChanges(true); }}
                      >+</Button>
                    </div>
                  </div>
                </div>
                {(scenario.approvalRate !== null || scenario.rejectionRate !== null || scenario.appsGrowth !== null || scenario.avgDealSize !== null) && (
                  <p className="text-xs text-blue-600 mt-2">
                    Master overrides apply to ALL brokers. Edit individual brokers below for finer control.
                  </p>
                )}
              </div>

              {/* NB Forecast Chart with TU Shadow */}
              {(() => {
                // Calculate current trends from last 3 months
                const recent = monthly_trends.slice(-3);
                const latest = monthly_trends[monthly_trends.length - 1] || {};
                
                // Current metrics from latest month
                const currentApps = latest.applications || 1700;
                const currentApprovalRate = latest.offers && latest.applications 
                  ? (latest.offers / latest.applications * 100) : 20;
                const currentRejectionRate = latest.rejected && latest.offers 
                  ? (latest.rejected / latest.offers * 100) : 70;
                const currentAvgDeal = latest.funded_value && latest.funded 
                  ? (latest.funded_value / latest.funded) : 19000;
                
                // Apps trend - capped to realistic bounds
                const rawAppsTrend = recent.length >= 2 
                  ? (recent[recent.length-1]?.applications - recent[0]?.applications) / (recent.length - 1)
                  : 0;
                const appsTrend = Math.max(-currentApps * 0.05, Math.min(currentApps * 0.05, rawAppsTrend));
                
                // Scenario values
                const scenarioApproval = scenario.approvalRate ?? currentApprovalRate;
                const scenarioRejection = scenario.rejectionRate ?? currentRejectionRate;
                const scenarioAppsGrowth = scenario.appsGrowth ?? appsTrend;
                const scenarioAvgDeal = scenario.avgDealSize ?? currentAvgDeal;
                
                // Build broker forecast with overrides
                const brokers = (broker_stats_full || [])
                  .filter(b => b.name && !b.name.includes('(TU)') && b.name !== 'Direct/Unknown')
                  .map(b => ({
                    name: b.name,
                    latestApps: b.latest_nb_apps || b.typical_monthly_nb_apps || Math.round((b.nb_apps || 0) / 12),
                    typicalApps: b.typical_monthly_nb_apps || Math.round((b.nb_apps || 0) / 12),
                    growthTrend: b.nb_apps_growth_trend || 0,
                    approvalRate: b.nb_approval_rate || 0,
                    conversionRate: b.nb_conversion_rate || 0,
                    rejectionRate: b.rejection_rate || 0,
                    avgDealSize: b.nb_avg_deal_size || currentAvgDeal,
                    nbValue: b.nb_value || 0,
                    monthlyNb: b.monthly_nb || []
                  }));
                
                const mainBrokers = brokers.filter(b => b.typicalApps >= 5);
                const smallBrokers = brokers.filter(b => b.typicalApps > 0 && b.typicalApps < 5);
                
                // Calculate forecasts
                const doNothingForecast = [];
                const scenarioForecast = [];
                const tuForecast = [];
                
                for (let i = 1; i <= 6; i++) {
                  const targetMonth = new Date();
                  targetMonth.setMonth(targetMonth.getMonth() + i);
                  const monthLabel = targetMonth.toLocaleDateString('en-GB', { month: 'short' });
                  
                  // NB - Do Nothing
                  let doNothingTotal = 0;
                  let scenarioTotal = 0;
                  
                  mainBrokers.forEach(b => {
                    const projectedApps = Math.max(0, b.latestApps + (b.growthTrend * i));
                    const baseValue = projectedApps * (b.approvalRate / 100) * (b.conversionRate / 100) * b.avgDealSize;
                    doNothingTotal += isNaN(baseValue) ? 0 : baseValue;
                    
                    // Apply overrides for scenario
                    // Only use global overrides if they're explicitly set, otherwise use broker's own values
                    const override = brokerOverrides[b.name] || {};
                    const sApproval = override.approval_rate != null ? override.approval_rate : (scenario.approvalRate != null ? scenario.approvalRate : b.approvalRate);
                    
                    // Calculate scenario conversion rate with proper handling
                    let sConversion = b.conversionRate;
                    if (override.rejection_rate != null) {
                      const baseRejectFactor = Math.max(0.01, (100 - b.rejectionRate) / 100);
                      sConversion = ((100 - override.rejection_rate) / 100) * b.conversionRate / baseRejectFactor;
                    } else if (scenario.rejectionRate != null) {
                      const baseRejectFactor = Math.max(0.01, (100 - b.rejectionRate) / 100);
                      sConversion = ((100 - scenario.rejectionRate) / 100) * b.conversionRate / baseRejectFactor;
                    }
                    
                    const sApps = projectedApps + ((override.apps_growth ?? 0) * i);
                    const sDealSize = scenario.avgDealSize != null ? scenario.avgDealSize : b.avgDealSize;
                    const sValue = sApps * (sApproval / 100) * (sConversion / 100) * sDealSize;
                    scenarioTotal += isNaN(sValue) ? 0 : sValue;
                  });
                  
                  // Add small brokers
                  const othersValue = smallBrokers.reduce((sum, b) => sum + (b.typicalApps * (b.approvalRate / 100) * (b.conversionRate / 100) * b.avgDealSize), 0);
                  doNothingTotal += othersValue;
                  scenarioTotal += othersValue;
                  
                  // TU forecast
                  const sourceMonth = new Date();
                  sourceMonth.setMonth(sourceMonth.getMonth() + i - 3);
                  const sourceMonthKey = sourceMonth.toISOString().slice(0, 7);
                  const sourceFunded = monthly_trends.find(m => m.month_key === sourceMonthKey)?.funded_value || 0;
                  const tuPct = topupPct[targetMonth.toISOString().slice(0, 7)] || 43.6;
                  const expectedTU = sourceFunded * (tuPct / 100);
                  
                  doNothingForecast.push({ month: monthLabel, value: doNothingTotal });
                  scenarioForecast.push({ month: monthLabel, value: scenarioTotal });
                  tuForecast.push({ month: monthLabel, value: expectedTU });
                }
                
                const doNothingTotal = doNothingForecast.reduce((s, m) => s + m.value, 0);
                const scenarioTotalVal = scenarioForecast.reduce((s, m) => s + m.value, 0);
                const tuTotalVal = tuForecast.reduce((s, m) => s + m.value, 0);
                
                // Broker contributions for table
                const brokerContributions = mainBrokers.map(b => {
                  const override = brokerOverrides[b.name] || {};
                  const baseValue = b.latestApps * (b.approvalRate / 100) * (b.conversionRate / 100) * b.avgDealSize;
                  return {
                    ...b,
                    override,
                    hasOverride: Object.values(override).some(v => v != null),
                    monthlyNBValue: baseValue
                  };
                }).sort((a, b) => b.monthlyNBValue - a.monthlyNBValue);
                
                return (
                  <>
                    {/* Forecast Chart */}
                    <div className="mb-6">
                      <h4 className="text-sm font-medium mb-3">6-Month NB Forecast + Expected TU (shadow)</h4>
                      <ResponsiveContainer width="100%" height={280}>
                        <ComposedChart data={doNothingForecast.map((dn, i) => ({
                          month: dn.month,
                          doNothing: dn.value,
                          withChanges: scenarioForecast[i].value,
                          tuShadow: tuForecast[i]?.value || 0,
                          target: salesTargets[`${currentDate.year}-${String((new Date().getMonth() + 2 + i) % 12 || 12).padStart(2, '0')}`] || 2000000
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis tickFormatter={(v) => `£${(v/1000000).toFixed(1)}m`} />
                          <Tooltip 
                            formatter={(v, name) => [`£${(v/1000).toFixed(0)}k`, name]}
                            labelFormatter={(label) => `Month: ${label}`}
                          />
                          <Legend />
                          <Bar dataKey="doNothing" stackId="doNothing" fill="#94a3b8" name="NB (Do Nothing)" radius={[0, 0, 0, 0]} />
                          <Bar dataKey="tuShadow" stackId="doNothing" fill="#c4b5fd" fillOpacity={0.5} name="Expected TU" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="withChanges" stackId="withChanges" fill="#3b82f6" name="NB (With Changes)" radius={[0, 0, 0, 0]} />
                          <Bar dataKey="tuShadow" stackId="withChanges" fill="#a78bfa" fillOpacity={0.4} name=" " radius={[4, 4, 0, 0]} />
                          <Line type="monotone" dataKey="target" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Target" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {/* Summary Cards */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="p-4 bg-slate-100 rounded-lg text-center">
                        <div className="text-2xl font-bold text-slate-700">£{isNaN(doNothingTotal) ? '0.00' : (doNothingTotal/1000000).toFixed(2)}M</div>
                        <div className="text-xs text-slate-500">NB "Do Nothing" (6mo)</div>
                      </div>
                      <div className="p-4 bg-blue-100 rounded-lg text-center">
                        <div className="text-2xl font-bold text-blue-700">£{isNaN(scenarioTotalVal) ? '0.00' : (scenarioTotalVal/1000000).toFixed(2)}M</div>
                        <div className="text-xs text-blue-600">NB "With Changes" (6mo)</div>
                      </div>
                      <div className={`p-4 rounded-lg text-center ${scenarioTotalVal > doNothingTotal ? 'bg-green-100' : 'bg-slate-100'}`}>
                        <div className={`text-2xl font-bold ${scenarioTotalVal > doNothingTotal ? 'text-green-700' : 'text-slate-700'}`}>
                          {scenarioTotalVal > doNothingTotal ? '+' : ''}£{isNaN(scenarioTotalVal - doNothingTotal) ? '0.00' : ((scenarioTotalVal - doNothingTotal)/1000000).toFixed(2)}M
                        </div>
                        <div className="text-xs text-slate-500">Impact of Changes</div>
                      </div>
                    </div>

                    {/* Broker Forecast Table - Editable */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-slate-100 px-4 py-3 flex items-center justify-between">
                        <h4 className="font-medium text-slate-800 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Broker NB Forecast (Edit individual metrics)
                        </h4>
                        {Object.keys(brokerOverrides).length > 0 && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setBrokerOverrides({})}
                            className="text-xs text-red-600"
                          >
                            Clear All Broker Edits
                          </Button>
                        )}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left py-2 px-3 font-medium">Broker</th>
                              <th className="text-center py-2 px-2 font-medium">Apps</th>
                              <th className="text-center py-2 px-2 font-medium">Growth</th>
                              <th className="text-center py-2 px-2 font-medium bg-blue-50">Approval %</th>
                              <th className="text-center py-2 px-2 font-medium bg-blue-50">Rejection %</th>
                              <th className="text-center py-2 px-2 font-medium">Conv %</th>
                              <th className="text-center py-2 px-2 font-medium">Deal £k</th>
                              <th className="text-right py-2 px-3 font-medium">Monthly NB</th>
                            </tr>
                          </thead>
                          <tbody>
                            {brokerContributions.slice(0, 12).map((broker, idx) => {
                              // Get monthly history data for sparklines (last 6 months)
                              const monthlyHistory = (broker_stats_full?.find(b => b.name === broker.name)?.monthly_nb || []).slice(-6);
                              const appsData = monthlyHistory.map((m, i) => ({ month: m.month || i, value: m.nb_apps || 0 }));
                              const avgApps = appsData.length > 0 ? Math.round(appsData.reduce((s, d) => s + d.value, 0) / appsData.length) : 0;
                              const maxApps = Math.max(...appsData.map(d => d.value), 1);
                              
                              // Calculate approval rate per month
                              const approvalHistory = monthlyHistory.map((m, i) => ({
                                month: m.month || i,
                                value: m.nb_apps > 0 ? Math.round((m.nb_offers / m.nb_apps) * 100) : 0
                              }));
                              const currentApproval = approvalHistory.length > 0 ? approvalHistory[approvalHistory.length - 1]?.value : broker.approvalRate;
                              
                              // Calculate conversion rate per month
                              const conversionHistory = monthlyHistory.map((m, i) => ({
                                month: m.month || i,
                                value: m.nb_offers > 0 ? Math.round((m.nb_funded / m.nb_offers) * 100) : 0
                              }));
                              const currentConversion = conversionHistory.length > 0 ? conversionHistory[conversionHistory.length - 1]?.value : broker.conversionRate;
                              
                              return (
                              <>
                              <tr key={broker.name} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${broker.hasOverride ? 'ring-1 ring-blue-300 ring-inset' : ''}`}>
                                <td className="py-2 px-3 font-medium">
                                  <HoverCard openDelay={200}>
                                    <HoverCardTrigger asChild>
                                      <button className="text-left hover:text-blue-600 flex items-center gap-1 cursor-pointer">
                                        {broker.name.length > 20 ? broker.name.slice(0, 20) + '...' : broker.name}
                                        <Info className="w-3 h-3 text-slate-400" />
                                      </button>
                                    </HoverCardTrigger>
                                    <HoverCardContent className="w-80 p-4" side="right">
                                      <div className="space-y-3">
                                        {/* Header */}
                                        <div className="flex items-center justify-between border-b pb-2">
                                          <div className="font-semibold">{broker.name}</div>
                                          <Badge className={broker.growthTrend >= 0 ? 'bg-green-500' : 'bg-red-500'}>
                                            {broker.growthTrend >= 0 ? '+' : ''}{broker.growthTrend.toFixed(0)} trend
                                          </Badge>
                                        </div>
                                        
                                        {/* Apps/Month Sparkline */}
                                        <div>
                                          <div className="flex items-center justify-between text-xs mb-1">
                                            <span className="text-slate-500">Apps/Month (6mo)</span>
                                            <span className="font-semibold">avg: {avgApps}</span>
                                          </div>
                                          <div className="h-12 flex items-end gap-1">
                                            {appsData.map((d, i) => (
                                              <div 
                                                key={i} 
                                                className="flex-1 bg-blue-400 rounded-t hover:bg-blue-500 transition-colors"
                                                style={{ height: `${Math.max((d.value / maxApps) * 100, 5)}%` }}
                                                title={`${d.value} apps`}
                                              />
                                            ))}
                                          </div>
                                          <div className="h-4 flex gap-1 text-[9px] text-slate-400 mt-0.5">
                                            {appsData.map((d, i) => (
                                              <div key={i} className="flex-1 text-center truncate">{d.value}</div>
                                            ))}
                                          </div>
                                        </div>
                                        
                                        {/* Approval Rate Sparkline */}
                                        <div>
                                          <div className="flex items-center justify-between text-xs mb-1">
                                            <span className="text-slate-500">Approval Rate %</span>
                                            <span className="font-semibold text-green-600">{currentApproval}%</span>
                                          </div>
                                          <div className="h-8 relative">
                                            <svg className="w-full h-full" viewBox={`0 0 ${Math.max(approvalHistory.length - 1, 1) * 20} 40`} preserveAspectRatio="none">
                                              <polyline
                                                fill="none"
                                                stroke="#22c55e"
                                                strokeWidth="2"
                                                points={approvalHistory.map((d, i) => `${i * 20},${40 - (d.value / 100) * 40}`).join(' ')}
                                              />
                                              {approvalHistory.map((d, i) => (
                                                <circle key={i} cx={i * 20} cy={40 - (d.value / 100) * 40} r="3" fill="#22c55e" />
                                              ))}
                                            </svg>
                                          </div>
                                        </div>
                                        
                                        {/* Conversion Rate Sparkline */}
                                        <div>
                                          <div className="flex items-center justify-between text-xs mb-1">
                                            <span className="text-slate-500">Conversion Rate %</span>
                                            <span className="font-semibold text-purple-600">{currentConversion}%</span>
                                          </div>
                                          <div className="h-8 relative">
                                            <svg className="w-full h-full" viewBox={`0 0 ${Math.max(conversionHistory.length - 1, 1) * 20} 40`} preserveAspectRatio="none">
                                              <polyline
                                                fill="none"
                                                stroke="#a855f7"
                                                strokeWidth="2"
                                                points={conversionHistory.map((d, i) => `${i * 20},${40 - (d.value / 100) * 40}`).join(' ')}
                                              />
                                              {conversionHistory.map((d, i) => (
                                                <circle key={i} cx={i * 20} cy={40 - (d.value / 100) * 40} r="3" fill="#a855f7" />
                                              ))}
                                            </svg>
                                          </div>
                                        </div>
                                        
                                        {/* Stats Grid */}
                                        <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t">
                                          <div className="bg-blue-50 p-2 rounded">
                                            <div className="text-slate-500">NB vs TU Split</div>
                                            <div className="font-bold text-blue-700">{broker_stats_full?.find(b => b.name === broker.name)?.nb_apps || 0} NB / {broker_stats_full?.find(b => b.name === broker.name)?.tu_apps || 0} TU</div>
                                          </div>
                                          <div className="bg-purple-50 p-2 rounded">
                                            <div className="text-slate-500">Avg Deal Size</div>
                                            <div className="font-bold text-purple-700">£{(broker.avgDealSize/1000).toFixed(0)}k</div>
                                          </div>
                                        </div>
                                      </div>
                                    </HoverCardContent>
                                  </HoverCard>
                                  {broker.hasOverride && <Badge className="ml-1 bg-blue-500 text-[9px] px-1">✓</Badge>}
                                  {/* Expand/Collapse Button */}
                                  <button 
                                    onClick={() => setExpandedBrokerRow(expandedBrokerRow === broker.name ? null : broker.name)}
                                    className="ml-2 p-1 hover:bg-slate-200 rounded transition-colors"
                                    title="Click to expand broker details"
                                  >
                                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expandedBrokerRow === broker.name ? 'rotate-180' : ''}`} />
                                  </button>
                                </td>
                                <td className="text-center py-2 px-2 text-slate-600">{broker.latestApps}</td>
                                <td className={`text-center py-2 px-2 ${broker.growthTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {broker.growthTrend >= 0 ? '+' : ''}{broker.growthTrend.toFixed(0)}
                                </td>
                                <td className="text-center py-2 px-2 bg-blue-50/50">
                                  <Input
                                    type="number" min="5" max="50" step="1"
                                    placeholder={broker.approvalRate.toFixed(0)}
                                    value={broker.override.approval_rate ?? ''}
                                    onChange={(e) => setBrokerOverrides(prev => ({
                                      ...prev,
                                      [broker.name]: { ...prev[broker.name], approval_rate: e.target.value === '' ? null : parseFloat(e.target.value) }
                                    }))}
                                    className="h-7 w-14 text-center text-xs"
                                  />
                                </td>
                                <td className="text-center py-2 px-2 bg-blue-50/50">
                                  <Input
                                    type="number" min="20" max="90" step="5"
                                    placeholder={broker.rejectionRate.toFixed(0)}
                                    value={broker.override.rejection_rate ?? ''}
                                    onChange={(e) => setBrokerOverrides(prev => ({
                                      ...prev,
                                      [broker.name]: { ...prev[broker.name], rejection_rate: e.target.value === '' ? null : parseFloat(e.target.value) }
                                    }))}
                                    className="h-7 w-14 text-center text-xs"
                                  />
                                </td>
                                <td className="text-center py-2 px-2 text-slate-600">{broker.conversionRate.toFixed(0)}%</td>
                                <td className="text-center py-2 px-2 text-slate-600">£{(broker.avgDealSize/1000).toFixed(0)}k</td>
                                <td className="text-right py-2 px-3 font-bold text-emerald-700">
                                  £{(broker.monthlyNBValue/1000).toFixed(0)}k
                                </td>
                              </tr>
                              {/* Expanded Broker Detail Row */}
                              {expandedBrokerRow === broker.name && (
                                <tr className="bg-slate-100">
                                  <td colSpan={8} className="p-4">
                                    <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
                                      {/* Broker Detail Header */}
                                      <div className="flex items-center justify-between mb-4 pb-3 border-b">
                                        <div>
                                          <h4 className="font-semibold text-lg">{broker.name}</h4>
                                          <p className="text-sm text-slate-500">6-Month Performance History</p>
                                        </div>
                                        <div className="flex items-center gap-4">
                                          <div className="text-center">
                                            <div className="text-2xl font-bold text-blue-600">{broker_stats_full?.find(b => b.name === broker.name)?.nb_apps || 0}</div>
                                            <div className="text-xs text-slate-500">Total NB Apps</div>
                                          </div>
                                          <div className="text-center">
                                            <div className="text-2xl font-bold text-green-600">{broker_stats_full?.find(b => b.name === broker.name)?.nb_funded || 0}</div>
                                            <div className="text-xs text-slate-500">Total NB Funded</div>
                                          </div>
                                          <div className="text-center">
                                            <div className="text-2xl font-bold text-purple-600">£{((broker_stats_full?.find(b => b.name === broker.name)?.nb_value || 0) / 1000).toFixed(0)}k</div>
                                            <div className="text-xs text-slate-500">Total NB Value</div>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {/* Monthly History Table */}
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                          <thead className="bg-slate-50">
                                            <tr>
                                              <th className="text-left py-2 px-3 font-medium">Month</th>
                                              <th className="text-center py-2 px-3 font-medium">NB Apps</th>
                                              <th className="text-center py-2 px-3 font-medium">NB Offers</th>
                                              <th className="text-center py-2 px-3 font-medium bg-green-50">Offer Rate</th>
                                              <th className="text-center py-2 px-3 font-medium">NB Funded</th>
                                              <th className="text-center py-2 px-3 font-medium bg-purple-50">Conv. Rate</th>
                                              <th className="text-right py-2 px-3 font-medium">NB Value</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {monthlyHistory.map((month, mIdx) => {
                                              const offerRate = month.nb_apps > 0 ? ((month.nb_offers / month.nb_apps) * 100).toFixed(0) : 0;
                                              const convRate = month.nb_offers > 0 ? ((month.nb_funded / month.nb_offers) * 100).toFixed(0) : 0;
                                              return (
                                                <tr key={mIdx} className={mIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                                  <td className="py-2 px-3 font-medium">{month.month || `Month ${mIdx + 1}`}</td>
                                                  <td className="text-center py-2 px-3">{month.nb_apps || 0}</td>
                                                  <td className="text-center py-2 px-3">{month.nb_offers || 0}</td>
                                                  <td className="text-center py-2 px-3 bg-green-50">
                                                    <span className={`font-medium ${offerRate >= 20 ? 'text-green-600' : offerRate >= 10 ? 'text-amber-600' : 'text-red-600'}`}>
                                                      {offerRate}%
                                                    </span>
                                                  </td>
                                                  <td className="text-center py-2 px-3">{month.nb_funded || 0}</td>
                                                  <td className="text-center py-2 px-3 bg-purple-50">
                                                    <span className={`font-medium ${convRate >= 40 ? 'text-purple-600' : convRate >= 20 ? 'text-amber-600' : 'text-red-600'}`}>
                                                      {convRate}%
                                                    </span>
                                                  </td>
                                                  <td className="text-right py-2 px-3 font-medium text-emerald-700">
                                                    £{((month.nb_value || 0) / 1000).toFixed(0)}k
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                          {/* Averages Row */}
                                          <tfoot className="bg-blue-50 border-t-2 border-blue-200">
                                            <tr>
                                              <td className="py-2 px-3 font-semibold">Average</td>
                                              <td className="text-center py-2 px-3 font-semibold">{avgApps}</td>
                                              <td className="text-center py-2 px-3 font-semibold">
                                                {Math.round(monthlyHistory.reduce((s, m) => s + (m.nb_offers || 0), 0) / (monthlyHistory.length || 1))}
                                              </td>
                                              <td className="text-center py-2 px-3 font-semibold bg-green-100 text-green-700">
                                                {broker.approvalRate.toFixed(0)}%
                                              </td>
                                              <td className="text-center py-2 px-3 font-semibold">
                                                {Math.round(monthlyHistory.reduce((s, m) => s + (m.nb_funded || 0), 0) / (monthlyHistory.length || 1))}
                                              </td>
                                              <td className="text-center py-2 px-3 font-semibold bg-purple-100 text-purple-700">
                                                {broker.conversionRate.toFixed(0)}%
                                              </td>
                                              <td className="text-right py-2 px-3 font-semibold text-emerald-700">
                                                £{(monthlyHistory.reduce((s, m) => s + (m.nb_value || 0), 0) / (monthlyHistory.length || 1) / 1000).toFixed(0)}k
                                              </td>
                                            </tr>
                                          </tfoot>
                                        </table>
                                      </div>
                                      
                                      {/* Visual Charts Row */}
                                      <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
                                        {/* Apps Trend Chart */}
                                        <div className="bg-blue-50 rounded-lg p-3">
                                          <div className="text-xs font-medium text-blue-700 mb-2">Apps per Month</div>
                                          <div className="h-16 flex items-end gap-1">
                                            {appsData.map((d, i) => (
                                              <div key={i} className="flex-1 flex flex-col items-center">
                                                <div 
                                                  className="w-full bg-blue-400 rounded-t"
                                                  style={{ height: `${Math.max((d.value / maxApps) * 100, 8)}%` }}
                                                />
                                                <span className="text-[10px] mt-1 text-blue-600">{d.value}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                        
                                        {/* Offer Rate Trend */}
                                        <div className="bg-green-50 rounded-lg p-3">
                                          <div className="text-xs font-medium text-green-700 mb-2">Offer Rate Trend</div>
                                          <div className="h-16 relative">
                                            <svg className="w-full h-full" viewBox={`0 0 ${Math.max(approvalHistory.length - 1, 1) * 30} 60`} preserveAspectRatio="none">
                                              <polyline
                                                fill="none"
                                                stroke="#22c55e"
                                                strokeWidth="3"
                                                points={approvalHistory.map((d, i) => `${i * 30},${60 - (d.value / 50) * 60}`).join(' ')}
                                              />
                                              {approvalHistory.map((d, i) => (
                                                <g key={i}>
                                                  <circle cx={i * 30} cy={60 - (d.value / 50) * 60} r="4" fill="#22c55e" />
                                                  <text x={i * 30} y={70} textAnchor="middle" className="text-[8px] fill-green-600">{d.value}%</text>
                                                </g>
                                              ))}
                                            </svg>
                                          </div>
                                        </div>
                                        
                                        {/* Conversion Rate Trend */}
                                        <div className="bg-purple-50 rounded-lg p-3">
                                          <div className="text-xs font-medium text-purple-700 mb-2">Conversion Rate Trend</div>
                                          <div className="h-16 relative">
                                            <svg className="w-full h-full" viewBox={`0 0 ${Math.max(conversionHistory.length - 1, 1) * 30} 60`} preserveAspectRatio="none">
                                              <polyline
                                                fill="none"
                                                stroke="#a855f7"
                                                strokeWidth="3"
                                                points={conversionHistory.map((d, i) => `${i * 30},${60 - (d.value / 100) * 60}`).join(' ')}
                                              />
                                              {conversionHistory.map((d, i) => (
                                                <g key={i}>
                                                  <circle cx={i * 30} cy={60 - (d.value / 100) * 60} r="4" fill="#a855f7" />
                                                  <text x={i * 30} y={70} textAnchor="middle" className="text-[8px] fill-purple-600">{d.value}%</text>
                                                </g>
                                              ))}
                                            </svg>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                              </>
                              );
                            })}
                            {/* Others row */}
                            <tr className="bg-slate-100 border-t">
                              <td className="py-2 px-3 text-slate-600" colSpan={7}>Others ({smallBrokers.length} small brokers)</td>
                              <td className="text-right py-2 px-3 font-medium text-slate-600">
                                £{(smallBrokers.reduce((s, b) => s + b.typicalApps * (b.approvalRate/100) * (b.conversionRate/100) * b.avgDealSize, 0)/1000).toFixed(0)}k
                              </td>
                            </tr>
                            {/* Total row */}
                            <tr className="bg-emerald-100 border-t-2 border-emerald-300">
                              <td className="py-2 px-3 font-bold" colSpan={7}>Total Monthly NB</td>
                              <td className="text-right py-2 px-3 font-bold text-emerald-800 text-lg">
                                £{(doNothingForecast[0]?.value/1000).toFixed(0)}k
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>
            </TabsContent>

            {/* TU Planning Sub-tab */}
            <TabsContent value="tu-planning" className="space-y-6">
              <Card className="border-purple-300 bg-purple-50/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-purple-600" />
                    Top-up Planning
                  </CardTitle>
                  <CardDescription>
                    Top-ups are driven by your existing loan book. Expected TU = Historical TU Rate × Loan Book Value
                  </CardDescription>
                </CardHeader>
            <CardContent>
              {/* Current Metrics Summary */}
              {(() => {
                // Calculate current trends from last 3 months
                const recent = monthly_trends.slice(-3);
                const latest = monthly_trends[monthly_trends.length - 1] || {};
                
                // Current metrics from latest month
                const currentApps = latest.applications || 1700;
                const currentApprovalRate = latest.offers && latest.applications 
                  ? (latest.offers / latest.applications * 100) : 20;
                const currentRejectionRate = latest.rejected && latest.offers 
                  ? (latest.rejected / latest.offers * 100) : 70;
                const currentConvRate = latest.funded && latest.offers 
                  ? (latest.funded / latest.offers * 100) : 20;
                const currentAvgDeal = latest.funded_value && latest.funded 
                  ? (latest.funded_value / latest.funded) : 19000;
                
                // Calculate REALISTIC baseline - use rolling 3-month average of ACTUAL funded value
                const recentFundedValues = recent.map(m => m.funded_value || 0);
                const avgRecentFundedValue = recentFundedValues.length > 0 
                  ? recentFundedValues.reduce((a, b) => a + b, 0) / recentFundedValues.length 
                  : 1500000;
                
                // Apps trend - but capped to realistic bounds
                const rawAppsTrend = recent.length >= 2 
                  ? (recent[recent.length-1]?.applications - recent[0]?.applications) / (recent.length - 1)
                  : 0;
                // Cap apps trend to ±5% monthly growth for "do nothing" - no wild swings
                const appsTrend = Math.max(-currentApps * 0.05, Math.min(currentApps * 0.05, rawAppsTrend));
                
                const approvalTrend = recent.length >= 2
                  ? ((recent[recent.length-1]?.offers / recent[recent.length-1]?.applications * 100) - 
                     (recent[0]?.offers / recent[0]?.applications * 100)) / (recent.length - 1)
                  : 0;
                
                // Scenario values (use override or current)
                const scenarioApproval = scenario.approvalRate ?? currentApprovalRate;
                const scenarioRejection = scenario.rejectionRate ?? currentRejectionRate;
                const scenarioAppsGrowth = scenario.appsGrowth ?? appsTrend;
                const scenarioAvgDeal = scenario.avgDealSize ?? currentAvgDeal;
                
                // Calculate broker-level contributions for "With Changes"
                // Uses a RELATIVE multiplier approach - each broker's impact is relative to their current contribution
                const calculateBrokerScenarioForecast = (baselineForecast) => {
                  const brokers = (broker_stats_full || [])
                    .filter(b => b.name && b.name !== 'Direct/Unknown' && b.total_applications >= 10);
                  
                  if (brokers.length === 0 || Object.keys(brokerOverrides).length === 0) {
                    return null; // Use global calculation
                  }
                  
                  // Calculate total baseline monthly value from all brokers
                  const totalMonthlyNBValue = brokers.reduce((sum, b) => sum + ((b.nb_value || 0) / 12), 0);
                  
                  const forecasts = [];
                  for (let i = 1; i <= 6; i++) {
                    const monthDate = new Date();
                    monthDate.setMonth(monthDate.getMonth() + i);
                    const monthLabel = monthDate.toLocaleDateString('en-GB', { month: 'short' });
                    
                    // Start with the baseline "Do Nothing" value for this month
                    const baselineValue = baselineForecast[i-1]?.value || avgRecentFundedValue;
                    
                    // Calculate the weighted uplift from broker overrides
                    let totalUpliftMultiplier = 1;
                    let totalWeight = 0;
                    
                    brokers.forEach(broker => {
                      const override = brokerOverrides[broker.name] || {};
                      const brokerMonthlyValue = (broker.nb_value || 0) / 12;
                      const brokerWeight = totalMonthlyNBValue > 0 ? brokerMonthlyValue / totalMonthlyNBValue : 0;
                      
                      // Only count brokers with overrides
                      if (Object.values(override).some(v => v !== null && v !== undefined)) {
                        const baseApproval = broker.approval_rate || currentApprovalRate;
                        const baseRejection = broker.rejection_rate || currentRejectionRate;
                        
                        // Calculate multiplier from overrides
                        const approvalMultiplier = override.approval_rate != null 
                          ? override.approval_rate / Math.max(baseApproval, 1) 
                          : 1;
                        const rejectionMultiplier = override.rejection_rate != null
                          ? (100 - override.rejection_rate) / Math.max(100 - baseRejection, 1)
                          : 1;
                        const appsMultiplier = override.apps_growth != null
                          ? 1 + (override.apps_growth * i / 100) // Apps growth as percentage effect
                          : 1;
                        
                        const brokerMultiplier = approvalMultiplier * rejectionMultiplier * appsMultiplier;
                        
                        // Add weighted contribution to total
                        totalUpliftMultiplier += (brokerMultiplier - 1) * brokerWeight;
                        totalWeight += brokerWeight;
                      }
                    });
                    
                    // Apply the weighted uplift to the baseline
                    const scenarioValue = baselineValue * Math.max(0.5, Math.min(3, totalUpliftMultiplier));
                    
                    forecasts.push({
                      month: monthLabel,
                      apps: Math.round(currentApps + (scenarioAppsGrowth * i)),
                      funded: Math.round(scenarioValue / (scenarioAvgDeal || currentAvgDeal)),
                      value: scenarioValue
                    });
                  }
                  
                  return forecasts;
                };
                
                // Calculate "Do Nothing" forecast for next 6 months
                // BROKER-BY-BROKER APPROACH: Build forecast from individual broker contributions
                const doNothingForecast = [];
                let scenarioForecast = [];
                
                // Build broker-by-broker NB forecast with growth trends
                const brokers = (broker_stats_full || [])
                  .filter(b => b.name && !b.name.includes('(TU)') && b.name !== 'Direct/Unknown')
                  .map(b => ({
                    name: b.name,
                    latestApps: b.latest_nb_apps || b.typical_monthly_nb_apps || Math.round((b.nb_apps || 0) / 12),
                    typicalApps: b.typical_monthly_nb_apps || Math.round((b.nb_apps || 0) / 12),
                    growthTrend: b.nb_apps_growth_trend || 0,  // Apps growth per month
                    approvalRate: b.nb_approval_rate || 0,
                    conversionRate: b.nb_conversion_rate || 0,
                    avgDealSize: b.nb_avg_deal_size || currentAvgDeal,
                    nbValue: b.nb_value || 0
                  }));
                
                // Split into main brokers (≥5 apps/month) and "others"
                const mainBrokers = brokers.filter(b => b.typicalApps >= 5);
                const smallBrokers = brokers.filter(b => b.typicalApps > 0 && b.typicalApps < 5);
                
                // Aggregate small brokers into "Others"
                const othersTypicalApps = smallBrokers.reduce((sum, b) => sum + b.typicalApps, 0);
                const othersGrowthTrend = smallBrokers.reduce((sum, b) => sum + b.growthTrend, 0);
                const othersNBValue = smallBrokers.reduce((sum, b) => sum + b.nbValue, 0);
                const othersAvgApproval = smallBrokers.length > 0 
                  ? smallBrokers.reduce((sum, b) => sum + b.approvalRate, 0) / smallBrokers.length 
                  : currentApprovalRate;
                const othersAvgConversion = smallBrokers.length > 0
                  ? smallBrokers.reduce((sum, b) => sum + b.conversionRate, 0) / smallBrokers.length
                  : 30;
                const othersAvgDeal = smallBrokers.length > 0 && othersNBValue > 0
                  ? othersNBValue / smallBrokers.reduce((sum, b) => sum + (b.typicalApps * b.approvalRate/100 * b.conversionRate/100 * 12), 1)
                  : currentAvgDeal;
                
                // Calculate monthly NB value for a broker with growth projection
                const calculateBrokerMonthlyNB = (broker, monthIndex, override = {}) => {
                  // Project apps forward using growth trend
                  const baseApps = broker.latestApps || broker.typicalApps;
                  const growth = override.growth_trend ?? broker.growthTrend;
                  const projectedApps = Math.max(0, baseApps + (growth * monthIndex));
                  
                  const apps = override.apps ?? projectedApps;
                  const approval = override.approval_rate ?? broker.approvalRate;
                  const conversion = override.conversion_rate ?? (100 - (override.rejection_rate ?? (100 - broker.conversionRate)));
                  const dealSize = override.avg_deal_size ?? broker.avgDealSize;
                  
                  const offers = apps * (approval / 100);
                  const funded = offers * (conversion / 100);
                  return funded * dealSize;
                };
                
                // Check if any scenario lever has been changed
                const hasScenarioChanges = 
                  (scenario.approvalRate !== null && Math.abs(scenario.approvalRate - currentApprovalRate) > 0.5) ||
                  (scenario.rejectionRate !== null && Math.abs(scenario.rejectionRate - currentRejectionRate) > 0.5) ||
                  (scenario.appsGrowth !== null && Math.abs(scenario.appsGrowth - appsTrend) > 5) ||
                  (scenario.avgDealSize !== null && Math.abs(scenario.avgDealSize - currentAvgDeal) > 500) ||
                  Object.keys(brokerOverrides).length > 0;
                
                // Build broker breakdown for UI display (using month 1 as baseline)
                const brokerContributions = mainBrokers.map(b => {
                  const override = brokerOverrides[b.name] || {};
                  return {
                    name: b.name,
                    latestApps: b.latestApps,
                    growthTrend: b.growthTrend,
                    projectedApps: Math.round(b.latestApps + b.growthTrend), // Next month projection
                    approvalRate: override.approval_rate ?? b.approvalRate,
                    conversionRate: b.conversionRate,
                    avgDealSize: b.avgDealSize,
                    monthlyNBValue: calculateBrokerMonthlyNB(b, 1), // Month 1 baseline
                    hasOverride: Object.values(override).some(v => v != null)
                  };
                }).sort((a, b) => b.monthlyNBValue - a.monthlyNBValue);
                
                for (let i = 1; i <= 6; i++) {
                  const monthDate = new Date();
                  monthDate.setMonth(monthDate.getMonth() + i);
                  const monthLabel = monthDate.toLocaleDateString('en-GB', { month: 'short' });
                  
                  // DO NOTHING: Sum up each broker's expected NB contribution with growth
                  let doNothingTotal = 0;
                  let scenarioTotal = 0;
                  let totalProjectedApps = 0;
                  
                  mainBrokers.forEach(broker => {
                    const baselineValue = calculateBrokerMonthlyNB(broker, i); // Uses growth trend
                    doNothingTotal += baselineValue;
                    totalProjectedApps += Math.max(0, broker.latestApps + (broker.growthTrend * i));
                    
                    // For scenario: apply broker-specific or global overrides
                    const override = brokerOverrides[broker.name] || {};
                    const hasOverride = Object.values(override).some(v => v != null);
                    
                    if (hasOverride || hasScenarioChanges) {
                      const scenarioOverride = {
                        growth_trend: override.apps_growth ?? broker.growthTrend,
                        approval_rate: override.approval_rate ?? (scenario.approvalRate ?? undefined),
                        rejection_rate: override.rejection_rate ?? (scenario.rejectionRate ?? undefined),
                        avg_deal_size: scenario.avgDealSize ?? undefined
                      };
                      scenarioTotal += calculateBrokerMonthlyNB(broker, i, scenarioOverride);
                    } else {
                      scenarioTotal += baselineValue;
                    }
                  });
                  
                  // Add "Others" contribution with growth
                  if (othersTypicalApps > 0) {
                    const othersProjectedApps = Math.max(0, othersTypicalApps + (othersGrowthTrend * i));
                    const othersBaseline = othersProjectedApps * (othersAvgApproval / 100) * (othersAvgConversion / 100) * othersAvgDeal;
                    doNothingTotal += othersBaseline;
                    scenarioTotal += othersBaseline;
                    totalProjectedApps += othersProjectedApps;
                  }
                  
                  doNothingForecast.push({
                    month: monthLabel,
                    apps: Math.round(totalProjectedApps),
                    funded: Math.round(doNothingTotal / currentAvgDeal),
                    value: doNothingTotal
                  });
                  
                  scenarioForecast.push({
                    month: monthLabel,
                    apps: Math.round(totalProjectedApps),
                    funded: Math.round(scenarioTotal / currentAvgDeal),
                    value: scenarioTotal
                  });
                }
                
                // Calculate TU forecast for each month (using 3-month lag model)
                const tuForecast = [];
                for (let i = 1; i <= 6; i++) {
                  const targetMonth = new Date();
                  targetMonth.setMonth(targetMonth.getMonth() + i);
                  const monthLabel = targetMonth.toLocaleDateString('en-GB', { month: 'short' });
                  
                  // Get funded value from 3 months ago
                  const sourceMonth = new Date();
                  sourceMonth.setMonth(sourceMonth.getMonth() + i - 3);
                  const sourceMonthKey = sourceMonth.toISOString().slice(0, 7);
                  const sourceFunded = monthly_trends.find(m => m.month_key === sourceMonthKey)?.funded_value || 0;
                  
                  // Use the top-up percentage assumption (default 43.6% based on historical analysis)
                  const tuPct = topupPct[targetMonth.toISOString().slice(0, 7)] || 43.6;
                  const expectedTU = sourceFunded * (tuPct / 100);
                  
                  tuForecast.push({
                    month: monthLabel,
                    value: expectedTU
                  });
                }
                
                // Calculate totals
                const doNothingTotal = doNothingForecast.reduce((sum, m) => sum + m.value, 0);
                const scenarioTotal = scenarioForecast.reduce((sum, m) => sum + m.value, 0);
                const uplift = scenarioTotal - doNothingTotal;
                
                return (
                  <>
                    {/* Current Funnel Metrics */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-sm text-muted-foreground">Monthly Apps</p>
                        <p className="text-xl font-bold">{currentApps.toLocaleString()}</p>
                        <p className="text-xs text-green-600">{appsTrend > 0 ? '+' : ''}{appsTrend.toFixed(0)}/mo trend</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-sm text-muted-foreground">Approval Rate</p>
                        <p className="text-xl font-bold">{currentApprovalRate.toFixed(1)}%</p>
                        <p className="text-xs text-red-600">{approvalTrend.toFixed(1)}%/mo trend</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-sm text-muted-foreground">Rejection Rate</p>
                        <p className="text-xl font-bold text-red-600">{currentRejectionRate.toFixed(1)}%</p>
                        <p className="text-xs text-muted-foreground">of offers</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-sm text-muted-foreground">Conversion Rate</p>
                        <p className="text-xl font-bold">{currentConvRate.toFixed(1)}%</p>
                        <p className="text-xs text-muted-foreground">offers → funded</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-sm text-muted-foreground">Avg Deal Size</p>
                        <p className="text-xl font-bold">£{(currentAvgDeal/1000).toFixed(0)}k</p>
                        <p className="text-xs text-muted-foreground">per funded</p>
                      </div>
                    </div>

                    {/* Scenario Sliders */}
                    <div className="p-4 bg-purple-100 rounded-lg border border-purple-300 mb-6">
                      <h4 className="font-medium text-purple-800 mb-4">Adjust Scenario Levers</h4>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label className="text-sm font-medium text-purple-700">Approval Rate (%)</label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="range"
                              min="10"
                              max="40"
                              step="0.5"
                              value={scenario.approvalRate ?? currentApprovalRate}
                              onChange={(e) => setScenario({...scenario, approvalRate: parseFloat(e.target.value)})}
                              className="flex-1"
                            />
                            <span className="text-sm font-bold w-12">{(scenario.approvalRate ?? currentApprovalRate).toFixed(1)}%</span>
                          </div>
                          <p className="text-xs text-purple-600 mt-1">Current: {currentApprovalRate.toFixed(1)}%</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-purple-700">Rejection Rate (%)</label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="range"
                              min="30"
                              max="90"
                              step="1"
                              value={scenario.rejectionRate ?? currentRejectionRate}
                              onChange={(e) => setScenario({...scenario, rejectionRate: parseFloat(e.target.value)})}
                              className="flex-1"
                            />
                            <span className="text-sm font-bold w-12">{(scenario.rejectionRate ?? currentRejectionRate).toFixed(0)}%</span>
                          </div>
                          <p className="text-xs text-purple-600 mt-1">Current: {currentRejectionRate.toFixed(0)}%</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-purple-700">Apps Growth/Month</label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="range"
                              min="-100"
                              max="300"
                              step="10"
                              value={scenario.appsGrowth ?? appsTrend}
                              onChange={(e) => setScenario({...scenario, appsGrowth: parseFloat(e.target.value)})}
                              className="flex-1"
                            />
                            <span className="text-sm font-bold w-12">{(scenario.appsGrowth ?? appsTrend) > 0 ? '+' : ''}{(scenario.appsGrowth ?? appsTrend).toFixed(0)}</span>
                          </div>
                          <p className="text-xs text-purple-600 mt-1">Trend: {appsTrend > 0 ? '+' : ''}{appsTrend.toFixed(0)}/mo</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-purple-700">Avg Deal Size (£k)</label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="range"
                              min="10"
                              max="40"
                              step="1"
                              value={(scenario.avgDealSize ?? currentAvgDeal) / 1000}
                              onChange={(e) => setScenario({...scenario, avgDealSize: parseFloat(e.target.value) * 1000})}
                              className="flex-1"
                            />
                            <span className="text-sm font-bold w-12">£{((scenario.avgDealSize ?? currentAvgDeal)/1000).toFixed(0)}k</span>
                          </div>
                          <p className="text-xs text-purple-600 mt-1">Current: £{(currentAvgDeal/1000).toFixed(0)}k</p>
                        </div>
                      </div>
                      <div className="mt-3 flex justify-between items-center">
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setBrokerEditorOpen(true)}
                            className="border-purple-400 text-purple-700 hover:bg-purple-100"
                          >
                            <Settings2 className="w-4 h-4 mr-1" />
                            Edit Broker Rates
                          </Button>
                          {Object.keys(brokerOverrides).length > 0 && (
                            <Badge className="bg-purple-600">
                              {Object.keys(brokerOverrides).length} broker{Object.keys(brokerOverrides).length > 1 ? 's' : ''} customized
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={resetScenario}
                          >
                            Reset to Current Trends
                          </Button>
                        </div>
                      </div>
                      
                      {/* Save/Load Scenarios */}
                      <div className="mt-4 p-3 bg-white rounded-lg border border-purple-200">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="text-sm font-medium text-purple-800 flex items-center gap-1">
                            <FolderOpen className="w-4 h-4" />
                            Save & Load Scenarios
                          </h5>
                        </div>
                        <div className="flex gap-2 mb-2">
                          <Input
                            placeholder="Scenario name..."
                            value={scenarioName}
                            onChange={(e) => setScenarioName(e.target.value)}
                            className="flex-1 h-8 text-sm"
                          />
                          <Button 
                            size="sm" 
                            onClick={handleSaveScenario}
                            disabled={savingScenario || !scenarioName.trim()}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            {savingScenario ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                            Save
                          </Button>
                        </div>
                        {savedScenarios.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {savedScenarios.slice(0, 5).map(s => (
                              <div 
                                key={s.id}
                                onClick={() => handleLoadScenario(s)}
                                className={`text-xs px-2 py-1 rounded cursor-pointer flex items-center gap-1 transition-colors ${
                                  loadedScenarioId === s.id 
                                    ? 'bg-purple-600 text-white' 
                                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                                }`}
                              >
                                <span>{s.name}</span>
                                <button 
                                  onClick={(e) => handleDeleteScenario(s.id, e)}
                                  className="hover:text-red-500 ml-1"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Comparison Chart - NB with TU shadow */}
                    <div className="mb-6">
                      <h4 className="text-sm font-medium mb-3">6-Month NB Forecast + Expected TU (shadow)</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={doNothingForecast.map((dn, i) => ({
                          month: dn.month,
                          doNothing: dn.value,
                          withChanges: scenarioForecast[i].value,
                          tuShadow: tuForecast[i]?.value || 0,
                          target: salesTargets[`${currentDate.year}-${String((new Date().getMonth() + 2 + i) % 12 || 12).padStart(2, '0')}`] || 2000000
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis tickFormatter={(v) => `£${(v/1000000).toFixed(1)}m`} />
                          <Tooltip 
                            formatter={(v, name) => [`£${(v/1000).toFixed(0)}k`, name]}
                            labelFormatter={(label) => `Month: ${label}`}
                          />
                          <Legend />
                          <Bar dataKey="doNothing" stackId="doNothing" fill="#94a3b8" name="NB (Do Nothing)" radius={[0, 0, 0, 0]} />
                          <Bar dataKey="tuShadow" stackId="doNothing" fill="#c4b5fd" fillOpacity={0.5} name="Expected TU" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="withChanges" stackId="withChanges" fill="#8b5cf6" name="NB (With Changes)" radius={[0, 0, 0, 0]} />
                          <Bar dataKey="tuShadow" stackId="withChanges" fill="#a78bfa" fillOpacity={0.4} name=" " radius={[4, 4, 0, 0]} />
                          <Line type="monotone" dataKey="target" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Target" />
                        </ComposedChart>
                      </ResponsiveContainer>
                      <p className="text-xs text-slate-500 mt-1">
                        Solid bars = NB forecast | Semi-transparent = Expected TU (stacked to show combined total vs target)
                      </p>
                    </div>

                    {/* Impact Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-slate-100 rounded-lg">
                        <p className="text-sm text-muted-foreground">"Do Nothing" 6-Month Total</p>
                        <p className="text-2xl font-bold text-slate-600">£{isNaN(doNothingTotal) ? '0.00' : (doNothingTotal/1000000).toFixed(2)}M</p>
                      </div>
                      <div className="p-4 bg-purple-100 rounded-lg">
                        <p className="text-sm text-purple-700">"With Changes" 6-Month Total</p>
                        <p className="text-2xl font-bold text-purple-700">£{isNaN(scenarioTotal) ? '0.00' : (scenarioTotal/1000000).toFixed(2)}M</p>
                      </div>
                      <div className={`p-4 rounded-lg ${uplift >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                        <p className="text-sm text-muted-foreground">Impact of Changes</p>
                        <p className={`text-2xl font-bold ${uplift >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {uplift >= 0 ? '+' : ''}£{isNaN(uplift) ? '0.00' : (uplift/1000000).toFixed(2)}M
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {uplift >= 0 ? 'Additional' : 'Less'} revenue over 6 months
                        </p>
                      </div>
                    </div>

                    {/* Broker Forecast Breakdown Table */}
                    <div className="mt-6">
                      <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        NB Forecast Breakdown by Broker
                      </h4>
                      <div className="overflow-x-auto rounded-lg border border-slate-200">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="text-left py-2 px-3 font-medium">Broker</th>
                              <th className="text-center py-2 px-2 font-medium">Latest Apps</th>
                              <th className="text-center py-2 px-2 font-medium">Growth/mo</th>
                              <th className="text-center py-2 px-2 font-medium">Next Mo</th>
                              <th className="text-center py-2 px-2 font-medium">Approval</th>
                              <th className="text-center py-2 px-2 font-medium">Conv.</th>
                              <th className="text-center py-2 px-2 font-medium">Avg Deal</th>
                              <th className="text-right py-2 px-3 font-medium">Monthly NB</th>
                            </tr>
                          </thead>
                          <tbody>
                            {brokerContributions.slice(0, 12).map((broker, idx) => (
                              <tr key={broker.name} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                <td className="py-2 px-3 font-medium">
                                  {broker.name}
                                  {broker.hasOverride && <Badge className="ml-1 bg-purple-500 text-[9px] px-1">✓</Badge>}
                                </td>
                                <td className="text-center py-2 px-2">{broker.latestApps}</td>
                                <td className={`text-center py-2 px-2 ${broker.growthTrend > 0 ? 'text-green-600' : broker.growthTrend < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                                  {broker.growthTrend > 0 ? '+' : ''}{broker.growthTrend.toFixed(1)}
                                </td>
                                <td className="text-center py-2 px-2 font-medium">{broker.projectedApps}</td>
                                <td className="text-center py-2 px-2">{broker.approvalRate.toFixed(1)}%</td>
                                <td className="text-center py-2 px-2">{broker.conversionRate.toFixed(1)}%</td>
                                <td className="text-center py-2 px-2">£{(broker.avgDealSize/1000).toFixed(0)}k</td>
                                <td className="text-right py-2 px-3 font-bold text-emerald-700">
                                  £{(broker.monthlyNBValue/1000).toFixed(0)}k
                                </td>
                              </tr>
                            ))}
                            {/* Others row */}
                            {othersTypicalApps > 0 && (
                              <tr className="bg-slate-100 border-t">
                                <td className="py-2 px-3 font-medium text-slate-600">Others ({smallBrokers.length} brokers)</td>
                                <td className="text-center py-2 px-2 text-slate-500">{othersTypicalApps}</td>
                                <td className={`text-center py-2 px-2 ${othersGrowthTrend > 0 ? 'text-green-600' : othersGrowthTrend < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                                  {othersGrowthTrend > 0 ? '+' : ''}{othersGrowthTrend.toFixed(1)}
                                </td>
                                <td className="text-center py-2 px-2 text-slate-500">-</td>
                                <td className="text-center py-2 px-2 text-slate-500">{othersAvgApproval.toFixed(1)}%</td>
                                <td className="text-center py-2 px-2 text-slate-500">{othersAvgConversion.toFixed(1)}%</td>
                                <td className="text-center py-2 px-2 text-slate-500">£{(othersAvgDeal/1000).toFixed(0)}k</td>
                                <td className="text-right py-2 px-3 font-medium text-slate-600">
                                  £{((othersTypicalApps * (othersAvgApproval / 100) * (othersAvgConversion / 100) * othersAvgDeal)/1000).toFixed(0)}k
                                </td>
                              </tr>
                            )}
                            {/* Total row */}
                            <tr className="bg-emerald-100 border-t-2 border-emerald-300">
                              <td className="py-2 px-3 font-bold" colSpan={7}>Total Monthly NB Forecast</td>
                              <td className="text-right py-2 px-3 font-bold text-emerald-800 text-lg">
                                £{((doNothingForecast[0]?.value || 0)/1000).toFixed(0)}k
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Formula: Apps × Approval% × Conversion% × Avg Deal = Monthly NB Value. Growth trends project apps forward each month.
                      </p>
                    </div>

                    {/* Bottleneck Analysis */}
                    <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <h4 className="font-medium text-amber-800 mb-2">Bottleneck Analysis</h4>
                      <div className="text-sm text-amber-700 space-y-2">
                        {currentRejectionRate > 60 && (
                          <p>⚠️ <strong>High Rejection Rate ({currentRejectionRate.toFixed(0)}%)</strong>: Reducing to 50% could add ~£{((scenarioForecast.reduce((s,m) => s + m.value, 0) - doNothingTotal) / 1000).toFixed(0)}k over 6 months.</p>
                        )}
                        {currentApprovalRate < 22 && (
                          <p>⚠️ <strong>Low Approval Rate ({currentApprovalRate.toFixed(1)}%)</strong>: Improving to 25% would increase offer volume significantly.</p>
                        )}
                        {approvalTrend < 0 && (
                          <p>📉 <strong>Declining Approval Trend</strong>: Approval rate dropping {Math.abs(approvalTrend).toFixed(1)}%/month. Investigate underwriting criteria.</p>
                        )}
                        {appsTrend > 100 && currentApprovalRate < 22 && (
                          <p>✅ <strong>Apps Growing</strong>: +{appsTrend.toFixed(0)}/month. Focus on conversion, not volume.</p>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>
            </TabsContent>

            {/* TU Planning Sub-tab */}
            <TabsContent value="tu-planning" className="space-y-6">
              <Card className="border-purple-300 bg-purple-50/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-purple-600" />
                    Top-up Planning
                  </CardTitle>
                  <CardDescription>
                    Top-ups are driven by your existing loan book. Expected TU = Historical TU Rate × Loan Book Value
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* TU Forecast based on 3-month lag */}
                  <div className="space-y-4">
                    <div className="p-4 bg-purple-100/50 rounded-lg">
                      <h4 className="font-medium text-purple-800 mb-2">Top-up Prediction Model</h4>
                      <p className="text-sm text-purple-700">
                        Based on historical analysis: <strong>Top-ups in Month M ≈ 43.6% of Total Funded in Month M-3</strong>
                      </p>
                      <p className="text-xs text-purple-600 mt-1">
                        This accounts for the typical 13-week (3-month) period before customers become eligible for top-ups.
                      </p>
                    </div>

                    {/* TU Chart with NB Shadow */}
                    {(() => {
                      // Calculate TU and NB forecast for chart
                      const chartData = [];
                      for (let i = 1; i <= 6; i++) {
                        const targetMonth = new Date();
                        targetMonth.setMonth(targetMonth.getMonth() + i);
                        const monthLabel = targetMonth.toLocaleDateString('en-GB', { month: 'short' });
                        
                        // TU from 3-month lag
                        const sourceMonth = new Date();
                        sourceMonth.setMonth(sourceMonth.getMonth() + i - 3);
                        const sourceMonthKey = sourceMonth.toISOString().slice(0, 7);
                        const sourceFunded = monthly_trends.find(m => m.month_key === sourceMonthKey)?.funded_value || 0;
                        const tuPct = topupPct[targetMonth.toISOString().slice(0, 7)] || 43.6;
                        const expectedTU = sourceFunded * (tuPct / 100);
                        
                        // NB forecast (simplified - use broker model average)
                        const nbValue = broker_stats_full
                          .filter(b => b.name && !b.name.includes('(TU)') && b.name !== 'Direct/Unknown' && b.typical_monthly_nb_apps >= 5)
                          .reduce((sum, b) => {
                            const apps = (b.latest_nb_apps || b.typical_monthly_nb_apps) + ((b.nb_apps_growth_trend || 0) * i);
                            return sum + (apps * (b.nb_approval_rate || 0) / 100 * (b.nb_conversion_rate || 0) / 100 * (b.nb_avg_deal_size || 0));
                          }, 0);
                        
                        chartData.push({
                          month: monthLabel,
                          tu: expectedTU,
                          nbShadow: nbValue,
                          target: salesTargets[targetMonth.toISOString().slice(0, 7)] || 2000000
                        });
                      }
                      
                      return (
                        <div className="mb-4">
                          <h4 className="text-sm font-medium mb-3">6-Month TU Forecast + Expected NB (shadow)</h4>
                          <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={chartData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="month" />
                              <YAxis tickFormatter={(v) => `£${(v/1000000).toFixed(1)}m`} />
                              <Tooltip formatter={(v, name) => [`£${(v/1000).toFixed(0)}k`, name]} />
                              <Legend />
                              <Bar dataKey="tu" stackId="stack" fill="#a855f7" name="Expected TU" radius={[0, 0, 0, 0]} />
                              <Bar dataKey="nbShadow" stackId="stack" fill="#93c5fd" fillOpacity={0.5} name="NB Forecast (shadow)" radius={[4, 4, 0, 0]} />
                              <Line type="monotone" dataKey="target" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Target" />
                            </ComposedChart>
                          </ResponsiveContainer>
                          <p className="text-xs text-slate-500 mt-1">
                            Solid purple = TU forecast | Semi-transparent blue = NB forecast (stacked to show combined total vs target)
                          </p>
                        </div>
                      );
                    })()}
                    
                    {/* TU Forecast Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-purple-100">
                          <tr>
                            <th className="text-left py-2 px-3">Month</th>
                            <th className="text-center py-2 px-3">Funded (M-3)</th>
                            <th className="text-center py-2 px-3">TU %</th>
                            <th className="text-right py-2 px-3">Expected TU</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const rows = [];
                            for (let i = 1; i <= 6; i++) {
                              const targetMonth = new Date();
                              targetMonth.setMonth(targetMonth.getMonth() + i);
                              const monthLabel = targetMonth.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
                              
                              // Get funded value from 3 months ago
                              const sourceMonth = new Date();
                              sourceMonth.setMonth(sourceMonth.getMonth() + i - 3);
                              const sourceMonthKey = sourceMonth.toISOString().slice(0, 7);
                              const sourceFunded = monthly_trends.find(m => m.month_key === sourceMonthKey)?.funded_value || 0;
                              
                              const tuPct = topupPct[targetMonth.toISOString().slice(0, 7)] || 43.6;
                              const expectedTU = sourceFunded * (tuPct / 100);
                              
                              rows.push(
                                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-purple-50/50'}>
                                  <td className="py-2 px-3 font-medium">{monthLabel}</td>
                                  <td className="text-center py-2 px-3">£{(sourceFunded / 1000).toFixed(0)}k</td>
                                  <td className="text-center py-2 px-3">{tuPct.toFixed(0)}%</td>
                                  <td className="text-right py-2 px-3 font-bold text-purple-700">£{(expectedTU / 1000).toFixed(0)}k</td>
                                </tr>
                              );
                            }
                            return rows;
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* AI Plan Analysis Sub-tab */}
            <TabsContent value="ai-analysis" className="space-y-6">
              <Card className="border-blue-300 bg-blue-50/30">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Brain className="w-5 h-5 text-blue-600" />
                        AI Plan Analysis
                      </CardTitle>
                      <CardDescription>
                        Intelligent analysis of your 6-month sales plan - identifying risks, unrealistic expectations, and bottlenecks
                      </CardDescription>
                    </div>
                    <Button
                      onClick={analyzePlan}
                      disabled={planAnalysisLoading}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {planAnalysisLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                      Analyze Plan
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {planAnalysis ? (
                    <div className="space-y-4">
                      {/* Summary Stats */}
                      {planAnalysis.forecast_summary && (
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4 bg-white rounded-lg border border-blue-200 mb-4">
                          <div className="text-center">
                            <div className="text-xl font-bold text-slate-600">£{((planAnalysis.forecast_summary.historical_avg_nb || 0) / 1000000).toFixed(2)}M</div>
                            <div className="text-xs text-slate-500">Historical NB (3mo avg)</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xl font-bold text-blue-700">£{((planAnalysis.forecast_summary.avg_forecast_nb || planAnalysis.forecast_summary.total_monthly_nb) / 1000000).toFixed(2)}M</div>
                            <div className="text-xs text-slate-500">Forecast NB (6mo avg)</div>
                          </div>
                          <div className="text-center">
                            <div className={`text-xl font-bold ${(planAnalysis.forecast_summary.forecast_vs_historical_pct || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {(planAnalysis.forecast_summary.forecast_vs_historical_pct || 0) >= 0 ? '+' : ''}{(planAnalysis.forecast_summary.forecast_vs_historical_pct || 0).toFixed(0)}%
                            </div>
                            <div className="text-xs text-slate-500">Forecast vs Historical</div>
                          </div>
                          <div className="text-center">
                            <div className={`text-xl font-bold ${(planAnalysis.forecast_summary.avg_gap || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {(planAnalysis.forecast_summary.avg_gap || 0) >= 0 ? '+' : ''}£{((planAnalysis.forecast_summary.avg_gap || 0) / 1000).toFixed(0)}k
                            </div>
                            <div className="text-xs text-slate-500">Gap vs Target</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xl font-bold text-purple-700">{planAnalysis.forecast_summary.broker_count || 0}</div>
                            <div className="text-xs text-slate-500">Brokers Analyzed</div>
                          </div>
                        </div>
                      )}
                      
                      {/* AI Analysis Content */}
                      <div className="prose prose-sm max-w-none dark:prose-invert">
                        <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                          components={{
                            table: ({node, ...props}) => <div className="overflow-x-auto my-3"><table className="text-xs border-collapse w-auto min-w-[50%]" {...props} /></div>,
                            th: ({node, ...props}) => <th className="border border-slate-200 bg-slate-50 px-2 py-1 text-left font-semibold" {...props} />,
                            td: ({node, ...props}) => <td className="border border-slate-100 px-2 py-1" {...props} />,
                            h1: ({node, ...props}) => <h1 className="text-xl font-bold mt-4 mb-2 text-blue-900" {...props} />,
                            h2: ({node, ...props}) => <h2 className="text-lg font-semibold mt-4 mb-2 text-blue-800 border-b border-blue-200 pb-1" {...props} />,
                            h3: ({node, ...props}) => <h3 className="text-base font-semibold mt-3 mb-1 text-blue-700" {...props} />,
                            p: ({node, ...props}) => <p className="my-2 text-slate-700" {...props} />,
                            ul: ({node, ...props}) => <ul className="list-disc pl-5 my-2 space-y-1" {...props} />,
                            ol: ({node, ...props}) => <ol className="list-decimal pl-5 my-2 space-y-1" {...props} />,
                            li: ({node, ...props}) => <li className="text-slate-700" {...props} />,
                            strong: ({node, ...props}) => <strong className="font-semibold text-blue-900" {...props} />,
                            em: ({node, ...props}) => <em className="text-blue-700" {...props} />,
                          }}
                        >
                          {planAnalysis.analysis}
                        </ReactMarkdown>
                      </div>
                      
                      {/* Analysis Metadata */}
                      <div className="mt-4 pt-3 border-t border-blue-200 flex items-center justify-between text-xs text-muted-foreground">
                        <span>Generated: {new Date(planAnalysis.generated_at).toLocaleString("en-GB")}</span>
                        <span>{planAnalysis.forecast_summary?.broker_count || 0} brokers analyzed</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Brain className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>Click "Analyze Plan" to get AI-powered insights on your sales planning.</p>
                      <p className="text-xs mt-2 max-w-md mx-auto">
                        The AI will analyze your current scenario settings (master overrides and broker-specific adjustments) 
                        to identify risks, bottlenecks, and unrealistic assumptions in your 6-month plan.
                      </p>
                      {(Object.keys(brokerOverrides).length > 0 || Object.values(scenario).some(v => v !== null)) && (
                        <div className="mt-4 p-3 bg-blue-100 rounded-lg inline-block">
                          <p className="text-xs text-blue-700 font-medium">
                            Current scenario includes {Object.keys(brokerOverrides).length} broker overrides
                            {Object.values(scenario).some(v => v !== null) && ' + master overrides'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* ==================== PERFORMANCE & REPORTING TAB ==================== */}
        <TabsContent value="performance" className="space-y-6">
          {/* FY Selector for Performance Tab */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                <Calendar className="w-3 h-3 mr-1" />
                {performanceFYRange.label}
              </Badge>
              <span className="text-xs text-muted-foreground">
                (Jul {performanceFY} - Jun {performanceFY + 1})
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Financial Year:</span>
              <Select value={String(performanceFY)} onValueChange={(val) => setPerformanceFY(parseInt(val))}>
                <SelectTrigger className="w-[140px]" data-testid="performance-fy-selector">
                  <SelectValue placeholder="Select FY" />
                </SelectTrigger>
                <SelectContent>
                  {fyOptions.map(opt => (
                    <SelectItem key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Performance Sub-tabs - Organized into logical groups */}
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList className="flex-wrap h-auto gap-1 p-2 bg-slate-50 border border-slate-200">
              {/* Overview */}
              <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">Overview</TabsTrigger>
              
              {/* Activity & Pipeline Group */}
              <div className="w-px h-6 bg-slate-300 mx-1" />
              <TabsTrigger value="daily" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">Daily Activity</TabsTrigger>
              <TabsTrigger value="funnel" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">Funnel</TabsTrigger>
              <TabsTrigger value="velocity" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">Deal Velocity</TabsTrigger>
              
              {/* Brokers Group */}
              <div className="w-px h-6 bg-slate-300 mx-1" />
              <TabsTrigger value="brokers" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">Broker Performance</TabsTrigger>
              <TabsTrigger value="rejected" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">Deal Leakage</TabsTrigger>
              
              {/* Offers & Underwriting Group */}
              <div className="w-px h-6 bg-slate-300 mx-1" />
              <TabsTrigger value="offers" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">Offers</TabsTrigger>
              <TabsTrigger value="underwriters" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">Underwriters</TabsTrigger>
              <TabsTrigger value="declines" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">Declines</TabsTrigger>
              
              {/* Funding Group */}
              <div className="w-px h-6 bg-slate-300 mx-1" />
              <TabsTrigger value="topups" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">New vs Top-ups</TabsTrigger>
              <TabsTrigger value="funding" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Funding</TabsTrigger>
            </TabsList>

        {/* Targets Tab - kept for backward compatibility */}
        <TabsContent value="targets" className="space-y-6">
          <Card className="border-emerald-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{currentDate.year} Sales Targets</CardTitle>
                  <CardDescription>Monthly funding targets by quarter - click Edit to modify</CardDescription>
                </div>
                <div className="flex gap-2">
                  {editingTargets ? (
                    <>
                      <Button variant="outline" size="sm" onClick={() => { setEditingTargets(false); loadTargets(); }}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={saveTargets} disabled={targetsSaving}>
                        {targetsSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Save Targets
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setEditingTargets(true)}>
                      Edit Targets
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* Q1 */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-emerald-700">Q1 - £2m/month</h3>
                  {['01', '02', '03'].map(month => {
                    const key = `${currentDate.year}-${month}`;
                    const monthName = new Date(currentDate.year, parseInt(month) - 1).toLocaleString('en-GB', { month: 'short' });
                    const actual = monthly_trends.find(m => m.month_key === key)?.funded_value || 0;
                    const target = salesTargets[key] || 0;
                    const pct = target > 0 ? (actual / target * 100) : 0;
                    return (
                      <div key={key} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{monthName} {currentDate.year}</span>
                          {pct >= 100 ? <Badge className="bg-emerald-500">✓</Badge> : 
                           pct >= 80 ? <Badge variant="secondary">On Track</Badge> :
                           <Badge variant="destructive">{pct.toFixed(0)}%</Badge>}
                        </div>
                        {editingTargets ? (
                          <Input
                            type="number"
                            value={salesTargets[key] || ''}
                            onChange={(e) => setSalesTargets({...salesTargets, [key]: parseFloat(e.target.value) || 0})}
                            className="h-8"
                            placeholder="Target £"
                          />
                        ) : (
                          <>
                            <div className="text-sm">Target: £{(target / 1000000).toFixed(2)}m</div>
                            <div className="text-sm">Actual: £{(actual / 1000000).toFixed(2)}m</div>
                            <div className="w-full bg-slate-200 rounded-full h-2 mt-1">
                              <div 
                                className={`h-2 rounded-full ${pct >= 100 ? 'bg-emerald-500' : pct >= 80 ? 'bg-amber-500' : 'bg-red-500'}`}
                                style={{ width: `${Math.min(pct, 100)}%` }}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Q2 */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-blue-700">Q2 - £2.5m/month</h3>
                  {['04', '05', '06'].map(month => {
                    const key = `${currentDate.year}-${month}`;
                    const monthName = new Date(currentDate.year, parseInt(month) - 1).toLocaleString('en-GB', { month: 'short' });
                    const actual = monthly_trends.find(m => m.month_key === key)?.funded_value || 0;
                    const target = salesTargets[key] || 0;
                    const pct = target > 0 ? (actual / target * 100) : 0;
                    return (
                      <div key={key} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{monthName} {currentDate.year}</span>
                          {pct >= 100 ? <Badge className="bg-emerald-500">✓</Badge> : 
                           pct >= 80 ? <Badge variant="secondary">On Track</Badge> :
                           actual > 0 ? <Badge variant="destructive">{pct.toFixed(0)}%</Badge> : 
                           <Badge variant="outline">Future</Badge>}
                        </div>
                        {editingTargets ? (
                          <Input
                            type="number"
                            value={salesTargets[key] || ''}
                            onChange={(e) => setSalesTargets({...salesTargets, [key]: parseFloat(e.target.value) || 0})}
                            className="h-8"
                            placeholder="Target £"
                          />
                        ) : (
                          <>
                            <div className="text-sm">Target: £{(target / 1000000).toFixed(2)}m</div>
                            <div className="text-sm">Actual: £{(actual / 1000000).toFixed(2)}m</div>
                            <div className="w-full bg-slate-200 rounded-full h-2 mt-1">
                              <div 
                                className={`h-2 rounded-full ${pct >= 100 ? 'bg-emerald-500' : pct >= 80 ? 'bg-amber-500' : 'bg-red-500'}`}
                                style={{ width: `${Math.min(pct, 100)}%` }}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Q3 */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-purple-700">Q3 - £3.3m/month</h3>
                  {['07', '08', '09'].map(month => {
                    const key = `${currentDate.year}-${month}`;
                    const monthName = new Date(currentDate.year, parseInt(month) - 1).toLocaleString('en-GB', { month: 'short' });
                    const target = salesTargets[key] || 0;
                    return (
                      <div key={key} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{monthName} {currentDate.year}</span>
                          <Badge variant="outline">Future</Badge>
                        </div>
                        {editingTargets ? (
                          <Input
                            type="number"
                            value={salesTargets[key] || ''}
                            onChange={(e) => setSalesTargets({...salesTargets, [key]: parseFloat(e.target.value) || 0})}
                            className="h-8"
                            placeholder="Target £"
                          />
                        ) : (
                          <div className="text-sm">Target: £{(target / 1000000).toFixed(2)}m</div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Q4 */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-orange-700">Q4 - £3.5m→£4m</h3>
                  {['10', '11', '12'].map(month => {
                    const key = `${currentDate.year}-${month}`;
                    const monthName = new Date(currentDate.year, parseInt(month) - 1).toLocaleString('en-GB', { month: 'short' });
                    const target = salesTargets[key] || 0;
                    return (
                      <div key={key} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{monthName} {currentDate.year}</span>
                          <Badge variant="outline">Future</Badge>
                        </div>
                        {editingTargets ? (
                          <Input
                            type="number"
                            value={salesTargets[key] || ''}
                            onChange={(e) => setSalesTargets({...salesTargets, [key]: parseFloat(e.target.value) || 0})}
                            className="h-8"
                            placeholder="Target £"
                          />
                        ) : (
                          <div className="text-sm">Target: £{(target / 1000000).toFixed(2)}m</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Target vs Actual Chart */}
              <div className="mt-6">
                <h3 className="font-semibold mb-3">Target vs Actual Performance</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={monthly_trends.filter(m => m.month_key >= `${currentDate.year}-01`)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(v) => `£${(v / 1000000).toFixed(1)}m`} />
                    <Tooltip 
                      formatter={(value, name) => [`£${(value / 1000000).toFixed(2)}m`, name]}
                      content={<CustomTooltip />}
                    />
                    <Legend />
                    <Bar dataKey="funded_value" fill="#10b981" name="Actual Funded" radius={[4, 4, 0, 0]} />
                    <Line 
                      type="monotone" 
                      dataKey={(d) => salesTargets[d.month_key] || 0}
                      stroke="#f59e0b" 
                      strokeWidth={3} 
                      strokeDasharray="5 5"
                      name="Target"
                      dot={{ fill: '#f59e0b', r: 5 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="flex justify-end -mb-2"><ExportXlsxButton sheets="summary" fy={performanceFY} label="Export this section" size="sm" /></div>
          {/* Period Selector */}
          <div className="flex items-center justify-end gap-2 mb-2">
            <span className="text-sm text-muted-foreground">View:</span>
            <div className="flex border rounded-lg overflow-hidden">
              {periodOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setOverviewPeriod(opt.value)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    overviewPeriod === opt.value 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Applications Trend */}
            <Card>
              <CardHeader>
                <CardTitle>Applications {overviewPeriod === 'daily' ? 'per Day' : overviewPeriod === 'weekly' ? 'per Week' : 'per Month'}</CardTitle>
                <CardDescription>Total applications received ({performanceFYRange.label})</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={
                    overviewPeriod === 'monthly' ? performanceMonthlyTrends : 
                    overviewPeriod === 'weekly' ? (daily_activity?.weekly_apps || []) : 
                    (daily_activity?.apps_per_day || [])
                  }>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={overviewPeriod === 'monthly' ? 'month' : overviewPeriod === 'weekly' ? 'week' : 'day'} />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey={overviewPeriod === 'weekly' ? 'apps' : 'applications'} fill="#3b82f6" name="Applications" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Offer Rate Trend */}
            <Card>
              <CardHeader>
                <CardTitle>Offer Rate Trend ({overviewPeriod === 'daily' ? 'Daily' : overviewPeriod === 'weekly' ? 'Weekly' : 'Monthly'})</CardTitle>
                <CardDescription>% of applications that received an offer ({performanceFYRange.label})</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={
                    overviewPeriod === 'monthly' ? performanceMonthlyTrends : 
                    overviewPeriod === 'weekly' ? (daily_activity?.weekly_offer_rates || []) : 
                    (daily_activity?.apps_per_day || [])
                  }>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={overviewPeriod === 'monthly' ? 'month' : overviewPeriod === 'weekly' ? 'week' : 'day'} />
                    <YAxis domain={[0, 100]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line 
                      type="monotone" 
                      dataKey={overviewPeriod === 'weekly' ? 'offer_rate' : 'offer_rate'} 
                      stroke="#f59e0b" 
                      strokeWidth={2}
                      name="Offer Rate %"
                      dot={{ fill: '#f59e0b' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Summary Stats Table */}
          <Card>
            <CardHeader>
              <CardTitle>FY Summary ({performanceFYRange.label})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold text-blue-600">{performanceMonthlyTrends.reduce((sum, m) => sum + (m.applications || 0), 0).toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">Total Applications</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold text-amber-600">{(performanceMonthlyTrends.reduce((sum, m) => sum + (m.offers || 0), 0) / Math.max(performanceMonthlyTrends.reduce((sum, m) => sum + (m.applications || 0), 0), 1) * 100).toFixed(1)}%</p>
                  <p className="text-sm text-muted-foreground">Offer Rate</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold text-red-600">{(performanceMonthlyTrends.reduce((sum, m) => sum + (m.declined || 0), 0) / Math.max(performanceMonthlyTrends.reduce((sum, m) => sum + (m.applications || 0), 0), 1) * 100).toFixed(1)}%</p>
                  <p className="text-sm text-muted-foreground">Decline Rate</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold text-emerald-600">{performanceMonthlyTrends.reduce((sum, m) => sum + (m.funded || 0), 0).toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">Total Funded</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold text-purple-600">£{(performanceMonthlyTrends.reduce((sum, m) => sum + (m.funded_value || 0), 0) / 1000000).toFixed(1)}M</p>
                  <p className="text-sm text-muted-foreground">Funded Value</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Funnel Analysis Tab */}
        <TabsContent value="funnel" className="space-y-4">
          <div className="flex justify-end -mb-2"><ExportXlsxButton sheets="funnel" fy={performanceFY} label="Export this section" size="sm" /></div>
          {/* Period Selector */}
          <div className="flex items-center justify-end gap-2 mb-2">
            <span className="text-sm text-muted-foreground">View:</span>
            <div className="flex border rounded-lg overflow-hidden">
              {periodOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFunnelPeriod(opt.value)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    funnelPeriod === opt.value 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>{funnelPeriod === 'daily' ? 'Daily' : funnelPeriod === 'weekly' ? 'Weekly' : 'Monthly'} Funnel: Applications → Offers → Declined → Funded</CardTitle>
              <CardDescription>Track the conversion at each stage ({performanceFYRange.label})</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={
                  funnelPeriod === 'monthly' ? performanceMonthlyTrends : 
                  funnelPeriod === 'weekly' ? (daily_activity?.weekly_apps || []) : 
                  (daily_activity?.apps_per_day || [])
                }>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey={funnelPeriod === 'monthly' ? 'month' : funnelPeriod === 'weekly' ? 'week' : 'day'} />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar yAxisId="left" dataKey={funnelPeriod === 'weekly' ? 'apps' : 'applications'} fill="#3b82f6" name="Applications" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="left" dataKey="offers" fill="#f59e0b" name="Offers" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="left" dataKey="declined" fill="#ef4444" name="Declined" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="left" dataKey="funded" fill="#10b981" name="Funded" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="offer_rate" stroke="#8b5cf6" strokeWidth={2} name="Offer Rate %" dot={{ fill: '#8b5cf6' }} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Monthly Breakdown Table */}
          <Card>
            <CardHeader>
              <CardTitle>Monthly Breakdown</CardTitle>
              <CardDescription>Note: Recent months may show 0 declined - apps not yet moved to Declines board</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Month</th>
                      <th className="text-right py-3 px-2">Apps</th>
                      <th className="text-right py-3 px-2">Offers</th>
                      <th className="text-right py-3 px-2">Offer Rate</th>
                      <th className="text-right py-3 px-2">Declined</th>
                      <th className="text-right py-3 px-2">Decline Rate</th>
                      <th className="text-right py-3 px-2">Funded</th>
                      <th className="text-right py-3 px-2">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...performanceMonthlyTrends].reverse().map((m, idx) => (
                      <tr key={m.month_key} className={idx % 2 === 0 ? 'bg-slate-50' : ''}>
                        <td className="py-2 px-2 font-medium">{m.month_full}</td>
                        <td className="text-right py-2 px-2">{m.applications}</td>
                        <td className="text-right py-2 px-2">{m.offers}</td>
                        <td className="text-right py-2 px-2">
                          <Badge variant={m.offer_rate > 30 ? "default" : m.offer_rate > 20 ? "secondary" : "outline"}>
                            {m.offer_rate}%
                          </Badge>
                        </td>
                        <td className="text-right py-2 px-2 text-red-600">{m.declined || 0}</td>
                        <td className="text-right py-2 px-2">
                          {m.decline_rate > 0 ? (
                            <Badge variant="destructive">{m.decline_rate}%</Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">Pending</span>
                          )}
                        </td>
                        <td className="text-right py-2 px-2 text-emerald-600">{m.funded}</td>
                        <td className="text-right py-2 px-2">£{m.funded_value?.toLocaleString() || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Offer Rejected Tab */}
        <TabsContent value="rejected" className="space-y-4">
          <div className="flex justify-end -mb-2"><ExportXlsxButton sheets="offers" fy={performanceFY} label="Export this section" size="sm" /></div>
          {/* Period Selector */}
          <div className="flex items-center justify-end gap-2 mb-2">
            <span className="text-sm text-muted-foreground">View:</span>
            <div className="flex border rounded-lg overflow-hidden">
              {periodOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setLeakagePeriod(opt.value)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    leakagePeriod === opt.value 
                      ? 'bg-amber-600 text-white' 
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Deal Leakage Heatmap - AI Recommended Feature */}
          <Card className="border-red-300 bg-gradient-to-br from-red-50 to-orange-50">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Deal Leakage Heatmap ({performanceFYRange.label})</CardTitle>
                  <CardDescription>Brokers with high volume + high rejection rate = significant £ leakage</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Heatmap Legend */}
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-4">
                    <span className="text-muted-foreground">Severity:</span>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-yellow-200"></div>
                      <span>Medium</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-orange-300"></div>
                      <span>High</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-red-400"></div>
                      <span>Critical</span>
                    </div>
                  </div>
                  <span className="text-muted-foreground">Showing brokers with 5+ offers and 30%+ rejection rate</span>
                </div>
                
                {/* Heatmap Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {(() => {
                    // Filter to brokers with significant leakage: 5+ offers AND 30%+ rejection rate
                    const leakageBrokers = (rejection_analysis.by_broker_ytd || [])
                      .filter(b => b.offers >= 5 && b.rejection_rate >= 30)
                      .sort((a, b) => b.rejected_value - a.rejected_value)
                      .slice(0, 9);
                    
                    if (leakageBrokers.length === 0) {
                      return (
                        <div className="col-span-full text-center py-8 text-muted-foreground">
                          <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                          <p>No significant deal leakage detected</p>
                        </div>
                      );
                    }
                    
                    // Calculate max values for scaling
                    const maxValue = Math.max(...leakageBrokers.map(b => b.rejected_value));
                    
                    return leakageBrokers.map((broker, idx) => {
                      // Calculate severity score (0-100) based on rejection rate and value
                      const rateScore = Math.min(broker.rejection_rate, 100);
                      const valueScore = (broker.rejected_value / maxValue) * 100;
                      const severity = (rateScore * 0.4 + valueScore * 0.6); // Weight value more
                      
                      // Determine color based on severity
                      const bgColor = severity >= 70 ? 'bg-red-100 border-red-300' :
                                     severity >= 50 ? 'bg-orange-100 border-orange-300' :
                                     'bg-yellow-100 border-yellow-300';
                      const textColor = severity >= 70 ? 'text-red-700' :
                                       severity >= 50 ? 'text-orange-700' :
                                       'text-yellow-700';
                      const barColor = severity >= 70 ? 'bg-red-400' :
                                      severity >= 50 ? 'bg-orange-400' :
                                      'bg-yellow-400';
                      
                      return (
                        <div 
                          key={broker.name} 
                          className={`p-3 rounded-lg border-2 ${bgColor} relative overflow-hidden`}
                        >
                          {/* Severity indicator bar */}
                          <div 
                            className={`absolute bottom-0 left-0 h-1 ${barColor}`} 
                            style={{ width: `${severity}%` }}
                          />
                          
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center ${
                                idx === 0 ? 'bg-red-200 text-red-800' :
                                idx === 1 ? 'bg-orange-200 text-orange-800' :
                                idx === 2 ? 'bg-amber-200 text-amber-800' :
                                'bg-slate-200 text-slate-600'
                              }`}>{idx + 1}</span>
                              <span className="font-medium text-sm truncate max-w-[140px]" title={broker.name}>
                                {broker.name}
                              </span>
                            </div>
                            <Badge variant="destructive" className="text-xs">
                              {broker.rejection_rate.toFixed(0)}%
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div>
                              <p className="text-muted-foreground">Offers</p>
                              <p className="font-semibold">{broker.offers}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Rejected</p>
                              <p className={`font-semibold ${textColor}`}>{broker.rejected}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">£ Lost</p>
                              <p className={`font-bold ${textColor}`}>
                                £{(broker.rejected_value / 1000).toFixed(0)}k
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                
                {/* Summary Stats */}
                <div className="flex items-center justify-between pt-3 border-t">
                  <div className="flex items-center gap-6 text-sm">
                    <div>
                      <span className="text-muted-foreground">Total Leakage from High-Risk Brokers: </span>
                      <span className="font-bold text-red-600">
                        £{((rejection_analysis.by_broker_ytd || [])
                          .filter(b => b.offers >= 5 && b.rejection_rate >= 30)
                          .reduce((sum, b) => sum + b.rejected_value, 0) / 1000).toFixed(0)}k
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Deals Lost: </span>
                      <span className="font-bold text-red-600">
                        {(rejection_analysis.by_broker_ytd || [])
                          .filter(b => b.offers >= 5 && b.rejection_rate >= 30)
                          .reduce((sum, b) => sum + b.rejected, 0)}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    YTD data • High rejection rate may indicate broker shopping competitors
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-red-600">{summary.total_rejected || 0}</p>
                <p className="text-sm text-muted-foreground mt-1">Total Offer Rejections</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-amber-600">{summary.total_conditional || 0}</p>
                <p className="text-sm text-muted-foreground mt-1">Conditional Offers (At Risk)</p>
                <p className="text-xs text-amber-600 mt-1">£{((summary.total_conditional_value || 0) / 1000).toFixed(0)}k exposure</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-orange-600">£{((summary.total_rejected_value || 0) / 1000000).toFixed(2)}M</p>
                <p className="text-sm text-muted-foreground mt-1">Lost Offer Value</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-slate-600">{summary.overall_rejection_rate || 0}%</p>
                <p className="text-sm text-muted-foreground mt-1">Rejection Rate (of offers)</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-purple-600">{current_month.rejected || 0}</p>
                <p className="text-sm text-muted-foreground mt-1">Rejected This Month</p>
                {current_month.conditional > 0 && (
                  <p className="text-xs text-amber-600 mt-1">+{current_month.conditional} at risk</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Monthly Rejection Chart with Conditional Offers Stacked */}
          <Card>
            <CardHeader>
              <CardTitle>Offer Rejections by Month</CardTitle>
              <CardDescription>Rejected offers with Conditional Offers (at risk) stacked on top - these may convert or reject within ~2 weeks</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={monthly_trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="rejected_nb" stackId="rejection" fill="#ef4444" name="Rejected NB" />
                  <Bar yAxisId="left" dataKey="rejected_tu" stackId="rejection" fill="#f97316" name="Rejected TU" />
                  <Bar yAxisId="left" dataKey="conditional" stackId="rejection" fill="#fbbf24" name="Conditional (At Risk)" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="rejected_value" stroke="#8b5cf6" strokeWidth={2} name="Rejected Value (£)" dot={{ fill: '#8b5cf6' }} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Rejection Rate vs Pipeline Chart with Projection */}
          <Card>
            <CardHeader>
              <CardTitle>Rejection Rate vs Pipeline</CardTitle>
              <CardDescription>Applications, offers made, and rejections with projected rejection rate exposure</CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                // Calculate projected rejection RATE including conditional offers at risk
                const projectedData = monthly_trends.map((m, idx) => {
                  const conditionalAtRisk = m.conditional || 0;
                  const offers = m.offers || 0;
                  const rejected = m.rejected || 0;
                  
                  // Calculate what the rejection rate would be if all conditional offers rejected
                  const projectedRejected = rejected + conditionalAtRisk;
                  const projectedRate = offers > 0 ? (projectedRejected / offers * 100) : 0;
                  
                  return {
                    ...m,
                    // Only show projected rate for months with conditional offers
                    projected_rejection_rate: conditionalAtRisk > 0 ? Math.round(projectedRate * 10) / 10 : null
                  };
                });
                
                return (
                  <ResponsiveContainer width="100%" height={380}>
                    <ComposedChart data={projectedData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 100]} label={{ value: 'Rate %', angle: 90, position: 'insideRight' }} />
                      <Tooltip content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0]?.payload;
                          return (
                            <div className="bg-white p-3 border rounded-lg shadow-lg">
                              <p className="font-semibold mb-2">{label}</p>
                              {payload.filter(p => p.value !== null).map((entry, index) => (
                                <p key={index} style={{ color: entry.color }} className="text-sm">
                                  {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
                                  {entry.name.includes('Rate') || entry.name.includes('Exposure') ? '%' : ''}
                                </p>
                              ))}
                              {data?.conditional > 0 && (
                                <div className="mt-2 pt-2 border-t border-dashed">
                                  <p className="text-xs text-amber-600 font-medium">Exposure Analysis:</p>
                                  <p className="text-xs text-muted-foreground">{data.conditional} conditional offers at risk</p>
                                  <p className="text-xs text-muted-foreground">Current rate: {data.rejection_rate}%</p>
                                  <p className="text-xs text-red-600 font-medium">If all reject: {data.projected_rejection_rate}%</p>
                                </div>
                              )}
                            </div>
                          );
                        }
                        return null;
                      }} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="applications" fill="#94a3b8" name="Applications" radius={[4, 4, 0, 0]} />
                      <Bar yAxisId="left" dataKey="offers" fill="#3b82f6" name="Offers Made" radius={[4, 4, 0, 0]} />
                      <Bar yAxisId="left" dataKey="rejected" fill="#ef4444" name="Rejections" radius={[4, 4, 0, 0]} />
                      <Line yAxisId="right" type="monotone" dataKey="rejection_rate" stroke="#f59e0b" strokeWidth={3} name="Rejection Rate %" dot={{ fill: '#f59e0b', r: 5 }} />
                      <Line 
                        yAxisId="right" 
                        type="monotone" 
                        dataKey="projected_rejection_rate" 
                        stroke="#f59e0b" 
                        strokeWidth={2} 
                        strokeDasharray="8 4"
                        name="Projected Rate (if At Risk reject)" 
                        dot={{ fill: '#ef4444', stroke: '#f59e0b', strokeWidth: 2, r: 6 }}
                        connectNulls={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                );
              })()}
              <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <div className="w-6 border-t-2 border-dashed border-amber-500"></div>
                  <span>Dashed orange line shows projected rejection rate if all conditional offers reject</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Breakdown Table */}
          <Card>
            <CardHeader>
              <CardTitle>Monthly Rejection Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Month</th>
                      <th className="text-right py-3 px-2">Offers</th>
                      <th className="text-right py-3 px-2">Rejected</th>
                      <th className="text-right py-3 px-2">NB</th>
                      <th className="text-right py-3 px-2">TU</th>
                      <th className="text-right py-3 px-2 text-amber-600">At Risk</th>
                      <th className="text-right py-3 px-2">Rejection Rate</th>
                      <th className="text-right py-3 px-2">Lost Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...monthly_trends].reverse().map((m, idx) => (
                      <tr key={m.month_key} className={idx % 2 === 0 ? 'bg-slate-50' : ''}>
                        <td className="py-2 px-2 font-medium">{m.month_full}</td>
                        <td className="text-right py-2 px-2">{m.offers}</td>
                        <td className="text-right py-2 px-2 text-red-600 font-semibold">{m.rejected || 0}</td>
                        <td className="text-right py-2 px-2">{m.rejected_nb || 0}</td>
                        <td className="text-right py-2 px-2">{m.rejected_tu || 0}</td>
                        <td className="text-right py-2 px-2">
                          {m.conditional > 0 ? (
                            <Badge variant="secondary" className="bg-amber-100 text-amber-700">{m.conditional}</Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="text-right py-2 px-2">
                          <Badge variant={m.rejection_rate > 30 ? "destructive" : m.rejection_rate > 15 ? "secondary" : "outline"}>
                            {m.rejection_rate || 0}%
                          </Badge>
                        </td>
                        <td className="text-right py-2 px-2">£{(m.rejected_value || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Broker Rejection Analysis - Week/Month/Year Tabs */}
          <Card className="border-red-200 bg-red-50/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Broker Rejection Analysis
              </CardTitle>
              <CardDescription>
                Identify which brokers are bouncing offers - possibly sending deals to competitors offering higher commissions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="this_month" className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="this_week">This Week</TabsTrigger>
                  <TabsTrigger value="this_month">This Month</TabsTrigger>
                  <TabsTrigger value="ytd">Year to Date</TabsTrigger>
                </TabsList>

                {/* This Week */}
                <TabsContent value="this_week">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-red-100/50">
                          <th className="text-left py-3 px-2">#</th>
                          <th className="text-left py-3 px-2">Broker</th>
                          <th className="text-right py-3 px-2">Offers</th>
                          <th className="text-right py-3 px-2">Rejected</th>
                          <th className="text-right py-3 px-2">Rejection Rate</th>
                          <th className="text-right py-3 px-2">Lost Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(rejection_analysis.by_broker_this_week || []).slice(0, 15).map((broker, idx) => (
                          <tr key={broker.name} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${broker.rejection_rate > 70 ? 'bg-red-50' : ''}`}>
                            <td className="py-2 px-2">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                idx === 0 ? "bg-red-200 text-red-800" :
                                idx === 1 ? "bg-orange-200 text-orange-800" :
                                idx === 2 ? "bg-amber-200 text-amber-800" :
                                "bg-slate-200 text-slate-600"
                              }`}>{idx + 1}</div>
                            </td>
                            <td className="py-2 px-2 font-medium">{broker.name}</td>
                            <td className="text-right py-2 px-2">{broker.offers}</td>
                            <td className="text-right py-2 px-2 text-red-600 font-semibold">{broker.rejected}</td>
                            <td className="text-right py-2 px-2">
                              <Badge variant={broker.rejection_rate > 70 ? "destructive" : broker.rejection_rate > 50 ? "secondary" : "outline"}>
                                {broker.rejection_rate}%
                              </Badge>
                            </td>
                            <td className="text-right py-2 px-2">£{(broker.rejected_value || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                        {(!rejection_analysis.by_broker_this_week || rejection_analysis.by_broker_this_week.length === 0) && (
                          <tr><td colSpan={6} className="text-center py-4 text-muted-foreground">No rejection data this week</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>

                {/* This Month */}
                <TabsContent value="this_month">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-red-100/50">
                          <th className="text-left py-3 px-2">#</th>
                          <th className="text-left py-3 px-2">Broker</th>
                          <th className="text-right py-3 px-2">Offers</th>
                          <th className="text-right py-3 px-2">Rejected</th>
                          <th className="text-right py-3 px-2">Rejection Rate</th>
                          <th className="text-right py-3 px-2">Lost Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(rejection_analysis.by_broker_this_month || []).slice(0, 15).map((broker, idx) => (
                          <tr key={broker.name} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${broker.rejection_rate > 70 ? 'bg-red-50' : ''}`}>
                            <td className="py-2 px-2">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                idx === 0 ? "bg-red-200 text-red-800" :
                                idx === 1 ? "bg-orange-200 text-orange-800" :
                                idx === 2 ? "bg-amber-200 text-amber-800" :
                                "bg-slate-200 text-slate-600"
                              }`}>{idx + 1}</div>
                            </td>
                            <td className="py-2 px-2 font-medium">{broker.name}</td>
                            <td className="text-right py-2 px-2">{broker.offers}</td>
                            <td className="text-right py-2 px-2 text-red-600 font-semibold">{broker.rejected}</td>
                            <td className="text-right py-2 px-2">
                              <Badge variant={broker.rejection_rate > 70 ? "destructive" : broker.rejection_rate > 50 ? "secondary" : "outline"}>
                                {broker.rejection_rate}%
                              </Badge>
                            </td>
                            <td className="text-right py-2 px-2">£{(broker.rejected_value || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                        {(!rejection_analysis.by_broker_this_month || rejection_analysis.by_broker_this_month.length === 0) && (
                          <tr><td colSpan={6} className="text-center py-4 text-muted-foreground">No rejection data this month</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>

                {/* YTD */}
                <TabsContent value="ytd">
                  <p className="text-xs text-muted-foreground mb-3">* Only showing brokers with 5+ offers for meaningful rejection rates</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-red-100/50">
                          <th className="text-left py-3 px-2">#</th>
                          <th className="text-left py-3 px-2">Broker</th>
                          <th className="text-right py-3 px-2">Offers</th>
                          <th className="text-right py-3 px-2">Rejected</th>
                          <th className="text-right py-3 px-2">Rejection Rate</th>
                          <th className="text-right py-3 px-2">Lost Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(rejection_analysis.by_broker_ytd || []).slice(0, 15).map((broker, idx) => (
                          <tr key={broker.name} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${broker.rejection_rate > 70 ? 'bg-red-50' : ''}`}>
                            <td className="py-2 px-2">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                idx === 0 ? "bg-red-200 text-red-800" :
                                idx === 1 ? "bg-orange-200 text-orange-800" :
                                idx === 2 ? "bg-amber-200 text-amber-800" :
                                "bg-slate-200 text-slate-600"
                              }`}>{idx + 1}</div>
                            </td>
                            <td className="py-2 px-2 font-medium">{broker.name}</td>
                            <td className="text-right py-2 px-2">{broker.offers}</td>
                            <td className="text-right py-2 px-2 text-red-600 font-semibold">{broker.rejected}</td>
                            <td className="text-right py-2 px-2">
                              <Badge variant={broker.rejection_rate > 70 ? "destructive" : broker.rejection_rate > 50 ? "secondary" : "outline"}>
                                {broker.rejection_rate}%
                              </Badge>
                            </td>
                            <td className="text-right py-2 px-2">£{(broker.rejected_value || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                        {(!rejection_analysis.by_broker_ytd || rejection_analysis.by_broker_ytd.length === 0) && (
                          <tr><td colSpan={6} className="text-center py-4 text-muted-foreground">No rejection data this year</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Broker Month-on-Month Rejection Trends */}
          <Card className="border-orange-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-orange-600" />
                Broker Rejection Rate Trends (Month-on-Month)
              </CardTitle>
              <CardDescription>
                Track how broker rejection rates are changing over time - identify if brokers are increasingly favoring competitors
              </CardDescription>
            </CardHeader>
            <CardContent>
              {rejection_analysis.broker_monthly_trends && (
                <>
                  {/* Current Month Note */}
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                    <p className="font-medium text-amber-800">📊 Current Month ({currentDate.monthNameShort}): Rates include conditional offers at risk</p>
                    <p className="text-amber-700 text-xs">Since many offers are still pending in conditional status, the current month shows projected rates including these at-risk offers to give a realistic trend picture.</p>
                  </div>

                  {/* Line Chart showing rejection rate trends */}
                  <div className="mb-6">
                    <p className="text-sm text-muted-foreground mb-3">Rejection rate % by month for top brokers (by volume) - current month includes conditional offers</p>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={rejection_analysis.broker_monthly_trends?.monthly_summary || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis domain={[0, 100]} label={{ value: 'Rejection Rate %', angle: -90, position: 'insideLeft' }} />
                        <Tooltip 
                          content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                              return (
                                <div className="bg-white p-3 border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                                  <p className="font-semibold mb-2">{label}</p>
                                  {payload
                                    .filter(p => p.value !== null)
                                    .sort((a, b) => (b.value || 0) - (a.value || 0))
                                    .map((entry, index) => (
                                      <p key={index} style={{ color: entry.color }} className="text-sm">
                                        {entry.name}: {entry.value}%
                                      </p>
                                    ))}
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: '11px' }} />
                        {(rejection_analysis.broker_monthly_trends?.brokers_tracked || []).map((broker, idx) => {
                          const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f43f5e'];
                          const safe_name = broker.replace(/ /g, "_").replace(/\./g, "").replace(/,/g, "").slice(0, 20);
                          return (
                            <Line 
                              key={broker}
                              type="monotone" 
                              dataKey={safe_name}
                              stroke={colors[idx % colors.length]}
                              strokeWidth={2}
                              dot={{ fill: colors[idx % colors.length], r: 3 }}
                              name={broker}
                              connectNulls={false}
                            />
                          );
                        })}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Detailed broker table with monthly breakdown */}
                  <div className="overflow-x-auto">
                    <p className="text-sm font-medium mb-3">Monthly Rejection Rates by Broker</p>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-orange-50">
                          <th className="text-left py-2 px-2 sticky left-0 bg-orange-50">Broker</th>
                          {(rejection_analysis.broker_monthly_trends?.monthly_summary || []).slice(-6).map(m => (
                            <th key={m.month_key} className={`text-center py-2 px-2 min-w-16 ${m.is_current ? 'bg-amber-100' : ''}`}>
                              {m.month}{m.is_current ? '*' : ''}
                            </th>
                          ))}
                          <th className="text-center py-2 px-2 bg-orange-100">Trend</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(rejection_analysis.broker_monthly_trends?.brokers_tracked || []).slice(0, 10).map((broker, idx) => {
                          const brokerData = rejection_analysis.broker_monthly_trends?.by_broker?.[broker] || [];
                          const last6 = brokerData.slice(-6);
                          
                          // For trend: use projected_rate for current month if available
                          const getEffectiveRate = (m) => m.is_current && m.projected_rate !== null ? m.projected_rate : m.rejection_rate;
                          const firstRate = last6.find(m => getEffectiveRate(m) !== null)?.rejection_rate;
                          const lastMonth = [...last6].reverse().find(m => getEffectiveRate(m) !== null);
                          const lastRate = lastMonth ? getEffectiveRate(lastMonth) : null;
                          const trend = firstRate !== undefined && lastRate !== null ? lastRate - firstRate : null;
                          
                          return (
                            <tr key={broker} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                              <td className="py-2 px-2 font-medium sticky left-0 bg-inherit truncate max-w-32" title={broker}>
                                {broker.length > 20 ? broker.slice(0, 20) + '...' : broker}
                              </td>
                              {last6.map(m => {
                                // Use projected rate for current month
                                const displayRate = m.is_current && m.projected_rate !== null ? m.projected_rate : m.rejection_rate;
                                const hasProjection = m.is_current && m.projected_rate !== null && m.conditional > 0;
                                
                                return (
                                  <td key={m.month_key} className={`text-center py-2 px-2 ${m.is_current ? 'bg-amber-50' : ''}`}>
                                    {displayRate !== null ? (
                                      <div className="flex flex-col items-center">
                                        <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${
                                          displayRate > 80 ? 'bg-red-200 text-red-800' :
                                          displayRate > 60 ? 'bg-orange-200 text-orange-800' :
                                          displayRate > 40 ? 'bg-amber-200 text-amber-800' :
                                          'bg-green-100 text-green-700'
                                        }`}>
                                          {displayRate}%{hasProjection ? '*' : ''}
                                        </span>
                                        {hasProjection && (
                                          <span className="text-[10px] text-amber-600 mt-0.5">+{m.conditional} pending</span>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-slate-300">-</span>
                                    )}
                                  </td>
                                );
                              })}
                              <td className="text-center py-2 px-2 bg-orange-50">
                                {trend !== null ? (
                                  <span className={`inline-flex items-center gap-1 font-semibold ${
                                    trend > 10 ? 'text-red-600' : trend > 0 ? 'text-orange-500' : trend < -10 ? 'text-green-600' : 'text-green-500'
                                  }`}>
                                    {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                    {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
                                  </span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <p className="text-xs text-muted-foreground mt-2">
                      <span className="font-medium">* {currentDate.monthNameShort} shows PROJECTED rate</span> (actual rejected + pending conditional offers). This gives a realistic trend instead of artificially low incomplete data.<br/>
                      Trend shows change from first to last month. 
                      <span className="text-red-600 font-medium"> Red ↑ = rejection rate increasing (getting worse)</span>, 
                      <span className="text-green-600 font-medium"> Green ↓ = decreasing (improving)</span>
                    </p>
                  </div>
                </>
              )}
              {!rejection_analysis.broker_monthly_trends && (
                <p className="text-center text-muted-foreground py-8">No monthly trend data available</p>
              )}
            </CardContent>
          </Card>

          {/* Rejection by Deal Value */}
          <Card>
            <CardHeader>
              <CardTitle>Rejection by Offer Value</CardTitle>
              <CardDescription>Which deal sizes have the highest bounce rate?</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Chart */}
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={rejection_analysis.by_value_bucket || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" fontSize={11} />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="offers" fill="#3b82f6" name="Total Offers" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="left" dataKey="rejected" fill="#ef4444" name="Rejected" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="rejection_rate" stroke="#f59e0b" strokeWidth={3} name="Rejection Rate %" dot={{ fill: '#f59e0b', r: 5 }} />
                  </ComposedChart>
                </ResponsiveContainer>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Offer Size</th>
                        <th className="text-right py-3 px-2">Offers</th>
                        <th className="text-right py-3 px-2">Rejected</th>
                        <th className="text-right py-3 px-2">Rate</th>
                        <th className="text-right py-3 px-2">Lost Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(rejection_analysis.by_value_bucket || []).map((bucket, idx) => (
                        <tr key={bucket.bucket} className={idx % 2 === 0 ? 'bg-slate-50' : ''}>
                          <td className="py-2 px-2 font-medium">{bucket.bucket}</td>
                          <td className="text-right py-2 px-2">{bucket.offers}</td>
                          <td className="text-right py-2 px-2 text-red-600">{bucket.rejected}</td>
                          <td className="text-right py-2 px-2">
                            <Badge variant={bucket.rejection_rate > 70 ? "destructive" : bucket.rejection_rate > 50 ? "secondary" : "outline"}>
                              {bucket.rejection_rate}%
                            </Badge>
                          </td>
                          <td className="text-right py-2 px-2">£{(bucket.rejected_value || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Broker Performance Tab */}
        <TabsContent value="brokers" className="space-y-4">
          <div className="flex justify-end -mb-2"><ExportXlsxButton sheets="brokers" fy={performanceFY} label="Export this section" size="sm" /></div>
          {/* Period Selector */}
          <div className="flex items-center justify-end gap-2 mb-2">
            <span className="text-sm text-muted-foreground">Time Period:</span>
            <div className="flex border rounded-lg overflow-hidden">
              {periodOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setBrokersPeriod(opt.value)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    brokersPeriod === opt.value 
                      ? 'bg-amber-600 text-white' 
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* View Toggle */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle>Broker Performance ({performanceFYRange.label})</CardTitle>
                  <CardDescription>
                    Compare overall stats vs New Business only vs Top-ups only (TU tracking since Nov 2025)
                  </CardDescription>
                </div>
                <div className="flex gap-1 flex-wrap">
                  <Button
                    variant={brokerView === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setBrokerView("all")}
                  >
                    All Deals
                  </Button>
                  <Button
                    variant={brokerView === "newBusiness" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setBrokerView("newBusiness")}
                  >
                    New Business Only
                  </Button>
                  <Button
                    variant={brokerView === "topups" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setBrokerView("topups")}
                  >
                    Top-ups Only
                  </Button>
                  <Button
                    variant={brokerView === "thisMonth" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setBrokerView("thisMonth")}
                  >
                    {currentDate.monthNameShort}
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Problem Brokers Alert */}
          {brokerView === "all" && problem_brokers && problem_brokers.length > 0 && problem_brokers[0].conversion_rate < 20 && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="text-red-800 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Low Conversion Brokers
                </CardTitle>
                <CardDescription className="text-red-700">
                  These brokers have 10+ applications but very low conversion rates
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {problem_brokers.slice(0, 3).map(broker => (
                    <div key={broker.name} className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="font-medium">{broker.name}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground">{broker.total_applications} apps</span>
                        <Badge variant="destructive">{broker.conversion_rate}% conversion</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Broker Table based on view - WITH EXPANDABLE ROWS */}
          <Card>
            <CardHeader>
              <CardTitle>
                {brokerView === "all" && "All Brokers - Expandable Performance View"}
                {brokerView === "newBusiness" && "Top Brokers - New Business Only (Since Nov 2025)"}
                {brokerView === "topups" && "Top Brokers - Top-ups Only (Since Nov 2025)"}
                {brokerView === "thisMonth" && "Top Brokers - This Month"}
              </CardTitle>
              <CardDescription>
                Click on any broker row to expand and see 6-month performance history
                {brokerView === "newBusiness" && " • ⚠️ Only showing true new customer acquisition"}
                {brokerView === "topups" && " • ⚠️ Only showing renewal/top-up business"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-100">
                      <th className="text-left py-3 px-2 w-8"></th>
                      <th className="text-left py-3 px-2">Broker</th>
                      <th className="text-right py-3 px-2">Apps</th>
                      <th className="text-right py-3 px-2">Trend</th>
                      <th className="text-right py-3 px-2">Offers</th>
                      <th className="text-right py-3 px-2">Offer %</th>
                      <th className="text-right py-3 px-2">Funded</th>
                      <th className="text-right py-3 px-2">Conv %</th>
                      <th className="text-right py-3 px-2">Avg Deal</th>
                      <th className="text-right py-3 px-2">Total Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Get all brokers sorted by total apps, filtered by view
                      let filteredBrokers = broker_stats_full || [];
                      
                      if (brokerView === "newBusiness") {
                        filteredBrokers = filteredBrokers.filter(b => b.nb_apps > 0).sort((a, b) => b.nb_apps - a.nb_apps);
                      } else if (brokerView === "topups") {
                        filteredBrokers = filteredBrokers.filter(b => b.tu_apps > 0).sort((a, b) => b.tu_apps - a.tu_apps);
                      } else if (brokerView === "thisMonth") {
                        // Use this month data
                        return broker_this_month.slice(0, 20).map((broker, idx) => {
                          const isExpanded = expandedPerfBroker === broker.name;
                          const fullBrokerData = broker_stats_full?.find(b => b.name === broker.name);
                          
                          return (
                            <React.Fragment key={broker.name}>
                              <tr 
                                className={`cursor-pointer hover:bg-amber-50 transition-colors ${isExpanded ? 'bg-amber-100' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
                                onClick={() => setExpandedPerfBroker(isExpanded ? null : broker.name)}
                              >
                                <td className="py-2 px-2">
                                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </td>
                                <td className="py-2 px-2 font-medium">{broker.name}</td>
                                <td className="text-right py-2 px-2">{broker.applications}</td>
                                <td className="text-right py-2 px-2">-</td>
                                <td className="text-right py-2 px-2 text-emerald-600">{broker.offers}</td>
                                <td className="text-right py-2 px-2">
                                  <Badge variant={broker.approval_rate >= 25 ? "default" : broker.approval_rate >= 15 ? "secondary" : "destructive"} className="text-xs">
                                    {broker.approval_rate}%
                                  </Badge>
                                </td>
                                <td className="text-right py-2 px-2 text-blue-600">-</td>
                                <td className="text-right py-2 px-2">-</td>
                                <td className="text-right py-2 px-2">-</td>
                                <td className="text-right py-2 px-2 font-medium">-</td>
                              </tr>
                              {isExpanded && fullBrokerData && (
                                <tr className="bg-slate-100">
                                  <td colSpan={10} className="p-0">
                                    {/* Render expanded broker detail */}
                                    {(() => {
                                      const brokerTrendData = fullBrokerData.broker_trend_data || [];
                                      const monthlyHistory = brokerTrendData.slice(-6);
                                      const avgApps = Math.round(monthlyHistory.reduce((s, m) => s + (m.nb_apps || 0), 0) / (monthlyHistory.length || 1));
                                      
                                      return (
                                        <div className="bg-white m-2 rounded-lg border border-slate-200 p-4 shadow-sm">
                                          <div className="flex items-center justify-between mb-4 pb-3 border-b">
                                            <div>
                                              <h4 className="font-semibold text-lg">{broker.name}</h4>
                                              <p className="text-sm text-slate-500">6-Month Performance History</p>
                                            </div>
                                            <div className="flex items-center gap-4">
                                              <div className="text-center">
                                                <div className="text-2xl font-bold text-blue-600">{fullBrokerData.total_applications}</div>
                                                <div className="text-xs text-slate-500">Total Apps</div>
                                              </div>
                                              <div className="text-center">
                                                <div className="text-2xl font-bold text-green-600">{fullBrokerData.total_funded}</div>
                                                <div className="text-xs text-slate-500">Total Funded</div>
                                              </div>
                                              <div className="text-center">
                                                <div className="text-2xl font-bold text-purple-600">£{((fullBrokerData.total_value || 0) / 1000).toFixed(0)}k</div>
                                                <div className="text-xs text-slate-500">Total Value</div>
                                              </div>
                                            </div>
                                          </div>
                                          
                                          <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                              <thead className="bg-slate-50">
                                                <tr>
                                                  <th className="text-left py-2 px-3 font-medium">Month</th>
                                                  <th className="text-center py-2 px-3 font-medium">Apps</th>
                                                  <th className="text-center py-2 px-3 font-medium">Offers</th>
                                                  <th className="text-center py-2 px-3 font-medium bg-green-50">Offer Rate</th>
                                                  <th className="text-center py-2 px-3 font-medium">Funded</th>
                                                  <th className="text-center py-2 px-3 font-medium bg-purple-50">Conv. Rate</th>
                                                  <th className="text-right py-2 px-3 font-medium">Value</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {monthlyHistory.map((month, mIdx) => {
                                                  const apps = month.nb_apps || month.applications || 0;
                                                  const offers = month.nb_offers || month.offers || 0;
                                                  const funded = month.nb_funded || month.funded || 0;
                                                  const value = month.nb_value || month.funded_value || 0;
                                                  const offerRate = apps > 0 ? ((offers / apps) * 100).toFixed(0) : 0;
                                                  const convRate = offers > 0 ? ((funded / offers) * 100).toFixed(0) : 0;
                                                  return (
                                                    <tr key={mIdx} className={mIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                                      <td className="py-2 px-3 font-medium">{month.month}</td>
                                                      <td className="text-center py-2 px-3">{apps}</td>
                                                      <td className="text-center py-2 px-3">{offers}</td>
                                                      <td className="text-center py-2 px-3 bg-green-50">
                                                        <span className={`font-medium ${offerRate >= 20 ? 'text-green-600' : offerRate >= 10 ? 'text-amber-600' : 'text-red-600'}`}>
                                                          {offerRate}%
                                                        </span>
                                                      </td>
                                                      <td className="text-center py-2 px-3">{funded}</td>
                                                      <td className="text-center py-2 px-3 bg-purple-50">
                                                        <span className={`font-medium ${convRate >= 40 ? 'text-purple-600' : convRate >= 20 ? 'text-amber-600' : 'text-red-600'}`}>
                                                          {convRate}%
                                                        </span>
                                                      </td>
                                                      <td className="text-right py-2 px-3 font-medium text-emerald-700">
                                                        £{(value / 1000).toFixed(0)}k
                                                      </td>
                                                    </tr>
                                                  );
                                                })}
                                              </tbody>
                                              <tfoot className="bg-blue-50 border-t-2 border-blue-200">
                                                <tr>
                                                  <td className="py-2 px-3 font-semibold">Average</td>
                                                  <td className="text-center py-2 px-3 font-semibold">{avgApps}</td>
                                                  <td className="text-center py-2 px-3 font-semibold">
                                                    {Math.round(monthlyHistory.reduce((s, m) => s + (m.nb_offers || m.offers || 0), 0) / (monthlyHistory.length || 1))}
                                                  </td>
                                                  <td className="text-center py-2 px-3 font-semibold bg-green-100 text-green-700">
                                                    {fullBrokerData.approval_rate}%
                                                  </td>
                                                  <td className="text-center py-2 px-3 font-semibold">
                                                    {Math.round(monthlyHistory.reduce((s, m) => s + (m.nb_funded || m.funded || 0), 0) / (monthlyHistory.length || 1))}
                                                  </td>
                                                  <td className="text-center py-2 px-3 font-semibold bg-purple-100 text-purple-700">
                                                    {fullBrokerData.conversion_rate}%
                                                  </td>
                                                  <td className="text-right py-2 px-3 font-semibold text-emerald-700">
                                                    £{(monthlyHistory.reduce((s, m) => s + (m.nb_value || m.funded_value || 0), 0) / (monthlyHistory.length || 1) / 1000).toFixed(0)}k
                                                  </td>
                                                </tr>
                                              </tfoot>
                                            </table>
                                          </div>
                                          
                                          {/* Mini Charts */}
                                          <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
                                            <div className="bg-blue-50 rounded-lg p-3">
                                              <div className="text-xs font-medium text-blue-700 mb-2">Apps per Month</div>
                                              <div className="h-16 flex items-end gap-1">
                                                {monthlyHistory.map((m, i) => {
                                                  const val = m.nb_apps || m.applications || 0;
                                                  const maxVal = Math.max(...monthlyHistory.map(x => x.nb_apps || x.applications || 0), 1);
                                                  return (
                                                    <div key={i} className="flex-1 flex flex-col items-center">
                                                      <div 
                                                        className="w-full bg-blue-400 rounded-t"
                                                        style={{ height: `${Math.max((val / maxVal) * 100, 8)}%` }}
                                                      />
                                                      <span className="text-[10px] mt-1 text-blue-600">{val}</span>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            </div>
                                            
                                            <div className="bg-green-50 rounded-lg p-3">
                                              <div className="text-xs font-medium text-green-700 mb-2">Offer Rate Trend</div>
                                              <div className="h-16 relative">
                                                <svg className="w-full h-full" viewBox={`0 0 ${Math.max(monthlyHistory.length - 1, 1) * 30} 60`} preserveAspectRatio="none">
                                                  <polyline
                                                    fill="none"
                                                    stroke="#22c55e"
                                                    strokeWidth="3"
                                                    points={monthlyHistory.map((m, i) => {
                                                      const apps = m.nb_apps || m.applications || 0;
                                                      const offers = m.nb_offers || m.offers || 0;
                                                      const rate = apps > 0 ? (offers / apps) * 100 : 0;
                                                      return `${i * 30},${60 - (rate / 50) * 60}`;
                                                    }).join(' ')}
                                                  />
                                                </svg>
                                              </div>
                                            </div>
                                            
                                            <div className="bg-purple-50 rounded-lg p-3">
                                              <div className="text-xs font-medium text-purple-700 mb-2">Conversion Rate Trend</div>
                                              <div className="h-16 relative">
                                                <svg className="w-full h-full" viewBox={`0 0 ${Math.max(monthlyHistory.length - 1, 1) * 30} 60`} preserveAspectRatio="none">
                                                  <polyline
                                                    fill="none"
                                                    stroke="#a855f7"
                                                    strokeWidth="3"
                                                    points={monthlyHistory.map((m, i) => {
                                                      const offers = m.nb_offers || m.offers || 0;
                                                      const funded = m.nb_funded || m.funded || 0;
                                                      const rate = offers > 0 ? (funded / offers) * 100 : 0;
                                                      return `${i * 30},${60 - (rate / 100) * 60}`;
                                                    }).join(' ')}
                                                  />
                                                </svg>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        });
                      } else {
                        filteredBrokers = filteredBrokers.sort((a, b) => b.total_applications - a.total_applications);
                      }
                      
                      // For all/newBusiness/topups views - show ALL brokers with expandable rows
                      return filteredBrokers.map((broker, idx) => {
                        const isExpanded = expandedPerfBroker === broker.name;
                        // Use monthly_nb for history data (same as Planning section)
                        const monthlyHistory = (broker.monthly_nb || []).slice(-6);
                        
                        // Compute chart data the same way as Planning section
                        const appsData = monthlyHistory.map((m, i) => ({ month: m.month || i, value: m.nb_apps || 0 }));
                        const avgApps = appsData.length > 0 ? Math.round(appsData.reduce((s, d) => s + d.value, 0) / appsData.length) : 0;
                        const maxApps = Math.max(...appsData.map(d => d.value), 1);
                        
                        // Calculate approval/offer rate per month
                        const approvalHistory = monthlyHistory.map((m, i) => ({
                          month: m.month || i,
                          value: m.nb_apps > 0 ? Math.round((m.nb_offers / m.nb_apps) * 100) : 0
                        }));
                        
                        // Calculate conversion rate per month
                        const conversionHistory = monthlyHistory.map((m, i) => ({
                          month: m.month || i,
                          value: m.nb_offers > 0 ? Math.round((m.nb_funded / m.nb_offers) * 100) : 0
                        }));
                        
                        // Calculate trend (compare last 2 months)
                        const lastMonth = monthlyHistory[monthlyHistory.length - 1];
                        const prevMonth = monthlyHistory[monthlyHistory.length - 2];
                        const trend = lastMonth && prevMonth ? (lastMonth.nb_apps || 0) - (prevMonth.nb_apps || 0) : 0;
                        
                        // Choose data based on view
                        const apps = brokerView === "newBusiness" ? broker.nb_apps : brokerView === "topups" ? broker.tu_apps : broker.total_applications;
                        const offers = brokerView === "newBusiness" ? broker.nb_offers : brokerView === "topups" ? broker.tu_offers : broker.total_offers;
                        const funded = brokerView === "newBusiness" ? broker.nb_funded : brokerView === "topups" ? broker.tu_funded : broker.total_funded;
                        const value = brokerView === "newBusiness" ? broker.nb_value : brokerView === "topups" ? broker.tu_value : broker.total_value;
                        const approvalRate = brokerView === "newBusiness" ? broker.nb_approval_rate : brokerView === "topups" ? broker.tu_approval_rate : broker.approval_rate;
                        const convRate = brokerView === "newBusiness" ? broker.nb_conversion_rate : brokerView === "topups" ? broker.tu_conversion_rate : broker.conversion_rate;
                        const avgDeal = funded > 0 ? value / funded : 0;
                        
                        return (
                          <React.Fragment key={broker.name}>
                            <tr 
                              className={`cursor-pointer hover:bg-amber-50 transition-colors ${isExpanded ? 'bg-amber-100' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
                              onClick={() => setExpandedPerfBroker(isExpanded ? null : broker.name)}
                            >
                              <td className="py-2 px-2">
                                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </td>
                              <td className="py-2 px-2">
                                <HoverCard>
                                  <HoverCardTrigger asChild>
                                    <span className="font-medium cursor-pointer hover:text-blue-600">{broker.name}</span>
                                  </HoverCardTrigger>
                                  <HoverCardContent className="w-80">
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between">
                                        <h4 className="font-semibold">{broker.name}</h4>
                                        <Badge variant={trend >= 0 ? "default" : "destructive"} className="text-xs">
                                          {trend >= 0 ? '+' : ''}{trend} trend
                                        </Badge>
                                      </div>
                                      <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div className="bg-blue-50 p-2 rounded">
                                          <div className="text-slate-500 text-xs">Apps/Month (6mo)</div>
                                          <div className="font-bold text-blue-700">avg: {avgApps}</div>
                                        </div>
                                        <div className="bg-green-50 p-2 rounded">
                                          <div className="text-slate-500 text-xs">Approval Rate</div>
                                          <div className="font-bold text-green-700">{approvalRate}%</div>
                                        </div>
                                        <div className="bg-purple-50 p-2 rounded">
                                          <div className="text-slate-500 text-xs">Conversion Rate</div>
                                          <div className="font-bold text-purple-700">{convRate}%</div>
                                        </div>
                                        <div className="bg-emerald-50 p-2 rounded">
                                          <div className="text-slate-500 text-xs">Avg Deal Size</div>
                                          <div className="font-bold text-emerald-700">£{(avgDeal/1000).toFixed(0)}k</div>
                                        </div>
                                      </div>
                                    </div>
                                  </HoverCardContent>
                                </HoverCard>
                              </td>
                              <td className="text-right py-2 px-2">{apps}</td>
                              <td className={`text-right py-2 px-2 ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {trend >= 0 ? '+' : ''}{trend}
                              </td>
                              <td className="text-right py-2 px-2 text-emerald-600">{offers}</td>
                              <td className="text-right py-2 px-2">
                                <Badge variant={approvalRate >= 25 ? "default" : approvalRate >= 15 ? "secondary" : "destructive"} className="text-xs">
                                  {approvalRate}%
                                </Badge>
                              </td>
                              <td className="text-right py-2 px-2 text-blue-600">{funded}</td>
                              <td className="text-right py-2 px-2">
                                <Badge variant={convRate >= 40 ? "default" : convRate >= 20 ? "secondary" : "destructive"} className="text-xs">
                                  {convRate}%
                                </Badge>
                              </td>
                              <td className="text-right py-2 px-2 text-slate-600">£{(avgDeal/1000).toFixed(0)}k</td>
                              <td className="text-right py-2 px-2 font-medium text-emerald-700">£{(value/1000).toFixed(0)}k</td>
                            </tr>
                            
                            {/* Expanded Row */}
                            {isExpanded && (
                              <tr className="bg-slate-100">
                                <td colSpan={10} className="p-0">
                                  <div className="bg-white m-2 rounded-lg border border-slate-200 p-4 shadow-sm">
                                    {/* Header */}
                                    <div className="flex items-center justify-between mb-4 pb-3 border-b">
                                      <div>
                                        <h4 className="font-semibold text-lg">{broker.name}</h4>
                                        <p className="text-sm text-slate-500">6-Month Performance History</p>
                                      </div>
                                      <div className="flex items-center gap-4">
                                        <div className="text-center">
                                          <div className="text-2xl font-bold text-blue-600">{apps}</div>
                                          <div className="text-xs text-slate-500">Total Apps</div>
                                        </div>
                                        <div className="text-center">
                                          <div className="text-2xl font-bold text-green-600">{funded}</div>
                                          <div className="text-xs text-slate-500">Total Funded</div>
                                        </div>
                                        <div className="text-center">
                                          <div className="text-2xl font-bold text-purple-600">£{(value / 1000).toFixed(0)}k</div>
                                          <div className="text-xs text-slate-500">Total Value</div>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Monthly Table */}
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-sm">
                                        <thead className="bg-slate-50">
                                          <tr>
                                            <th className="text-left py-2 px-3 font-medium">Month</th>
                                            <th className="text-center py-2 px-3 font-medium">NB Apps</th>
                                            <th className="text-center py-2 px-3 font-medium">NB Offers</th>
                                            <th className="text-center py-2 px-3 font-medium bg-green-50">Offer Rate</th>
                                            <th className="text-center py-2 px-3 font-medium">NB Funded</th>
                                            <th className="text-center py-2 px-3 font-medium bg-purple-50">Conv. Rate</th>
                                            <th className="text-right py-2 px-3 font-medium">NB Value</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {monthlyHistory.map((month, mIdx) => {
                                            const mApps = month.nb_apps || 0;
                                            const mOffers = month.nb_offers || 0;
                                            const mFunded = month.nb_funded || 0;
                                            const mValue = month.nb_value || 0;
                                            const offerRate = mApps > 0 ? ((mOffers / mApps) * 100).toFixed(0) : 0;
                                            const mConvRate = mOffers > 0 ? ((mFunded / mOffers) * 100).toFixed(0) : 0;
                                            return (
                                              <tr key={mIdx} className={mIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                                <td className="py-2 px-3 font-medium">{month.month}</td>
                                                <td className="text-center py-2 px-3">{mApps}</td>
                                                <td className="text-center py-2 px-3">{mOffers}</td>
                                                <td className="text-center py-2 px-3 bg-green-50">
                                                  <span className={`font-medium ${offerRate >= 20 ? 'text-green-600' : offerRate >= 10 ? 'text-amber-600' : 'text-red-600'}`}>
                                                    {offerRate}%
                                                  </span>
                                                </td>
                                                <td className="text-center py-2 px-3">{mFunded}</td>
                                                <td className="text-center py-2 px-3 bg-purple-50">
                                                  <span className={`font-medium ${mConvRate >= 40 ? 'text-purple-600' : mConvRate >= 20 ? 'text-amber-600' : 'text-red-600'}`}>
                                                    {mConvRate}%
                                                  </span>
                                                </td>
                                                <td className="text-right py-2 px-3 font-medium text-emerald-700">
                                                  £{(mValue / 1000).toFixed(0)}k
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                        <tfoot className="bg-blue-50 border-t-2 border-blue-200">
                                          <tr>
                                            <td className="py-2 px-3 font-semibold">Average</td>
                                            <td className="text-center py-2 px-3 font-semibold">{avgApps}</td>
                                            <td className="text-center py-2 px-3 font-semibold">
                                              {Math.round(monthlyHistory.reduce((s, m) => s + (m.nb_offers || 0), 0) / (monthlyHistory.length || 1))}
                                            </td>
                                            <td className="text-center py-2 px-3 font-semibold bg-green-100 text-green-700">
                                              {approvalRate}%
                                            </td>
                                            <td className="text-center py-2 px-3 font-semibold">
                                              {Math.round(monthlyHistory.reduce((s, m) => s + (m.nb_funded || 0), 0) / (monthlyHistory.length || 1))}
                                            </td>
                                            <td className="text-center py-2 px-3 font-semibold bg-purple-100 text-purple-700">
                                              {convRate}%
                                            </td>
                                            <td className="text-right py-2 px-3 font-semibold text-emerald-700">
                                              £{(monthlyHistory.reduce((s, m) => s + (m.nb_value || 0), 0) / (monthlyHistory.length || 1) / 1000).toFixed(0)}k
                                            </td>
                                          </tr>
                                        </tfoot>
                                      </table>
                                    </div>
                                    
                                    {/* Mini Charts - using pre-computed data like Planning section */}
                                    <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
                                      {/* Apps per Month Bar Chart */}
                                      <div className="bg-blue-50 rounded-lg p-3">
                                        <div className="text-xs font-medium text-blue-700 mb-2">Apps per Month</div>
                                        <div className="h-16 flex items-end gap-1">
                                          {appsData.map((d, i) => (
                                            <div key={i} className="flex-1 flex flex-col items-center">
                                              <div 
                                                className="w-full bg-blue-400 rounded-t hover:bg-blue-500 transition-colors"
                                                style={{ height: `${Math.max((d.value / maxApps) * 100, 8)}%` }}
                                                title={`${d.value} apps`}
                                              />
                                              <span className="text-[10px] mt-1 text-blue-600">{d.value}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                      
                                      {/* Offer Rate Trend Line Chart */}
                                      <div className="bg-green-50 rounded-lg p-3">
                                        <div className="text-xs font-medium text-green-700 mb-2">Offer Rate Trend</div>
                                        <div className="h-16 relative">
                                          {approvalHistory.length > 0 && (
                                            <svg className="w-full h-full" viewBox={`0 0 ${Math.max(approvalHistory.length - 1, 1) * 30} 70`} preserveAspectRatio="none">
                                              <polyline
                                                fill="none"
                                                stroke="#22c55e"
                                                strokeWidth="3"
                                                points={approvalHistory.map((d, i) => `${i * 30},${50 - (d.value / 50) * 50}`).join(' ')}
                                              />
                                              {approvalHistory.map((d, i) => (
                                                <g key={i}>
                                                  <circle cx={i * 30} cy={50 - (d.value / 50) * 50} r="4" fill="#22c55e" />
                                                  <text x={i * 30} y={65} textAnchor="middle" fontSize="8" fill="#16a34a">{d.value}%</text>
                                                </g>
                                              ))}
                                            </svg>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Conversion Rate Trend Line Chart */}
                                      <div className="bg-purple-50 rounded-lg p-3">
                                        <div className="text-xs font-medium text-purple-700 mb-2">Conversion Rate Trend</div>
                                        <div className="h-16 relative">
                                          {conversionHistory.length > 0 && (
                                            <svg className="w-full h-full" viewBox={`0 0 ${Math.max(conversionHistory.length - 1, 1) * 30} 70`} preserveAspectRatio="none">
                                              <polyline
                                                fill="none"
                                                stroke="#a855f7"
                                                strokeWidth="3"
                                                points={conversionHistory.map((d, i) => `${i * 30},${50 - (d.value / 100) * 50}`).join(' ')}
                                              />
                                              {conversionHistory.map((d, i) => (
                                                <g key={i}>
                                                  <circle cx={i * 30} cy={50 - (d.value / 100) * 50} r="4" fill="#a855f7" />
                                                  <text x={i * 30} y={65} textAnchor="middle" fontSize="8" fill="#9333ea">{d.value}%</text>
                                                </g>
                                              ))}
                                            </svg>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* New vs Top-ups Tab */}
        <TabsContent value="topups" className="space-y-4">
          <div className="flex justify-end -mb-2"><ExportXlsxButton sheets="topups" fy={performanceFY} label="Export this section" size="sm" /></div>
          {/* Period Selector */}
          <div className="flex items-center justify-end gap-2 mb-2">
            <span className="text-sm text-muted-foreground">View:</span>
            <div className="flex border rounded-lg overflow-hidden">
              {periodOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setTopupsPeriod(opt.value)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    topupsPeriod === opt.value 
                      ? 'bg-emerald-600 text-white' 
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Info banner about data methodology */}
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2 text-green-800 text-sm">
                <CheckCircle className="w-4 h-4" />
                <span>Top-ups identified by "(TU)" in name OR 6% commission rate. Historical data available from Jul 2025.</span>
              </div>
            </CardContent>
          </Card>

          {/* Filter data from Jul 2025 onwards (when commission data starts) */}
          {(() => {
            const topupData = performanceMonthlyTrends.filter(m => m.month_key >= "2025-07");
            const totalFundedFiltered = topupData.reduce((sum, m) => sum + m.funded, 0);
            const totalTopupsFiltered = topupData.reduce((sum, m) => sum + m.topups, 0);
            const totalNewDealsFiltered = totalFundedFiltered - totalTopupsFiltered;
            const topupRateFiltered = totalFundedFiltered > 0 ? Math.round(totalTopupsFiltered / totalFundedFiltered * 100) : 0;
            
            // VALUE-based calculations (the key metric)
            const totalFundedValue = topupData.reduce((sum, m) => sum + (m.funded_value || 0), 0);
            const totalTopupValue = topupData.reduce((sum, m) => sum + (m.topup_value || 0), 0);
            const totalNewDealValue = totalFundedValue - totalTopupValue;
            const topupValueRate = totalFundedValue > 0 ? Math.round(totalTopupValue / totalFundedValue * 100) : 0;

            return (
              <>
                {/* VALUE-based KPIs - The Key Metrics */}
                <Card className="border-purple-200 bg-purple-50/50 mb-4">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                      Top-up Share by VALUE - {performanceFYRange.label}
                    </CardTitle>
                    <CardDescription>
                      What percentage of funded POUNDS comes from top-ups vs new business
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-3xl font-bold text-purple-700">{topupValueRate}%</p>
                        <p className="text-sm text-muted-foreground">Top-up % (by £)</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-2xl font-bold text-purple-600">£{(totalTopupValue / 1000000).toFixed(2)}M</p>
                        <p className="text-sm text-muted-foreground">Top-up Value</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-2xl font-bold text-blue-600">£{(totalNewDealValue / 1000000).toFixed(2)}M</p>
                        <p className="text-sm text-muted-foreground">New Business Value</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-2xl font-bold text-slate-700">£{(totalFundedValue / 1000000).toFixed(2)}M</p>
                        <p className="text-sm text-muted-foreground">Total Funded</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Count-based KPIs (secondary) */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-bold text-blue-600">{totalNewDealsFiltered}</p>
                      <p className="text-sm text-muted-foreground mt-1">New Deals</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-bold text-purple-600">{totalTopupsFiltered}</p>
                      <p className="text-sm text-muted-foreground mt-1">Top-ups</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-bold text-slate-600">{totalFundedFiltered}</p>
                      <p className="text-sm text-muted-foreground mt-1">Total Deals</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-bold text-amber-600">{topupRateFiltered}%</p>
                      <p className="text-sm text-muted-foreground mt-1">Top-up % (by count)</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* VALUE Chart - The Key Visual */}
                  <Card className="border-purple-200">
                    <CardHeader>
                      <CardTitle className="text-purple-700">Funded Value: NB vs Top-ups</CardTitle>
                      <CardDescription>The real picture - breakdown by £ value</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={topupData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} />
                          <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
                          <Legend />
                          <Bar dataKey="new_deal_value" stackId="a" fill="#3b82f6" name="New Business £" radius={[0, 0, 0, 0]} />
                          <Bar dataKey="topup_value" stackId="a" fill="#8b5cf6" name="Top-up £" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Count Chart - Secondary */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Deal Count: NB vs Top-ups</CardTitle>
                      <CardDescription>Number of deals by type</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={topupData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="new_deals" stackId="a" fill="#93c5fd" name="New Deals" radius={[0, 0, 0, 0]} />
                          <Bar dataKey="topups" stackId="a" fill="#c4b5fd" name="Top-ups" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                {/* Monthly Top-up Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle>Monthly Top-up Analysis</CardTitle>
                    <CardDescription>Count vs VALUE breakdown - Value % is the key metric</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-2">Month</th>
                            <th className="text-right py-3 px-2">Deals</th>
                            <th className="text-right py-3 px-2">Top-ups</th>
                            <th className="text-right py-3 px-2">TU % (Count)</th>
                            <th className="text-right py-3 px-2">NB Value</th>
                            <th className="text-right py-3 px-2">TU Value</th>
                            <th className="text-right py-3 px-2 bg-purple-50">TU % (Value)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...topupData].reverse().map((m, idx) => {
                            const tuValuePct = m.funded_value > 0 
                              ? Math.round((m.topup_value || 0) / m.funded_value * 100) 
                              : 0;
                            return (
                              <tr key={m.month_key} className={idx % 2 === 0 ? 'bg-slate-50' : ''}>
                                <td className="py-2 px-2 font-medium">{m.month_full}</td>
                                <td className="text-right py-2 px-2">{m.funded}</td>
                                <td className="text-right py-2 px-2 text-purple-600">{m.topups}</td>
                                <td className="text-right py-2 px-2 text-muted-foreground">
                                  {m.topup_percentage}%
                                </td>
                                <td className="text-right py-2 px-2 text-blue-600">£{((m.new_deal_value || 0) / 1000).toFixed(0)}k</td>
                                <td className="text-right py-2 px-2 text-purple-600">£{((m.topup_value || 0) / 1000).toFixed(0)}k</td>
                                <td className="text-right py-2 px-2 bg-purple-50">
                                  <Badge className={tuValuePct >= 60 ? 'bg-purple-600' : tuValuePct >= 40 ? 'bg-purple-400' : 'bg-slate-400'}>
                                    {m.topup_value_percentage || tuValuePct}%
                                  </Badge>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            );
          })()}
        </TabsContent>

        {/* Daily Activity Tab */}
        <TabsContent value="daily" className="space-y-6">
          {/* Top KPIs for Daily */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-blue-700">
                  {current_month.direct_apps || 0}
                </p>
                <p className="text-sm text-blue-600 mt-1">Direct Apps This Month</p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-emerald-700">
                  {current_month.direct_offers || 0}
                </p>
                <p className="text-sm text-emerald-600 mt-1">Direct Offers This Month</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-purple-700">
                  {current_month.offers}
                </p>
                <p className="text-sm text-purple-600 mt-1">Total Offers This Month</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-amber-700">
                  {(daily_activity.topups_per_day || []).reduce((sum, d) => sum + (d.topups || 0), 0)}
                </p>
                <p className="text-sm text-amber-600 mt-1">Top-ups This Month</p>
              </CardContent>
            </Card>
          </div>

          {/* Sub-tabs for Day/Week/Month views */}
          <Tabs defaultValue="day" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="day">Daily View</TabsTrigger>
              <TabsTrigger value="week">Weekly View</TabsTrigger>
              <TabsTrigger value="pattern">Day of Month Pattern</TabsTrigger>
            </TabsList>

            {/* Daily View */}
            <TabsContent value="day" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Apps per Day */}
                <Card>
                  <CardHeader>
                    <CardTitle>Apps per Day (This Month)</CardTitle>
                    <CardDescription>Daily application volume</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={daily_activity.apps_per_day || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" fontSize={10} angle={-45} textAnchor="end" height={60} />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="applications" fill="#3b82f6" name="Applications" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Top-ups vs New Business per Day */}
                <Card>
                  <CardHeader>
                    <CardTitle>Top-ups vs New Business (Daily)</CardTitle>
                    <CardDescription>Track when top-ups are being added</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={daily_activity.topups_per_day || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" fontSize={10} angle={-45} textAnchor="end" height={60} />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="new_business" stackId="a" fill="#3b82f6" name="New Business" />
                        <Bar dataKey="topups" stackId="a" fill="#f59e0b" name="Top-ups" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Offers per Day */}
                <Card>
                  <CardHeader>
                    <CardTitle>Offers per Day (By App Date)</CardTitle>
                    <CardDescription>How many apps from each day received an offer</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={daily_activity.offers_per_day || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" fontSize={10} angle={-45} textAnchor="end" height={60} />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="offers" fill="#10b981" name="Offers" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Deals Funded per Day - This Month */}
                <Card>
                  <CardHeader>
                    <CardTitle>Deals Funded per Day (This Month)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={daily_activity.funded_per_day || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" fontSize={10} angle={-45} textAnchor="end" height={60} />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="funded" fill="#6366f1" name="Funded" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Weekly View */}
            <TabsContent value="week" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Weekly Applications (YTD)</CardTitle>
                  <CardDescription>Top-ups vs New Business by week</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={daily_activity.weekly_apps || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" fontSize={10} />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="new_business" stackId="a" fill="#3b82f6" name="New Business" />
                      <Bar dataKey="topups" stackId="a" fill="#f59e0b" name="Top-ups" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Day of Month Pattern */}
            <TabsContent value="pattern" className="space-y-6">
              <Card className="border-amber-200 bg-amber-50/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    Top-up Timing Analysis
                  </CardTitle>
                  <CardDescription>
                    Check if top-ups are being added at month end to hit targets. 
                    High concentration in days 25-31 may indicate gaming.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart data={daily_activity.topup_by_day_of_month || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" label={{ value: 'Day of Month', position: 'bottom', offset: 0 }} />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="new_business" fill="#3b82f6" name="New Business" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="topups" fill="#f59e0b" name="Top-ups" radius={[4, 4, 0, 0]} />
                      <Line type="monotone" dataKey="topups" stroke="#ef4444" strokeWidth={2} dot={false} name="Top-up Trend" />
                    </ComposedChart>
                  </ResponsiveContainer>
                  
                  {/* Analysis Summary */}
                  {(() => {
                    const dayData = daily_activity.topup_by_day_of_month || [];
                    const earlyMonth = dayData.slice(0, 15).reduce((sum, d) => sum + (d.topups || 0), 0);
                    const lateMonth = dayData.slice(15, 31).reduce((sum, d) => sum + (d.topups || 0), 0);
                    const lastWeek = dayData.slice(24, 31).reduce((sum, d) => sum + (d.topups || 0), 0);
                    const total = earlyMonth + lateMonth;
                    const lateMonthPct = total > 0 ? Math.round(lateMonth / total * 100) : 0;
                    const lastWeekPct = total > 0 ? Math.round(lastWeek / total * 100) : 0;
                    
                    return (
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 bg-white rounded-lg border">
                          <p className="text-2xl font-bold text-blue-600">{earlyMonth}</p>
                          <p className="text-sm text-muted-foreground">Top-ups (Days 1-15)</p>
                        </div>
                        <div className="p-4 bg-white rounded-lg border">
                          <p className="text-2xl font-bold text-amber-600">{lateMonth}</p>
                          <p className="text-sm text-muted-foreground">Top-ups (Days 16-31)</p>
                          <p className="text-xs text-amber-600">{lateMonthPct}% of total</p>
                        </div>
                        <div className={`p-4 rounded-lg border ${lastWeekPct > 40 ? 'bg-red-50 border-red-200' : 'bg-white'}`}>
                          <p className={`text-2xl font-bold ${lastWeekPct > 40 ? 'text-red-600' : 'text-slate-600'}`}>{lastWeek}</p>
                          <p className="text-sm text-muted-foreground">Top-ups (Last Week: 25-31)</p>
                          <p className={`text-xs ${lastWeekPct > 40 ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                            {lastWeekPct}% of total {lastWeekPct > 40 ? '⚠️ High concentration' : ''}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Brokers This Month */}
          <Card>
            <CardHeader>
              <CardTitle>Apps by Broker This Month</CardTitle>
              <CardDescription>Top 15 brokers by application volume</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={(broker_this_month || []).slice(0, 15)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} fontSize={11} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="applications" fill="#3b82f6" name="Applications" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Deal Velocity Tab */}
        <TabsContent value="velocity" className="space-y-6">
          <div className="flex justify-end -mb-2"><ExportXlsxButton sheets="deals" fy={performanceFY} label="Export this section" size="sm" /></div>
          {/* Period Selector */}
          <div className="flex items-center justify-end gap-2 mb-2">
            <span className="text-sm text-muted-foreground">View:</span>
            <div className="flex border rounded-lg overflow-hidden">
              {periodOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setVelocityPeriod(opt.value)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    velocityPeriod === opt.value 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-blue-700">
                  {deal_velocity.overall?.app_to_offer?.avg || '—'}
                </p>
                <p className="text-sm text-blue-600 mt-1">Avg Days: App → Offer</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-amber-700">
                  {deal_velocity.overall?.offer_to_funded?.avg || '—'}
                </p>
                <p className="text-sm text-amber-600 mt-1">Avg Days: Offer → Funded</p>
                <p className="text-xs text-amber-500">(Broker selling time)</p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-emerald-700">
                  {deal_velocity.overall?.app_to_funded?.avg || '—'}
                </p>
                <p className="text-sm text-emerald-600 mt-1">Avg Days: Total Cycle</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-purple-700">
                  {deal_velocity.overall?.app_to_funded?.count || 0}
                </p>
                <p className="text-sm text-purple-600 mt-1">Deals with Velocity Data</p>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Velocity Trend */}
          <Card>
            <CardHeader>
              <CardTitle>Monthly Deal Velocity Trend</CardTitle>
              <CardDescription>Average days at each stage over the last 6 months</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={deal_velocity.monthly_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis label={{ value: 'Days', angle: -90, position: 'insideLeft' }} />
                  <Tooltip formatter={(v) => v ? `${v} days` : 'N/A'} />
                  <Legend />
                  <Bar 
                    dataKey={(d) => d.app_to_offer?.avg} 
                    fill="#3b82f6" 
                    name="App → Offer" 
                    radius={[4, 4, 0, 0]} 
                  />
                  <Bar 
                    dataKey={(d) => d.offer_to_funded?.avg} 
                    fill="#f59e0b" 
                    name="Offer → Funded (Broker)" 
                    radius={[4, 4, 0, 0]} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey={(d) => d.app_to_funded?.avg} 
                    stroke="#10b981" 
                    strokeWidth={3} 
                    name="Total Cycle" 
                    dot={{ fill: '#10b981', r: 5 }} 
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Broker Velocity Table */}
          <Card>
            <CardHeader>
              <CardTitle>Broker Deal Velocity Rankings</CardTitle>
              <CardDescription>
                How fast brokers close deals after receiving an offer. 
                Sorted by "Offer → Funded" time (fastest first).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">#</th>
                      <th className="text-left py-3 px-2">Broker</th>
                      <th className="text-right py-3 px-2">Deals</th>
                      <th className="text-right py-3 px-2">App → Offer</th>
                      <th className="text-right py-3 px-2 bg-amber-50">Offer → Funded</th>
                      <th className="text-right py-3 px-2">Total Cycle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(deal_velocity.by_broker || []).map((broker, idx) => (
                      <tr key={broker.name} className={idx % 2 === 0 ? 'bg-slate-50' : ''}>
                        <td className="py-2 px-2 font-bold text-muted-foreground">{idx + 1}</td>
                        <td className="py-2 px-2 font-medium">{broker.name}</td>
                        <td className="text-right py-2 px-2">{broker.deals_count}</td>
                        <td className="text-right py-2 px-2">
                          {broker.app_to_offer?.avg ? (
                            <span className="text-blue-600">{broker.app_to_offer.avg} days</span>
                          ) : '—'}
                        </td>
                        <td className="text-right py-2 px-2 bg-amber-50 font-medium">
                          {broker.offer_to_funded?.avg ? (
                            <Badge variant={broker.offer_to_funded.avg <= 7 ? "default" : broker.offer_to_funded.avg <= 14 ? "secondary" : "destructive"}>
                              {broker.offer_to_funded.avg} days
                            </Badge>
                          ) : '—'}
                        </td>
                        <td className="text-right py-2 px-2">
                          {broker.app_to_funded?.avg ? (
                            <span className="text-emerald-600">{broker.app_to_funded.avg} days</span>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Legend */}
              <div className="mt-4 pt-4 border-t flex gap-4 text-xs text-muted-foreground">
                <span><Badge variant="default" className="mr-1">≤7 days</Badge> Fast closer</span>
                <span><Badge variant="secondary" className="mr-1">8-14 days</Badge> Average</span>
                <span><Badge variant="destructive" className="mr-1">&gt;14 days</Badge> Slow closer</span>
              </div>
            </CardContent>
          </Card>

          {/* Insights Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-blue-800">Understanding Deal Velocity</CardTitle>
            </CardHeader>
            <CardContent className="text-blue-700 text-sm space-y-2">
              <p><strong>App → Offer:</strong> Time for underwriting/credit decision. Faster = more efficient internal process.</p>
              <p><strong>Offer → Funded:</strong> Time for broker to present offer and close the deal. This is the broker's "selling speed".</p>
              <p><strong>Total Cycle:</strong> End-to-end time from application to money out the door.</p>
              <p className="text-xs text-blue-600 mt-2">
                Note: Offer dates are extracted from the offer file upload date in Monday.com.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Underwriters Tab */}
        <TabsContent value="underwriters" className="space-y-6">
          <div className="flex justify-end -mb-2"><ExportXlsxButton sheets="underwriters" fy={performanceFY} label="Export this section" size="sm" /></div>
          {/* View Toggle */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Underwriter Performance</CardTitle>
                  <CardDescription>Account managers by workload and offers</CardDescription>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant={underwriterView === "allTime" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setUnderwriterView("allTime")}
                  >
                    All Time
                  </Button>
                  <Button
                    variant={underwriterView === "thisMonth" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setUnderwriterView("thisMonth")}
                  >
                    This Month
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Apps Worked per Underwriter */}
            <Card>
              <CardHeader>
                <CardTitle>Apps Worked per Underwriter</CardTitle>
                <CardDescription>{underwriterView === "thisMonth" ? "This month only" : "All time"}</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart 
                    data={(underwriterView === "thisMonth" ? underwriter_this_month : underwriter_stats).slice(0, 15)} 
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={120} fontSize={11} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar 
                      dataKey={underwriterView === "thisMonth" ? "this_month_apps" : "apps_worked"} 
                      fill="#3b82f6" 
                      name="Apps Worked" 
                      radius={[0, 4, 4, 0]} 
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Offers per Underwriter */}
            <Card>
              <CardHeader>
                <CardTitle>Offers per Underwriter</CardTitle>
                <CardDescription>{underwriterView === "thisMonth" ? "This month only" : "All time"}</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart 
                    data={(underwriterView === "thisMonth" 
                      ? underwriter_this_month.filter(u => u.this_month_offers > 0)
                      : underwriter_stats.filter(u => u.offers > 0)
                    ).slice(0, 15)} 
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={120} fontSize={11} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar 
                      dataKey={underwriterView === "thisMonth" ? "this_month_offers" : "offers"} 
                      fill="#10b981" 
                      name="Offers" 
                      radius={[0, 4, 4, 0]} 
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Value of Offers per Underwriter */}
          <Card>
            <CardHeader>
              <CardTitle>Value of Offers per Underwriter</CardTitle>
              <CardDescription>{underwriterView === "thisMonth" ? "This month only" : "All time"}</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart 
                  data={(underwriterView === "thisMonth"
                    ? underwriter_this_month.filter(u => u.this_month_offer_value > 0)
                    : underwriter_stats.filter(u => u.offer_value > 0)
                  ).slice(0, 15)} 
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} />
                  <YAxis dataKey="name" type="category" width={120} fontSize={11} />
                  <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
                  <Bar 
                    dataKey={underwriterView === "thisMonth" ? "this_month_offer_value" : "offer_value"} 
                    fill="#f59e0b" 
                    name="Offer Value" 
                    radius={[0, 4, 4, 0]} 
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Underwriter Table */}
          <Card>
            <CardHeader>
              <CardTitle>Underwriter Performance Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Underwriter</th>
                      <th className="text-right py-3 px-2">Apps Worked</th>
                      <th className="text-right py-3 px-2">Offers</th>
                      <th className="text-right py-3 px-2">Offer Value</th>
                      <th className="text-right py-3 px-2">Funded</th>
                      <th className="text-right py-3 px-2">Offer Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(underwriterView === "thisMonth" ? underwriter_this_month : underwriter_stats).slice(0, 20).map((uw, idx) => {
                      const apps = underwriterView === "thisMonth" ? uw.this_month_apps : uw.apps_worked;
                      const offers = underwriterView === "thisMonth" ? uw.this_month_offers : uw.offers;
                      const offerValue = underwriterView === "thisMonth" ? uw.this_month_offer_value : uw.offer_value;
                      
                      return (
                        <tr key={uw.name} className={idx % 2 === 0 ? 'bg-slate-50' : ''}>
                          <td className="py-2 px-2 font-medium">{uw.name}</td>
                          <td className="text-right py-2 px-2">{apps}</td>
                          <td className="text-right py-2 px-2 text-emerald-600">{offers}</td>
                          <td className="text-right py-2 px-2">£{(offerValue || 0).toLocaleString()}</td>
                          <td className="text-right py-2 px-2 text-blue-600">{uw.funded}</td>
                          <td className="text-right py-2 px-2">
                            <Badge variant="outline">
                              {apps > 0 ? ((offers / apps) * 100).toFixed(1) : 0}%
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Decline Reasons Tab */}
        <TabsContent value="declines" className="space-y-6">
          <div className="flex justify-end -mb-2"><ExportXlsxButton sheets="declines" fy={performanceFY} label="Export this section" size="sm" /></div>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Decline Reasons</CardTitle>
                  <CardDescription>Breakdown of why applications were declined</CardDescription>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant={declineView === "thisMonth" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setDeclineView("thisMonth")}
                  >
                    This Month
                  </Button>
                  <Button
                    variant={declineView === "allTime" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setDeclineView("allTime")}
                  >
                    All Time
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {decline_reasons.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={decline_reasons.slice(0, 15)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="reason" fontSize={10} angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar 
                      dataKey={declineView === "thisMonth" ? "count" : "total_count"} 
                      fill="#ef4444" 
                      name={declineView === "thisMonth" ? "Count This Month" : "All Time Count"} 
                      radius={[4, 4, 0, 0]} 
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <p>No decline reason data available yet.</p>
                  <p className="text-sm mt-2">Make sure the "Reason for decline" column is populated in Monday.com</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Decline Reasons Table */}
          {decline_reasons.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Decline Reasons Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Reason</th>
                        <th className="text-right py-3 px-2">This Month</th>
                        <th className="text-right py-3 px-2">All Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {decline_reasons.map((dr, idx) => (
                        <tr key={dr.reason} className={idx % 2 === 0 ? 'bg-slate-50' : ''}>
                          <td className="py-2 px-2 font-medium">{dr.reason}</td>
                          <td className="text-right py-2 px-2 text-red-600 font-medium">{dr.count}</td>
                          <td className="text-right py-2 px-2 text-muted-foreground">{dr.total_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Offers Tab */}
        <TabsContent value="offers" className="space-y-6">
          <div className="flex justify-end -mb-2"><ExportXlsxButton sheets="offers" fy={performanceFY} label="Export this section" size="sm" /></div>
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-amber-700">
                  {current_month.offers}
                </p>
                <p className="text-sm text-amber-600 mt-1">Offers This Month</p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-emerald-700">
                  £{((current_month.offer_value || 0) / 1000).toFixed(0)}k
                </p>
                <p className="text-sm text-emerald-600 mt-1">Offer Value This Month</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-blue-700">
                  {summary.total_offers?.toLocaleString()}
                </p>
                <p className="text-sm text-blue-600 mt-1">Total Offers (12 Months)</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-purple-700">
                  £{((summary.total_offer_value || 0) / 1000000).toFixed(1)}M
                </p>
                <p className="text-sm text-purple-600 mt-1">Total Offer Value (12 Months)</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Offers per Month (Count) */}
            <Card>
              <CardHeader>
                <CardTitle>Offers per Month</CardTitle>
                <CardDescription>Number of offers made vs applications received</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={offer_analytics.monthly_trends || monthly_trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="apps" fill="#94a3b8" name="Applications" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="left" dataKey="offers" fill="#f59e0b" name="Offers" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="offer_rate" stroke="#10b981" strokeWidth={2} name="Offer Rate %" dot={{ fill: '#10b981' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Offer Value per Month */}
            <Card>
              <CardHeader>
                <CardTitle>Offer Value per Month</CardTitle>
                <CardDescription>Total value of offers made each month</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={offer_analytics.monthly_trends || monthly_trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
                    <Legend />
                    <Bar dataKey="offer_value" fill="#8b5cf6" name="Offer Value" radius={[4, 4, 0, 0]} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Offer Size Buckets - With Toggle */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Offer Size Distribution</CardTitle>
                  <CardDescription>Breakdown by offer amount: Up to £10k, £10k-£25k, £25k-£50k, £50k-£100k, £100k+</CardDescription>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant={offerBucketView === "thisMonth" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setOfferBucketView("thisMonth")}
                  >
                    This Month
                  </Button>
                  <Button
                    variant={offerBucketView === "allTime" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setOfferBucketView("allTime")}
                  >
                    All Time
                  </Button>
                  <Button
                    variant={offerBucketView === "monthly" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setOfferBucketView("monthly")}
                  >
                    Monthly
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {offerBucketView === "monthly" ? (
                /* Monthly Comparison View */
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={offer_analytics.monthly_bucket_breakdown || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="count_Up_to_10k" stackId="a" fill="#94a3b8" name="Up to £10k" />
                    <Bar dataKey="count_10k_25k" stackId="a" fill="#3b82f6" name="£10k-£25k" />
                    <Bar dataKey="count_25k_50k" stackId="a" fill="#8b5cf6" name="£25k-£50k" />
                    <Bar dataKey="count_50k_100k" stackId="a" fill="#f59e0b" name="£50k-£100k" />
                    <Bar dataKey="count_100kplus" stackId="a" fill="#10b981" name="£100k+" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                /* This Month or All Time View */
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Count Chart */}
                  <div>
                    <h4 className="text-sm font-medium mb-4 text-center">By Count</h4>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={offerBucketView === "thisMonth" 
                        ? (offer_analytics.current_month_buckets || [])
                        : (offer_analytics.all_time_buckets || [])
                      }>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="bucket" fontSize={10} angle={-20} textAnchor="end" height={60} />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" fill="#3b82f6" name="Count" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Value Chart */}
                  <div>
                    <h4 className="text-sm font-medium mb-4 text-center">By Value</h4>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={offerBucketView === "thisMonth" 
                        ? (offer_analytics.current_month_buckets || [])
                        : (offer_analytics.all_time_buckets || [])
                      }>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="bucket" fontSize={10} angle={-20} textAnchor="end" height={60} />
                        <YAxis tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
                        <Bar dataKey="value" fill="#10b981" name="Value" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Best Brokers for Big Deals */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Top Brokers by Total Offer Value</CardTitle>
                <CardDescription>Who brings in the most offer value overall</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={(offer_analytics.top_brokers_by_value || []).slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} />
                    <YAxis dataKey="name" type="category" width={100} fontSize={10} />
                    <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
                    <Bar dataKey="total_value" fill="#8b5cf6" name="Total Value" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top Brokers for Big Deals (£50k+)</CardTitle>
                <CardDescription>Who sends us the most high-value applications</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={(offer_analytics.top_brokers_by_big_deals || []).slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} fontSize={10} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="big_offers" fill="#f59e0b" name="£50k+ Offers" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Broker Offer Performance Table */}
          <Card>
            <CardHeader>
              <CardTitle>Broker Offer Performance Summary</CardTitle>
              <CardDescription>Complete breakdown by offer size bucket</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Broker</th>
                      <th className="text-right py-3 px-2">Total Offers</th>
                      <th className="text-right py-3 px-2">Total Value</th>
                      <th className="text-right py-3 px-2">Avg Offer</th>
                      <th className="text-right py-3 px-2 text-xs">Up to £10k</th>
                      <th className="text-right py-3 px-2 text-xs">£10k-£25k</th>
                      <th className="text-right py-3 px-2 text-xs">£25k-£50k</th>
                      <th className="text-right py-3 px-2 text-xs">£50k-£100k</th>
                      <th className="text-right py-3 px-2 text-xs">£100k+</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(offer_analytics.top_brokers_by_value || []).slice(0, 15).map((broker, idx) => (
                      <tr key={broker.name} className={idx % 2 === 0 ? 'bg-slate-50' : ''}>
                        <td className="py-2 px-2 font-medium">{broker.name}</td>
                        <td className="text-right py-2 px-2">{broker.total_offers}</td>
                        <td className="text-right py-2 px-2 text-emerald-600 font-medium">£{(broker.total_value || 0).toLocaleString()}</td>
                        <td className="text-right py-2 px-2">£{(broker.avg_offer || 0).toLocaleString()}</td>
                        <td className="text-right py-2 px-2 text-xs">{broker.bucket_up_to_10k || 0}</td>
                        <td className="text-right py-2 px-2 text-xs">{broker.bucket_10k_25k || 0}</td>
                        <td className="text-right py-2 px-2 text-xs">{broker.bucket_25k_50k || 0}</td>
                        <td className="text-right py-2 px-2 text-xs text-amber-600">{broker.bucket_50k_100k || 0}</td>
                        <td className="text-right py-2 px-2 text-xs text-purple-600 font-medium">{broker.bucket_100k_plus || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Highest Average Offer Size */}
          <Card>
            <CardHeader>
              <CardTitle>Brokers with Highest Average Offer Size</CardTitle>
              <CardDescription>Who sends the biggest deals on average (min 5 offers)</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={(offer_analytics.top_brokers_by_avg_offer || []).slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={10} angle={-20} textAnchor="end" height={80} />
                  <YAxis tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
                  <Bar dataKey="avg_offer" fill="#ec4899" name="Avg Offer Size" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          
          {/* ==================== OFFER PATTERNS SECTION ==================== */}
          <Separator className="my-6" />
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-1 h-6 bg-blue-500 rounded"></div>
              <div>
                <h3 className="text-lg font-semibold">Offer Patterns Analysis</h3>
                <p className="text-sm text-muted-foreground">Analyze daily, weekly, and monthly offer rate patterns</p>
              </div>
            </div>
          
            {/* Section 1: Offer Rate by Application Date (Monday.com data) */}
            <div className="space-y-4 p-4 border rounded-lg bg-blue-50/30">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-blue-600" />
                <h4 className="font-medium text-blue-800">Offer Rate by Application Date</h4>
                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded">Monday.com</span>
              </div>
              
              {/* Context Note */}
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-blue-800">Conversion analysis: </span>
                  <span className="text-blue-700">
                    This shows offer conversion rates based on when applications were submitted. 
                    Useful for analyzing broker quality and seasonal patterns. 
                    Note: Conversion can appear lower in recent months as deals are still in progress.
                  </span>
                </div>
              </div>
          
            {/* Filter Controls */}
            <Card className="bg-slate-50">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-6 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-600">Year:</span>
                    <Select value={offerPatternsYear} onValueChange={setOfferPatternsYear}>
                      <SelectTrigger className="w-24 h-8 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {offerPatternsYears.map(year => (
                          <SelectItem key={year} value={year}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-600">Month:</span>
                    <Select value={offerPatternsMonth} onValueChange={setOfferPatternsMonth}>
                      <SelectTrigger className="w-32 h-8 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Months</SelectItem>
                        {monthNames.map((month, idx) => (
                          <SelectItem key={idx} value={String(idx)}>{month}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-600">View:</span>
                    <div className="flex rounded-lg border bg-white overflow-hidden">
                      <button 
                        onClick={() => setOfferPatternsPeriod('daily')}
                        className={`px-3 py-1.5 text-sm font-medium transition-colors ${offerPatternsPeriod === 'daily' ? 'bg-blue-600 text-white' : 'hover:bg-slate-100'}`}
                      >
                        Daily
                      </button>
                      <button 
                        onClick={() => setOfferPatternsPeriod('weekly')}
                        className={`px-3 py-1.5 text-sm font-medium border-x transition-colors ${offerPatternsPeriod === 'weekly' ? 'bg-blue-600 text-white' : 'hover:bg-slate-100'}`}
                      >
                        Weekly
                      </button>
                      <button 
                        onClick={() => setOfferPatternsPeriod('monthly')}
                        className={`px-3 py-1.5 text-sm font-medium transition-colors ${offerPatternsPeriod === 'monthly' ? 'bg-blue-600 text-white' : 'hover:bg-slate-100'}`}
                      >
                        Monthly
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground ml-auto">
                    Showing {offerPatternsPeriod} data for {offerPatternsMonth === 'all' ? offerPatternsYear : `${monthNames[parseInt(offerPatternsMonth)]} ${offerPatternsYear}`}
                  </div>
                </div>
              </CardContent>
            </Card>

          {/* ==================== SECTION 1: OFFER RATE BY APPLICATION DATE (Monday.com) ==================== */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 pb-2 border-b-2 border-blue-400">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-blue-900">Offer Rate by Application Date</h2>
                <p className="text-sm text-muted-foreground">Source: Monday.com — What % of applications received offers (grouped by when app was received)</p>
              </div>
            </div>
            
            {/* Context Note */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-medium text-blue-800">How to read this data: </span>
                <span className="text-blue-700">
                  This shows the offer rate for applications based on when they were submitted. 
                  Data may appear ~1 week behind current date as deals are still being worked through the pipeline. 
                  Recent days will show lower rates until all applications have been processed.
                </span>
              </div>
            </div>

            {/* Offer Rate Summary Cards */}
            {daily_activity?.month_end_analysis && (
              <Card>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className={`p-4 rounded-lg border-2 ${daily_activity.month_end_analysis.surge_indicator > 10 ? 'bg-red-100 border-red-400' : daily_activity.month_end_analysis.surge_indicator > 0 ? 'bg-amber-100 border-amber-400' : 'bg-green-100 border-green-400'}`}>
                      <div className="text-3xl font-bold">
                        {daily_activity.month_end_analysis.surge_indicator > 0 ? '+' : ''}{daily_activity.month_end_analysis.surge_indicator?.toFixed(1) || 0}%
                      </div>
                      <div className="text-sm font-medium">Month-End Rate Change</div>
                      <div className="text-xs text-muted-foreground">
                        {daily_activity.month_end_analysis.surge_indicator > 10 ? '⚠️ Higher approval at month-end' : 
                         daily_activity.month_end_analysis.surge_indicator < -10 ? '📉 Lower at month-end' : '✅ Consistent pattern'}
                      </div>
                    </div>
                    <div className="p-4 bg-slate-100 rounded-lg border">
                      <div className="text-2xl font-bold text-slate-700">
                        {daily_activity.month_end_analysis.rest_of_month?.offer_rate?.toFixed(1) || 0}%
                      </div>
                      <div className="text-sm font-medium">Days 1-26 Offer Rate</div>
                      <div className="text-xs text-muted-foreground">
                        {daily_activity.month_end_analysis.rest_of_month?.offers || 0} offers / {daily_activity.month_end_analysis.rest_of_month?.apps || 0} apps
                      </div>
                    </div>
                    <div className="p-4 bg-blue-100 rounded-lg border border-blue-300">
                      <div className="text-2xl font-bold text-blue-700">
                        {daily_activity.month_end_analysis.last_5_days?.offer_rate?.toFixed(1) || 0}%
                      </div>
                      <div className="text-sm font-medium">Days 27-31 Offer Rate</div>
                      <div className="text-xs text-muted-foreground">
                        {daily_activity.month_end_analysis.last_5_days?.offers || 0} offers / {daily_activity.month_end_analysis.last_5_days?.apps || 0} apps
                      </div>
                    </div>
                  </div>
                  
                  {/* Daily Offer Rate Chart */}
                  <div>
                    <h4 className="text-sm font-medium mb-3">
                      {offerPatternsPeriod === 'daily' ? 'Daily' : offerPatternsPeriod === 'weekly' ? 'Weekly' : 'Monthly'} Offer Rate ({offerPatternsMonth === 'all' ? offerPatternsYear : `${monthNames[parseInt(offerPatternsMonth)]} ${offerPatternsYear}`})
                    </h4>
                    {filteredOfferData.chartData && filteredOfferData.chartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <ComposedChart data={filteredOfferData.chartData || []}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey={filteredOfferData.xKey} 
                            fontSize={10} 
                            angle={-45} 
                            textAnchor="end" 
                            height={60}
                            tickFormatter={(value) => {
                              // Format ISO dates (YYYY-MM-DD) to readable format (Jan 15)
                              if (value && value.includes('-') && value.length === 10) {
                                const date = new Date(value);
                                return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
                              }
                              return value;
                            }}
                          />
                          <YAxis yAxisId="left" orientation="left" tickFormatter={(v) => `${v}%`} domain={[0, 50]} />
                          <YAxis yAxisId="right" orientation="right" />
                          <Tooltip 
                            formatter={(value, name) => name === 'Offer Rate' ? `${value}%` : value}
                            labelFormatter={(label) => {
                              // Format ISO dates in tooltip
                              if (label && label.includes('-') && label.length === 10) {
                                const date = new Date(label);
                                return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
                              }
                              return label;
                            }}
                          />
                          <Legend />
                          <Bar yAxisId="right" dataKey="apps" fill="#cbd5e1" name="Applications" />
                          <Line yAxisId="left" type="monotone" dataKey="offer_rate" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6' }} name="Offer Rate" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[280px] flex items-center justify-center bg-slate-50 rounded-lg border border-dashed">
                        <div className="text-center">
                          <AlertCircle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-slate-500 font-medium">No data available for {offerPatternsMonth === 'all' ? offerPatternsYear : `${monthNames[parseInt(offerPatternsMonth)]} ${offerPatternsYear}`}</p>
                          <p className="text-xs text-slate-400 mt-1">Try selecting a different time period or check if data exists for this range.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Offer Rate by Day of Month Pattern - only show in daily view */}
            {offerPatternsPeriod === 'daily' && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Offer Rate by Day of Month (Pattern)</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <ComposedChart data={(filteredOfferData.offer_rate_by_day_of_month || []).filter(d => d.apps > 0)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis yAxisId="left" orientation="left" tickFormatter={(v) => `${v}%`} domain={[0, 'auto']} />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip formatter={(value, name) => name.includes('Rate') ? `${value}%` : value} labelFormatter={(day) => `Day ${day}`} />
                      <Legend />
                      <Bar yAxisId="right" dataKey="apps" fill="#e2e8f0" name="Applications" />
                      <Bar yAxisId="right" dataKey="offers" fill="#3b82f6" name="Offers" />
                      <Line yAxisId="left" type="monotone" dataKey="offer_rate" stroke="#ef4444" strokeWidth={2} dot={{ fill: '#ef4444', r: 3 }} name="Offer Rate %" />
                      <ReferenceLine x={27} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: 'Month-End', position: 'top', fill: '#f59e0b', fontSize: 10 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Weekly Offer Rates */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Weekly Offer Rates ({offerPatternsYear})</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <ComposedChart data={filteredOfferData.weekly_offer_rates || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" fontSize={10} />
                    <YAxis yAxisId="left" orientation="left" tickFormatter={(v) => `${v}%`} domain={[0, 'auto']} />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip formatter={(value, name) => name.includes('Rate') ? `${value}%` : value} />
                    <Legend />
                    <Bar yAxisId="right" dataKey="apps" fill="#c7d2fe" name="Applications" />
                    <Bar yAxisId="right" dataKey="offers" fill="#6366f1" name="Offers" />
                    <Line yAxisId="left" type="monotone" dataKey="offer_rate" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 2 }} name="Offer Rate %" />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
          </div>

          {/* ==================== SECTION 2: OFFER VOLUME BY OFFER DATE (Airtable/JotForm) ==================== */}
          {filteredOfferSubmissions && (
            <div className="space-y-4 mt-8">
              <div className="flex items-center gap-3 pb-2 border-b-2 border-purple-400">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-purple-900">Offer Volume by Offer Date</h2>
                  <p className="text-sm text-muted-foreground">
                    Source: Airtable/JotForm — How many offers credit placed each day (grouped by when offer was submitted)
                    {filteredOfferSubmissions.last_sync && (
                      <span className="ml-2 text-xs bg-purple-100 px-2 py-0.5 rounded">
                        Synced: {new Date(filteredOfferSubmissions.last_sync).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              
              {/* Context Note */}
              <div className="flex items-start gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm">
                <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-purple-800">Real-time data: </span>
                  <span className="text-purple-700">
                    This shows the actual number of offers placed by the credit team each day. 
                    Data is accurate up to yesterday's date and syncs daily at 6am GMT. 
                    Use this to track credit team productivity and identify daily/weekly volume trends.
                  </span>
                </div>
              </div>
              
              {/* Section 2 Filter Controls (Independent) */}
              <Card className="bg-purple-50/50 border-purple-200">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-6 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-purple-700">Year:</span>
                      <Select value={offerVolumeYear} onValueChange={setOfferVolumeYear}>
                        <SelectTrigger className="w-24 h-8 bg-white border-purple-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {offerPatternsYears.map(year => (
                            <SelectItem key={year} value={year}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-purple-700">Month:</span>
                      <Select value={offerVolumeMonth} onValueChange={setOfferVolumeMonth}>
                        <SelectTrigger className="w-32 h-8 bg-white border-purple-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Months</SelectItem>
                          {monthNames.map((month, idx) => (
                            <SelectItem key={idx} value={String(idx)}>{month}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-purple-700">View:</span>
                      <div className="flex rounded-lg border border-purple-200 bg-white overflow-hidden">
                        <button 
                          onClick={() => setOfferVolumePeriod('daily')}
                          className={`px-3 py-1.5 text-sm font-medium transition-colors ${offerVolumePeriod === 'daily' ? 'bg-purple-600 text-white' : 'hover:bg-purple-50'}`}
                        >
                          Daily
                        </button>
                        <button 
                          onClick={() => setOfferVolumePeriod('weekly')}
                          className={`px-3 py-1.5 text-sm font-medium border-x border-purple-200 transition-colors ${offerVolumePeriod === 'weekly' ? 'bg-purple-600 text-white' : 'hover:bg-purple-50'}`}
                        >
                          Weekly
                        </button>
                        <button 
                          onClick={() => setOfferVolumePeriod('monthly')}
                          className={`px-3 py-1.5 text-sm font-medium transition-colors ${offerVolumePeriod === 'monthly' ? 'bg-purple-600 text-white' : 'hover:bg-purple-50'}`}
                        >
                          Monthly
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-purple-600 ml-auto">
                      Showing {offerVolumePeriod} data for {offerVolumeMonth === 'all' ? offerVolumeYear : `${monthNames[parseInt(offerVolumeMonth)]} ${offerVolumeYear}`}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Offer Volume Summary Cards */}
              <Card>
                <CardContent className="pt-6">
                  {/* Latest Day & Record Day Highlights */}
                  {(() => {
                    const dailyData = filteredOfferSubmissions.chartData || [];
                    const latestDay = dailyData.length > 0 ? dailyData[dailyData.length - 1] : null;
                    const recordDay = dailyData.length > 0 ? dailyData.reduce((max, d) => (d.submissions || 0) > (max.submissions || 0) ? d : max, dailyData[0]) : null;
                    const isRecordDay = latestDay && recordDay && latestDay.date === recordDay.date;
                    const avgDaily = dailyData.length > 0 ? Math.round(dailyData.reduce((sum, d) => sum + (d.submissions || 0), 0) / dailyData.length) : 0;
                    
                    return (
                      <>
                        {/* Top Row - Latest Day Performance */}
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className={`p-4 rounded-lg border-2 ${isRecordDay ? 'bg-gradient-to-r from-amber-100 to-yellow-100 border-amber-400' : 'bg-green-100 border-green-300'}`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-3xl font-bold text-green-700">
                                  {latestDay?.submissions || 0}
                                </div>
                                <div className="text-sm font-medium">Latest Day ({latestDay?.date ? new Date(latestDay.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'N/A'})</div>
                                <div className="text-xs text-muted-foreground">
                                  {latestDay?.new_customer || 0} new, {latestDay?.renewal || 0} renewal
                                </div>
                              </div>
                              {isRecordDay && (
                                <div className="text-right">
                                  <span className="inline-flex items-center px-2 py-1 bg-amber-200 text-amber-800 text-xs font-bold rounded-full">
                                    🏆 RECORD DAY!
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                            <div className="text-3xl font-bold text-amber-700">
                              {recordDay?.submissions || 0}
                            </div>
                            <div className="text-sm font-medium">All-Time Record ({recordDay?.date ? new Date(recordDay.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'N/A'})</div>
                            <div className="text-xs text-muted-foreground">
                              vs {avgDaily}/day average
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                  
                  {/* Bottom Row - Totals & Patterns */}
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="p-4 bg-purple-100 rounded-lg border border-purple-300">
                      <div className="text-2xl font-bold text-purple-700">
                        {filteredOfferSubmissions.total_records?.toLocaleString() || 0}
                      </div>
                      <div className="text-sm font-medium">Total Offers</div>
                      <div className="text-xs text-muted-foreground">All time</div>
                    </div>
                    <div className={`p-4 rounded-lg border ${
                      (filteredOfferSubmissions.month_end_analysis?.surge_indicator || 0) > 10 ? 'bg-red-50 border-red-300' : 
                      (filteredOfferSubmissions.month_end_analysis?.surge_indicator || 0) < -10 ? 'bg-slate-50 border-slate-300' : 
                      'bg-slate-50 border-slate-200'
                    }`}>
                      <div className="text-2xl font-bold text-slate-700">
                        {(filteredOfferSubmissions.month_end_analysis?.surge_indicator || 0) > 0 ? '+' : ''}{filteredOfferSubmissions.month_end_analysis?.surge_indicator?.toFixed(1) || 0}%
                      </div>
                      <div className="text-sm font-medium">Month-End Pattern</div>
                      <div className="text-xs text-muted-foreground">Historical trend</div>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg border">
                      <div className="text-2xl font-bold text-slate-600">
                        {filteredOfferSubmissions.month_end_analysis?.rest_of_month || 0}
                      </div>
                      <div className="text-sm font-medium">Days 1-26</div>
                      <div className="text-xs text-muted-foreground">
                        ~{Math.round((filteredOfferSubmissions.month_end_analysis?.rest_of_month || 0) / 26)}/day
                      </div>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg border">
                      <div className="text-2xl font-bold text-slate-600">
                        {filteredOfferSubmissions.month_end_analysis?.last_5_days || 0}
                      </div>
                      <div className="text-sm font-medium">Days 27-31</div>
                      <div className="text-xs text-muted-foreground">
                        ~{Math.round((filteredOfferSubmissions.month_end_analysis?.last_5_days || 0) / 5)}/day
                      </div>
                    </div>
                  </div>

                  {/* Daily Submissions Chart */}
                  <div>
                    <h4 className="text-sm font-medium mb-3">
                      {offerVolumePeriod === 'daily' ? 'Daily' : offerVolumePeriod === 'weekly' ? 'Weekly' : 'Monthly'} Offers Placed ({offerVolumeMonth === 'all' ? offerVolumeYear : `${monthNames[parseInt(offerVolumeMonth)]} ${offerVolumeYear}`})
                    </h4>
                    {filteredOfferSubmissions.chartData && filteredOfferSubmissions.chartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <ComposedChart data={filteredOfferSubmissions.chartData || []}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey={filteredOfferSubmissions.xKey}
                            fontSize={9} 
                            angle={-45} 
                            textAnchor="end" 
                            height={70}
                            tickFormatter={(d) => {
                              // For weekly/monthly views, just return the label as-is
                              if (offerVolumePeriod !== 'daily') return d;
                              // For daily, format the date
                              try {
                                const date = new Date(d);
                                if (isNaN(date.getTime())) return d;
                                return `${date.getDate()}/${date.getMonth()+1}`;
                              } catch {
                                return d;
                              }
                            }}
                          />
                          <YAxis />
                          <Tooltip 
                            labelFormatter={(d) => {
                              if (offerVolumePeriod !== 'daily') return d;
                              try {
                                const date = new Date(d);
                                if (isNaN(date.getTime())) return d;
                                return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
                              } catch {
                                return d;
                              }
                            }}
                            formatter={(value, name) => [value, name]}
                          />
                          <Legend />
                          <Bar dataKey="new_customer" stackId="a" fill="#8b5cf6" name="New Customer" />
                          <Bar dataKey="renewal" stackId="a" fill="#06b6d4" name="Renewal" />
                          <Line type="monotone" dataKey={filteredOfferSubmissions.dataKey} stroke="#f97316" strokeWidth={2} dot={false} name="Total" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[280px] flex items-center justify-center bg-purple-50 rounded-lg border border-dashed border-purple-200">
                        <div className="text-center">
                          <AlertCircle className="w-8 h-8 text-purple-300 mx-auto mb-2" />
                          <p className="text-purple-500 font-medium">No offer data for {offerVolumeMonth === 'all' ? offerVolumeYear : `${monthNames[parseInt(offerVolumeMonth)]} ${offerVolumeYear}`}</p>
                          <p className="text-xs text-purple-400 mt-1">Try selecting a different month or "All Months"</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Day of Month Volume Pattern - only show in daily view */}
              {offerVolumePeriod === 'daily' && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Offer Volume by Day of Month (Pattern)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={filteredOfferSubmissions.day_of_month_pattern || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" />
                        <YAxis />
                        <Tooltip labelFormatter={(day) => `Day ${day}`} />
                        <Bar dataKey="submissions" name="Offers Placed">
                          {(filteredOfferSubmissions.day_of_month_pattern || []).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.day >= 27 ? '#f59e0b' : '#8b5cf6'} />
                          ))}
                        </Bar>
                        <ReferenceLine x={27} stroke="#ef4444" strokeDasharray="5 5" label={{ value: 'Month-End', position: 'top', fill: '#ef4444', fontSize: 10 }} />
                      </BarChart>
                    </ResponsiveContainer>
                    <p className="text-xs text-muted-foreground text-center mt-1">
                      Orange bars = Month-end period (days 27-31)
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Weekly Volume Table */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Weekly Offer Volume ({offerVolumeYear})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-100">
                          <th className="text-left py-2 px-3">Week</th>
                          <th className="text-right py-2 px-3">Offers Placed</th>
                          <th className="text-right py-2 px-3">Avg/Day</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(filteredOfferSubmissions.weekly_submissions || []).slice(-12).map((week, idx) => (
                          <tr key={week.week} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="py-2 px-3 font-medium">{week.week}</td>
                            <td className="text-right py-2 px-3 font-bold text-purple-600">{week.submissions}</td>
                            <td className="text-right py-2 px-3 text-slate-600">{(week.submissions / 5).toFixed(1)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          </div>
        </TabsContent>


        {/* Top-up Forecast Tab */}
        <TabsContent value="topup-forecast" className="space-y-6">
          {/* NEW: 3-Month Lag Forecast Model - Based on Funding Cycles */}
          <Card className="border-blue-300 bg-blue-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Top-up Forecast (3-Month Funding Lag Model)
              </CardTitle>
              <CardDescription>
                Based on 26-week loans: Top-ups in Month M = {topup_forecast.funding_lag_forecast?.conversion_rate || 0}% of Total Funded in Month (M-3)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* KPI Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-blue-100 rounded-lg border-2 border-blue-400">
                  <p className="text-3xl font-bold text-blue-700">
                    {topup_forecast.funding_lag_forecast?.conversion_rate || 0}%
                  </p>
                  <p className="text-sm font-medium text-blue-600">Avg Conversion Rate</p>
                  <p className="text-xs text-muted-foreground">Funded → Top-up (3mo later)</p>
                </div>
                {(topup_forecast.funding_lag_forecast?.forecasts || []).map((f, idx) => (
                  <div key={f.forecast_month} className="p-4 bg-white rounded-lg border-2 border-blue-200">
                    <p className="text-2xl font-bold text-blue-600">
                      £{(f.expected_topups / 1000).toFixed(0)}k
                    </p>
                    <p className="text-sm font-medium text-blue-700">{f.forecast_month_label}</p>
                    <p className="text-xs text-muted-foreground">From {f.funded_month_label} (£{(f.funded_value/1000).toFixed(0)}k)</p>
                  </div>
                ))}
              </div>

              {/* Forecast Chart */}
              <div className="mb-6">
                <h4 className="text-sm font-medium mb-3">Forecast: Monthly Funding → Top-ups 3 Months Later</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={[
                    ...(topup_forecast.funding_lag_forecast?.correlation_data || []).map(c => ({
                      month: c.funded_month_label.split(' ')[0],
                      funded: c.funded_value,
                      topups: c.topup_value,
                      rate: c.conversion_rate,
                      type: 'historical'
                    })),
                    ...(topup_forecast.funding_lag_forecast?.forecasts || []).map(f => ({
                      month: f.funded_month_label.split(' ')[0],
                      funded: f.funded_value,
                      topups: f.expected_topups,
                      rate: topup_forecast.funding_lag_forecast?.conversion_rate || 0,
                      type: 'forecast'
                    }))
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                    <Tooltip 
                      formatter={(value, name) => {
                        if (name === 'rate') return [`${value}%`, 'Conversion Rate'];
                        return [`£${(value/1000).toFixed(0)}k`, name];
                      }}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="funded" fill="#dbeafe" name="Funded (source month)" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="left" dataKey="topups" fill="#3b82f6" name="Top-ups (3mo later)" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="rate" stroke="#1d4ed8" strokeWidth={2} dot={{ fill: '#1d4ed8', r: 4 }} name="Conv %" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {/* Historical Correlation Table */}
              <div>
                <h4 className="text-sm font-medium mb-3">Historical: Funding → Top-up Conversion</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">Funded Month</th>
                      <th className="text-right py-2 px-2">Amount Funded</th>
                      <th className="text-center py-2 px-2">→</th>
                      <th className="text-left py-2 px-2">Top-up Month (+3)</th>
                      <th className="text-right py-2 px-2">Top-ups Funded</th>
                      <th className="text-right py-2 px-2 bg-blue-50">Conv %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(topup_forecast.funding_lag_forecast?.correlation_data || []).slice().reverse().map((c, idx) => (
                      <tr key={c.funded_month} className={idx % 2 === 0 ? 'bg-slate-50' : ''}>
                        <td className="py-2 px-2 font-medium">{c.funded_month_label}</td>
                        <td className="text-right py-2 px-2">£{(c.funded_value / 1000).toFixed(0)}k</td>
                        <td className="text-center py-2 px-2 text-muted-foreground">→</td>
                        <td className="py-2 px-2">{c.topup_month}</td>
                        <td className="text-right py-2 px-2 text-blue-600">£{(c.topup_value / 1000).toFixed(0)}k</td>
                        <td className="text-right py-2 px-2 bg-blue-50">
                          <Badge className={c.conversion_rate >= 50 ? 'bg-blue-600' : c.conversion_rate >= 35 ? 'bg-blue-400' : 'bg-slate-400'}>
                            {c.conversion_rate}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Secondary: Loan Book Based Forecast */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-slate-600 flex items-center gap-2">
                <Database className="w-5 h-5" />
                Loan Book Correlation (Alternative View)
              </CardTitle>
              <CardDescription>
                Top-ups as a % of total open loan book (receivables) - less predictive but useful context
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-slate-100 rounded-lg border">
                  <p className="text-2xl font-bold text-slate-700">
                    {topup_forecast.loan_book_forecast?.historical_rate || 0}%
                  </p>
                  <p className="text-sm text-muted-foreground">Avg % of Book per Month</p>
                </div>
                <div className="p-3 bg-white rounded-lg border">
                  <p className="text-xl font-bold text-slate-600">
                    £{((topup_forecast.loan_book_forecast?.current_loan_book || 0) / 1000000).toFixed(2)}M
                  </p>
                  <p className="text-sm text-muted-foreground">Current Loan Book</p>
                </div>
                <div className="p-3 bg-white rounded-lg border">
                  <p className="text-xl font-bold text-slate-600">
                    £{((topup_forecast.loan_book_forecast?.monthly_forecast || 0) / 1000).toFixed(0)}k
                  </p>
                  <p className="text-sm text-muted-foreground">Expected Monthly</p>
                </div>
                <div className="p-3 bg-white rounded-lg border">
                  <p className="text-xl font-bold text-slate-600">
                    £{((topup_forecast.loan_book_forecast?.annual_forecast || 0) / 1000000).toFixed(2)}M
                  </p>
                  <p className="text-sm text-muted-foreground">Annualized</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Historical NB vs TU Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Historical: New Business vs Top-ups by Value</CardTitle>
                <CardDescription>Shows how much of funded value comes from renewals vs new customers</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={topup_forecast.historical_nb_tu || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
                    <Legend />
                    <Bar dataKey="nb_value" stackId="a" fill="#3b82f6" name="New Business Value" />
                    <Bar dataKey="tu_value" stackId="a" fill="#8b5cf6" name="Top-up Value" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Historical: Top-up % of Total Value</CardTitle>
                <CardDescription>Percentage of monthly funded value from top-ups</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={topup_forecast.historical_nb_tu || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                    <Tooltip formatter={(v, name) => name.includes('%') ? `${v}%` : v} />
                    <Legend />
                    <Bar dataKey="tu_pct_value" fill="#f59e0b" name="Top-up % (Value)" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="tu_pct_count" stroke="#ef4444" strokeWidth={2} name="Top-up % (Count)" dot={{ fill: '#ef4444' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Top-up Deals Table */}
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Top-up Opportunities</CardTitle>
              <CardDescription>Deals approaching their midpoint - potential top-up conversations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Month</th>
                      <th className="text-right py-3 px-2">Deals</th>
                      <th className="text-right py-3 px-2">Potential Value</th>
                      <th className="text-right py-3 px-2">Avg Deal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(topup_forecast.forecast_months || []).map((m, idx) => (
                      <tr key={m.month_key} className={idx % 2 === 0 ? 'bg-slate-50' : ''}>
                        <td className="py-2 px-2 font-medium">{m.month}</td>
                        <td className="text-right py-2 px-2">{m.potential_topups}</td>
                        <td className="text-right py-2 px-2 text-emerald-600 font-medium">
                          £{(m.potential_value || 0).toLocaleString()}
                        </td>
                        <td className="text-right py-2 px-2 text-muted-foreground">
                          £{m.potential_topups > 0 ? Math.round(m.potential_value / m.potential_topups).toLocaleString() : 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t-2 font-bold">
                    <tr>
                      <td className="py-2 px-2">Total</td>
                      <td className="text-right py-2 px-2">
                        {(topup_forecast.forecast_months || []).reduce((s, m) => s + m.potential_topups, 0)}
                      </td>
                      <td className="text-right py-2 px-2 text-emerald-600">
                        £{(topup_forecast.forecast_months || []).reduce((s, m) => s + m.potential_value, 0).toLocaleString()}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Funding Tab */}
        <TabsContent value="funding" className="space-y-6">
          <div className="flex justify-end -mb-2"><ExportXlsxButton sheets="summary" fy={performanceFY} label="Export this section" size="sm" /></div>
          {/* Funding Patterns Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-1 h-6 bg-emerald-500 rounded"></div>
              <h3 className="text-lg font-semibold">Funding Patterns Analysis</h3>
            </div>
            
            {/* Filter Controls */}
            <Card className="bg-emerald-50/50 border-emerald-200">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-6 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-emerald-700">Year:</span>
                    <Select value={fundingYear} onValueChange={setFundingYear}>
                      <SelectTrigger className="w-24 h-8 bg-white border-emerald-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {offerPatternsYears.map(year => (
                          <SelectItem key={year} value={year}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-emerald-700">Month:</span>
                    <Select value={fundingMonth} onValueChange={setFundingMonth}>
                      <SelectTrigger className="w-32 h-8 bg-white border-emerald-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Months</SelectItem>
                        {monthNames.map((month, idx) => (
                          <SelectItem key={idx} value={String(idx)}>{month}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-emerald-700">View:</span>
                    <div className="flex rounded-lg border border-emerald-200 bg-white overflow-hidden">
                      <button 
                        onClick={() => setFundingPeriod('daily')}
                        className={`px-3 py-1.5 text-sm font-medium transition-colors ${fundingPeriod === 'daily' ? 'bg-emerald-600 text-white' : 'hover:bg-emerald-50'}`}
                      >
                        Daily
                      </button>
                      <button 
                        onClick={() => setFundingPeriod('weekly')}
                        className={`px-3 py-1.5 text-sm font-medium border-x border-emerald-200 transition-colors ${fundingPeriod === 'weekly' ? 'bg-emerald-600 text-white' : 'hover:bg-emerald-50'}`}
                      >
                        Weekly
                      </button>
                      <button 
                        onClick={() => setFundingPeriod('monthly')}
                        className={`px-3 py-1.5 text-sm font-medium transition-colors ${fundingPeriod === 'monthly' ? 'bg-emerald-600 text-white' : 'hover:bg-emerald-50'}`}
                      >
                        Monthly
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-emerald-600 ml-auto">
                    Showing {fundingPeriod} data for {fundingMonth === 'all' ? fundingYear : `${monthNames[parseInt(fundingMonth)]} ${fundingYear}`}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-emerald-600 uppercase tracking-wide">Total Funded</p>
                  <p className="text-2xl font-bold text-emerald-700">{filteredFundingData.totalCount || 0}</p>
                  <p className="text-sm text-emerald-600">Deals</p>
                </CardContent>
              </Card>
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-emerald-600 uppercase tracking-wide">Total Value</p>
                  <p className="text-2xl font-bold text-emerald-700">£{((filteredFundingData.totalValue || 0) / 1000000).toFixed(2)}M</p>
                  <p className="text-sm text-emerald-600">Originated</p>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-amber-600 uppercase tracking-wide">Month-End (27th-31st)</p>
                  <p className="text-2xl font-bold text-amber-700">{filteredFundingData.monthEndAnalysis?.last5Days?.count || 0}</p>
                  <p className="text-sm text-amber-600">£{((filteredFundingData.monthEndAnalysis?.last5Days?.value || 0) / 1000).toFixed(0)}k</p>
                </CardContent>
              </Card>
              <Card className={`${(filteredFundingData.monthEndAnalysis?.surgeIndicator || 0) > 20 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-slate-600 uppercase tracking-wide">Month-End Surge</p>
                  <p className={`text-2xl font-bold ${(filteredFundingData.monthEndAnalysis?.surgeIndicator || 0) > 20 ? 'text-red-700' : 'text-slate-700'}`}>
                    {filteredFundingData.monthEndAnalysis?.surgeIndicator > 0 ? '+' : ''}{filteredFundingData.monthEndAnalysis?.surgeIndicator || 0}%
                  </p>
                  <p className="text-sm text-slate-500">vs rest of month (per day avg)</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Daily/Weekly/Monthly Funding Chart */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">
                    {fundingPeriod === 'daily' ? 'Daily' : fundingPeriod === 'weekly' ? 'Weekly' : 'Monthly'} Amount Funded ({fundingMonth === 'all' ? fundingYear : `${monthNames[parseInt(fundingMonth)]} ${fundingYear}`})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {filteredFundingData.chartData && filteredFundingData.chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <ComposedChart data={filteredFundingData.chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey={filteredFundingData.xKey} 
                          fontSize={10} 
                          angle={-45} 
                          textAnchor="end" 
                          height={60}
                          tickFormatter={(value) => {
                            if (fundingPeriod !== 'daily') return value;
                            if (value && value.includes('-') && value.length === 10) {
                              const date = new Date(value);
                              return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                            }
                            return value;
                          }}
                        />
                        <YAxis yAxisId="left" tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip 
                          labelFormatter={(label) => {
                            if (fundingPeriod !== 'daily') return label;
                            if (label && label.includes('-')) {
                              const date = new Date(label);
                              return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
                            }
                            return label;
                          }}
                          formatter={(value, name) => name === 'Value' ? `£${value.toLocaleString()}` : value}
                        />
                        <Legend />
                        <Bar yAxisId="left" dataKey="value" fill="#10b981" name="Value" radius={[4, 4, 0, 0]} />
                        <Line yAxisId="right" type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 2 }} name="Count" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[280px] flex items-center justify-center bg-slate-50 rounded-lg border border-dashed">
                      <div className="text-center">
                        <AlertCircle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-slate-500 font-medium">No funding data for {fundingMonth === 'all' ? fundingYear : `${monthNames[parseInt(fundingMonth)]} ${fundingYear}`}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Funding by Day of Month Pattern */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Funding by Day of Month (Pattern)</CardTitle>
                  <CardDescription>Shows month-end spikes - days 27-31 highlighted</CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredFundingData.dayOfMonthData && filteredFundingData.dayOfMonthData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={filteredFundingData.dayOfMonthData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" fontSize={10} />
                        <YAxis yAxisId="left" tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip formatter={(value, name) => name === 'Value' ? `£${value.toLocaleString()}` : value} />
                        <Legend />
                        <Bar 
                          yAxisId="left" 
                          dataKey="value" 
                          name="Value"
                          radius={[2, 2, 0, 0]}
                        >
                          {filteredFundingData.dayOfMonthData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.day >= 27 ? '#f59e0b' : '#10b981'} />
                          ))}
                        </Bar>
                        <Line yAxisId="right" type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={{ r: 1 }} name="Count" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[280px] flex items-center justify-center bg-slate-50 rounded-lg border border-dashed">
                      <p className="text-slate-500">No data available</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Weekly Funding */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Weekly Funding ({fundingYear})</CardTitle>
              </CardHeader>
              <CardContent>
                {filteredFundingData.weeklyData && filteredFundingData.weeklyData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <ComposedChart data={filteredFundingData.weeklyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" fontSize={10} angle={-45} textAnchor="end" height={60} />
                      <YAxis yAxisId="left" tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip formatter={(value, name) => name === 'Value' ? `£${value.toLocaleString()}` : value} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="value" fill="#10b981" name="Value" radius={[4, 4, 0, 0]} />
                      <Line yAxisId="right" type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6' }} name="Count" />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center bg-slate-50 rounded-lg border border-dashed">
                    <p className="text-slate-500">No weekly data available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          <Separator className="my-6" />
          
          {/* Existing KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-emerald-700">
                  £{(current_month.funded_value / 1000).toFixed(0)}k
                </p>
                <p className="text-sm text-emerald-600 mt-1">Total Funded This Month</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-blue-700">
                  £{(summary.total_funded_value / 1000000).toFixed(2)}M
                </p>
                <p className="text-sm text-blue-600 mt-1">YTD Originated</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-6 text-center">
                <p className="text-4xl font-bold text-amber-700">
                  {summary.total_offers}
                </p>
                <p className="text-sm text-amber-600 mt-1">Total Offers</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Deals Funded per Month */}
            <Card>
              <CardHeader>
                <CardTitle>Deals Funded per Month</CardTitle>
                <CardDescription>Number of deals successfully funded</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthly_trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="funded" fill="#10b981" name="Deals Funded" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Amount Funded per Month */}
            <Card>
              <CardHeader>
                <CardTitle>Amount £ Funded per Month</CardTitle>
                <CardDescription>Value of funded deals with count overlay</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={monthly_trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip formatter={(v, name) => name === 'Funded Value' ? `£${v.toLocaleString()}` : v} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="funded_value" fill="#f59e0b" name="Funded Value" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="funded" stroke="#3b82f6" strokeWidth={2} name="Count" dot={{ fill: '#3b82f6' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Top Brokers YTD */}
          <Card>
            <CardHeader>
              <CardTitle>Top 5 Brokers in {currentDate.fyLabel} by Apps</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={top_brokers_ytd}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={11} />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="applications" fill="#6366f1" name="Applications YTD" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Weekly Trends */}
          {weekly_trends.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Apps per Week (YTD)</CardTitle>
                <CardDescription>Weekly application trend for the year</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={weekly_trends.slice(-26)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" fontSize={10} />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="applications" fill="#8b5cf6" name="Apps" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </TabsContent>
          </Tabs>
        </TabsContent>

        {/* ==================== COST OF SALES TAB ==================== */}
        <TabsContent value="cost-of-sales" className="space-y-6">
          <div className="flex justify-end -mb-2"><ExportXlsxButton sheets="cost_of_sales" fy={selectedFY} label="Export this section" size="sm" /></div>
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <PoundSterling className="w-5 h-5 text-rose-600" />
                Cost of Sales Analysis
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Track broker commission costs as a percentage of funded amounts
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Financial Year Selector */}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <Select value={String(selectedFY)} onValueChange={(val) => setSelectedFY(parseInt(val))}>
                  <SelectTrigger className="w-[140px]" data-testid="fy-selector">
                    <SelectValue placeholder="Select FY" />
                  </SelectTrigger>
                  <SelectContent>
                    {fyOptions.map(opt => (
                      <SelectItem key={opt.value} value={String(opt.value)}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => loadCostOfSales(selectedFY)}
                disabled={costOfSalesLoading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${costOfSalesLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
          
          {/* FY Badge */}
          {costOfSalesData?.fy?.label && (
            <div className="flex items-center gap-2 pb-2">
              <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                <Calendar className="w-3 h-3 mr-1" />
                {costOfSalesData.fy.label}
              </Badge>
              <span className="text-xs text-muted-foreground">
                ({new Date(costOfSalesData.fy.start_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })} - {new Date(costOfSalesData.fy.end_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })})
              </span>
            </div>
          )}

          {costOfSalesLoading ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading cost of sales data...</span>
              </CardContent>
            </Card>
          ) : costOfSalesData?.error ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <AlertCircle className="w-6 h-6 text-red-500" />
                <span className="ml-2 text-red-500">Error: {costOfSalesData.error}</span>
              </CardContent>
            </Card>
          ) : costOfSalesData ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-rose-50 to-pink-50 border-rose-200">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-rose-600 uppercase tracking-wide">{costOfSalesData.fy?.label || 'FY'} Cost of Sales</p>
                        <p className="text-3xl font-bold text-rose-700">{costOfSalesData.fy?.cos_pct || costOfSalesData.ytd?.cos_pct || 0}%</p>
                        <p className="text-sm text-rose-600">£{((costOfSalesData.fy?.comms || costOfSalesData.ytd?.comms || 0) / 1000).toFixed(0)}k / £{((costOfSalesData.fy?.funded || costOfSalesData.ytd?.funded || 0) / 1000000).toFixed(2)}M</p>
                      </div>
                      <Percent className="w-8 h-8 text-rose-300" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs text-emerald-600 uppercase tracking-wide">{costOfSalesData.fy?.label || 'FY'} Funded</p>
                    <p className="text-2xl font-bold text-emerald-700">£{((costOfSalesData.fy?.funded || costOfSalesData.ytd?.funded || 0) / 1000000).toFixed(2)}M</p>
                    <p className="text-sm text-emerald-600">{costOfSalesData.fy?.count || costOfSalesData.ytd?.count || 0} deals</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs text-amber-600 uppercase tracking-wide">{costOfSalesData.fy?.label || 'FY'} Commissions</p>
                    <p className="text-2xl font-bold text-amber-700">£{((costOfSalesData.fy?.comms || costOfSalesData.ytd?.comms || 0) / 1000).toFixed(0)}k</p>
                    <p className="text-sm text-amber-600">Paid to brokers</p>
                  </CardContent>
                </Card>
                
                <Card className={`${(costOfSalesData.summary?.cos_trend || 0) > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs text-slate-600 uppercase tracking-wide">6-Month Avg / Trend</p>
                    <p className={`text-2xl font-bold ${(costOfSalesData.summary?.cos_trend || 0) > 0 ? 'text-red-700' : 'text-green-700'}`}>
                      {costOfSalesData.summary?.avg_cos_6m || 0}%
                    </p>
                    <p className={`text-sm ${(costOfSalesData.summary?.cos_trend || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {(costOfSalesData.summary?.cos_trend || 0) > 0 ? '↑' : '↓'} {Math.abs(costOfSalesData.summary?.cos_trend || 0)}% vs last month
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Commission Budget & Slush Fund */}
              <Card className="border-2 border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <PoundSterling className="w-5 h-5 text-blue-600" />
                        Commission Budget Tracker
                      </CardTitle>
                      <CardDescription>8% P&L provision vs actual commission spend</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Current Month Budget */}
                    <div className="space-y-4">
                      <h4 className="font-medium text-slate-700 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {costOfSalesData.summary?.latest_month?.month_name || 'Current Month'}
                      </h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white rounded-lg p-3 border">
                          <p className="text-xs text-slate-500 uppercase">8% Budget</p>
                          <p className="text-xl font-bold text-blue-600">
                            £{((costOfSalesData.summary?.latest_month?.budget_provision || 0) / 1000).toFixed(0)}k
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border">
                          <p className="text-xs text-slate-500 uppercase">Spent</p>
                          <p className="text-xl font-bold text-amber-600">
                            £{((costOfSalesData.summary?.latest_month?.comms || 0) / 1000).toFixed(0)}k
                          </p>
                        </div>
                        <div className={`rounded-lg p-3 border ${(costOfSalesData.summary?.latest_month?.slush_remaining || 0) >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                          <p className="text-xs text-slate-500 uppercase">Slush Fund</p>
                          <p className={`text-xl font-bold ${(costOfSalesData.summary?.latest_month?.slush_remaining || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            £{((costOfSalesData.summary?.latest_month?.slush_remaining || 0) / 1000).toFixed(1)}k
                          </p>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>Budget utilization</span>
                          <span>{100 - (costOfSalesData.summary?.latest_month?.slush_pct || 0)}% used</span>
                        </div>
                        <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${(100 - (costOfSalesData.summary?.latest_month?.slush_pct || 0)) > 100 ? 'bg-red-500' : (100 - (costOfSalesData.summary?.latest_month?.slush_pct || 0)) > 80 ? 'bg-amber-500' : 'bg-green-500'}`}
                            style={{ width: `${Math.min(100 - (costOfSalesData.summary?.latest_month?.slush_pct || 0), 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* FY Budget */}
                    <div className="space-y-4">
                      <h4 className="font-medium text-slate-700 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        {costOfSalesData.fy?.label || 'Financial Year'} Total
                      </h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white rounded-lg p-3 border">
                          <p className="text-xs text-slate-500 uppercase">8% Budget</p>
                          <p className="text-xl font-bold text-blue-600">
                            £{((costOfSalesData.fy?.budget_provision || costOfSalesData.ytd?.budget_provision || 0) / 1000).toFixed(0)}k
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border">
                          <p className="text-xs text-slate-500 uppercase">Spent</p>
                          <p className="text-xl font-bold text-amber-600">
                            £{((costOfSalesData.fy?.comms || costOfSalesData.ytd?.comms || 0) / 1000).toFixed(0)}k
                          </p>
                        </div>
                        <div className={`rounded-lg p-3 border ${(costOfSalesData.fy?.slush_remaining || costOfSalesData.ytd?.slush_remaining || 0) >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                          <p className="text-xs text-slate-500 uppercase">FY Slush</p>
                          <p className={`text-xl font-bold ${(costOfSalesData.fy?.slush_remaining || costOfSalesData.ytd?.slush_remaining || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            £{((costOfSalesData.fy?.slush_remaining || costOfSalesData.ytd?.slush_remaining || 0) / 1000).toFixed(1)}k
                          </p>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>FY budget utilization</span>
                          <span>{100 - (costOfSalesData.fy?.slush_pct || costOfSalesData.ytd?.slush_pct || 0)}% used</span>
                        </div>
                        <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${(100 - (costOfSalesData.fy?.slush_pct || costOfSalesData.ytd?.slush_pct || 0)) > 100 ? 'bg-red-500' : (100 - (costOfSalesData.fy?.slush_pct || costOfSalesData.ytd?.slush_pct || 0)) > 80 ? 'bg-amber-500' : 'bg-green-500'}`}
                            style={{ width: `${Math.min(100 - (costOfSalesData.fy?.slush_pct || costOfSalesData.ytd?.slush_pct || 0), 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* What-If Calculator - Uses draft state for local edits */}
              <Card className="border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-pink-50">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Calculator className="w-5 h-5 text-purple-600" />
                        What-If Calculator
                        {whatIfHasChanges && (
                          <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-300 text-xs">
                            Unapplied
                          </Badge>
                        )}
                        {whatIfDeals.length > 0 && (
                          <Badge variant="default" className="ml-2 bg-purple-600 text-xs">
                            {whatIfDeals.length} deal(s) applied
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription>Model broker incentive scenarios - edit locally, then apply to see impact</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {whatIfDeals.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearWhatIfDeals}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          Clear Applied
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={applyWhatIfDeals}
                        disabled={!whatIfHasChanges}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        Apply to Slush
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Draft Scenario Table - local edits */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-purple-100">
                            <th className="text-left py-2 px-3">Deal Description</th>
                            <th className="text-right py-2 px-3">Deal Size (£)</th>
                            <th className="text-right py-2 px-3">Comms %</th>
                            <th className="text-right py-2 px-3">Commission (£)</th>
                            <th className="text-right py-2 px-3">8% Budget (£)</th>
                            <th className="text-right py-2 px-3">Impact</th>
                            <th className="text-center py-2 px-3 w-10"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {draftWhatIfDeals.map((deal, idx) => {
                            const commsAmount = deal.dealSize * (deal.commsPct / 100);
                            const budgetAmount = deal.dealSize * 0.08;
                            const impact = budgetAmount - commsAmount;
                            return (
                              <tr key={deal.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-purple-50/50'}>
                                <td className="py-2 px-3">
                                  <input
                                    type="text"
                                    value={deal.description}
                                    onChange={(e) => {
                                      const newDeals = [...draftWhatIfDeals];
                                      newDeals[idx].description = e.target.value;
                                      setDraftWhatIfDeals(newDeals);
                                      setWhatIfHasChanges(true);
                                    }}
                                    placeholder="e.g., Broker incentive deal"
                                    className="w-full px-2 py-1 border rounded text-sm"
                                  />
                                </td>
                                <td className="py-2 px-3">
                                  <input
                                    type="number"
                                    value={deal.dealSize}
                                    onChange={(e) => {
                                      const newDeals = [...draftWhatIfDeals];
                                      newDeals[idx].dealSize = parseFloat(e.target.value) || 0;
                                      setDraftWhatIfDeals(newDeals);
                                      setWhatIfHasChanges(true);
                                    }}
                                    className="w-24 px-2 py-1 border rounded text-sm text-right"
                                  />
                                </td>
                                <td className="py-2 px-3">
                                  <input
                                    type="number"
                                    step="0.5"
                                    value={deal.commsPct}
                                    onChange={(e) => {
                                      const newDeals = [...draftWhatIfDeals];
                                      newDeals[idx].commsPct = parseFloat(e.target.value) || 0;
                                      setDraftWhatIfDeals(newDeals);
                                      setWhatIfHasChanges(true);
                                    }}
                                    className="w-16 px-2 py-1 border rounded text-sm text-right"
                                  />
                                </td>
                                <td className="text-right py-2 px-3 text-amber-600 font-medium">
                                  £{commsAmount.toLocaleString()}
                                </td>
                                <td className="text-right py-2 px-3 text-blue-600 font-medium">
                                  £{budgetAmount.toLocaleString()}
                                </td>
                                <td className={`text-right py-2 px-3 font-bold ${impact >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {impact >= 0 ? '+' : ''}£{impact.toLocaleString()}
                                </td>
                                <td className="text-center py-2 px-3">
                                  <button
                                    onClick={() => {
                                      setDraftWhatIfDeals(draftWhatIfDeals.filter((_, i) => i !== idx));
                                      setWhatIfHasChanges(true);
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                    disabled={draftWhatIfDeals.length === 1}
                                  >
                                    ✕
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="bg-purple-100 border-t-2 border-purple-300">
                          <tr>
                            <td className="py-2 px-3 font-semibold">DRAFT TOTAL</td>
                            <td className="text-right py-2 px-3 font-semibold">
                              £{draftWhatIfDeals.reduce((sum, d) => sum + d.dealSize, 0).toLocaleString()}
                            </td>
                            <td className="text-right py-2 px-3 font-semibold">
                              {draftWhatIfDeals.length > 0 && draftWhatIfDeals.reduce((sum, d) => sum + d.dealSize, 0) > 0 
                                ? (draftWhatIfDeals.reduce((sum, d) => sum + (d.dealSize * d.commsPct / 100), 0) / draftWhatIfDeals.reduce((sum, d) => sum + d.dealSize, 0) * 100).toFixed(1) 
                                : 0}%
                            </td>
                            <td className="text-right py-2 px-3 font-semibold text-amber-600">
                              £{draftWhatIfDeals.reduce((sum, d) => sum + (d.dealSize * d.commsPct / 100), 0).toLocaleString()}
                            </td>
                            <td className="text-right py-2 px-3 font-semibold text-blue-600">
                              £{draftWhatIfDeals.reduce((sum, d) => sum + (d.dealSize * 0.08), 0).toLocaleString()}
                            </td>
                            <td className={`text-right py-2 px-3 font-bold ${draftWhatIfDeals.reduce((sum, d) => sum + (d.dealSize * 0.08) - (d.dealSize * d.commsPct / 100), 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {draftWhatIfDeals.reduce((sum, d) => sum + (d.dealSize * 0.08) - (d.dealSize * d.commsPct / 100), 0) >= 0 ? '+' : ''}
                              £{draftWhatIfDeals.reduce((sum, d) => sum + (d.dealSize * 0.08) - (d.dealSize * d.commsPct / 100), 0).toLocaleString()}
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setDraftWhatIfDeals([...draftWhatIfDeals, { id: Date.now(), dealSize: 50000, commsPct: 9, description: '' }]);
                          setWhatIfHasChanges(true);
                        }}
                        className="border-purple-300 text-purple-700 hover:bg-purple-100"
                      >
                        + Add Deal
                      </Button>
                      
                      {/* Summary of impact on current month slush - shows APPLIED deals impact */}
                      <div className="text-right">
                        <p className="text-sm text-slate-600">
                          Current month slush: <span className="font-semibold text-green-600">£{((costOfSalesData.summary?.latest_month?.slush_remaining || 0) / 1000).toFixed(1)}k</span>
                        </p>
                        {whatIfDeals.length > 0 && (
                          <p className="text-sm text-slate-600">
                            After applied deals: <span className={`font-semibold ${((costOfSalesData.summary?.latest_month?.slush_remaining || 0) + whatIfDeals.reduce((sum, d) => sum + (d.dealSize * 0.08) - (d.dealSize * d.commsPct / 100), 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              £{(((costOfSalesData.summary?.latest_month?.slush_remaining || 0) + whatIfDeals.reduce((sum, d) => sum + (d.dealSize * 0.08) - (d.dealSize * d.commsPct / 100), 0)) / 1000).toFixed(1)}k
                            </span>
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Quick scenario examples */}
                    <div className="pt-4 border-t">
                      <p className="text-xs text-slate-500 mb-2">Quick scenarios (load into draft):</p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setDraftWhatIfDeals([{ id: Date.now(), dealSize: 100000, commsPct: 9, description: '+1% incentive on £100k deal' }]); setWhatIfHasChanges(true); }}
                          className="text-xs"
                        >
                          +1% on £100k deal
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { 
                            setDraftWhatIfDeals([
                              { id: Date.now(), dealSize: 50000, commsPct: 10, description: 'Deal 1' },
                              { id: Date.now() + 1, dealSize: 75000, commsPct: 10, description: 'Deal 2' },
                              { id: Date.now() + 2, dealSize: 60000, commsPct: 10, description: 'Deal 3' }
                            ]); 
                            setWhatIfHasChanges(true); 
                          }}
                          className="text-xs"
                        >
                          3 deals at 10%
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setDraftWhatIfDeals([{ id: Date.now(), dealSize: 250000, commsPct: 8, description: 'Big deal at standard rate' }]); setWhatIfHasChanges(true); }}
                          className="text-xs"
                        >
                          £250k at 8%
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Monthly Cost of Sales Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Monthly Cost of Sales Trend</CardTitle>
                  <CardDescription>Commission as a percentage of funded amount</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart data={costOfSalesData.monthly?.slice(-12) || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month_name" fontSize={11} angle={-45} textAnchor="end" height={60} />
                      <YAxis yAxisId="left" tickFormatter={(v) => `${v}%`} domain={[0, 'auto']} />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} />
                      <Tooltip 
                        formatter={(value, name) => {
                          if (name === 'CoS %') return [`${value}%`, name];
                          if (name === 'Commissions') return [`£${value.toLocaleString()}`, name];
                          if (name === 'Funded') return [`£${value.toLocaleString()}`, name];
                          return [value, name];
                        }}
                      />
                      <Legend />
                      <Bar yAxisId="right" dataKey="funded" fill="#10b981" name="Funded" opacity={0.7} />
                      <Bar yAxisId="right" dataKey="comms" fill="#f59e0b" name="Commissions" />
                      <Line yAxisId="left" type="monotone" dataKey="cos_pct" stroke="#e11d48" strokeWidth={3} dot={{ fill: '#e11d48', r: 4 }} name="CoS %" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              
              {/* Monthly Breakdown Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Monthly Breakdown</CardTitle>
                  <CardDescription>Detailed monthly cost of sales analysis</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-100">
                          <th className="text-left py-3 px-3">Month</th>
                          <th className="text-right py-3 px-3">Deals</th>
                          <th className="text-right py-3 px-3">Funded</th>
                          <th className="text-right py-3 px-3">Commissions</th>
                          <th className="text-right py-3 px-3">CoS %</th>
                          <th className="text-right py-3 px-3">Avg Deal</th>
                          <th className="text-right py-3 px-3">Avg Comms</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(costOfSalesData.monthly || []).slice(-12).reverse().map((month, idx) => {
                          const avgDeal = month.count > 0 ? month.funded / month.count : 0;
                          const avgComms = month.count > 0 ? month.comms / month.count : 0;
                          return (
                            <tr key={month.month} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                              <td className="py-2 px-3 font-medium">{month.month_name}</td>
                              <td className="text-right py-2 px-3">{month.count}</td>
                              <td className="text-right py-2 px-3 text-emerald-600 font-medium">£{(month.funded / 1000).toFixed(0)}k</td>
                              <td className="text-right py-2 px-3 text-amber-600 font-medium">£{(month.comms / 1000).toFixed(0)}k</td>
                              <td className="text-right py-2 px-3">
                                <Badge variant={month.cos_pct <= 5 ? 'default' : month.cos_pct <= 7 ? 'secondary' : 'destructive'}>
                                  {month.cos_pct}%
                                </Badge>
                              </td>
                              <td className="text-right py-2 px-3 text-slate-600">£{(avgDeal / 1000).toFixed(0)}k</td>
                              <td className="text-right py-2 px-3 text-slate-600">£{avgComms.toFixed(0)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-rose-50 border-t-2 border-rose-200">
                        <tr>
                          <td className="py-2 px-3 font-semibold">YTD Total</td>
                          <td className="text-right py-2 px-3 font-semibold">{costOfSalesData.ytd?.count || 0}</td>
                          <td className="text-right py-2 px-3 font-semibold text-emerald-700">£{((costOfSalesData.ytd?.funded || 0) / 1000).toFixed(0)}k</td>
                          <td className="text-right py-2 px-3 font-semibold text-amber-700">£{((costOfSalesData.ytd?.comms || 0) / 1000).toFixed(0)}k</td>
                          <td className="text-right py-2 px-3 font-semibold">
                            <Badge variant="destructive">{costOfSalesData.ytd?.cos_pct || 0}%</Badge>
                          </td>
                          <td className="text-right py-2 px-3 font-semibold text-slate-600">
                            £{(((costOfSalesData.ytd?.funded || 0) / (costOfSalesData.ytd?.count || 1)) / 1000).toFixed(0)}k
                          </td>
                          <td className="text-right py-2 px-3 font-semibold text-slate-600">
                            £{((costOfSalesData.ytd?.comms || 0) / (costOfSalesData.ytd?.count || 1)).toFixed(0)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              </Card>
              
              {/* Broker Performance Analysis - With Period Selector */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-slate-600" />
                        Broker Commission Analysis - {selectedBrokerBreakdown.label}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        Brokers ranked by highest CoS % (min. 2 deals) • 
                        <span className="text-amber-600 font-medium"> Note: Low CoS brokers (renewals at 6%) are more valuable</span>
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={brokerBreakdownPeriod} onValueChange={setBrokerBreakdownPeriod}>
                        <SelectTrigger className="w-[180px]" data-testid="broker-period-selector">
                          <SelectValue placeholder="Select period" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fy">
                            <span className="font-semibold text-indigo-600">{costOfSalesData.fy?.label || 'FY'} to Date</span>
                          </SelectItem>
                          <SelectItem value="latest">
                            <span className="font-medium">Latest Month</span>
                          </SelectItem>
                          <hr className="my-1" />
                          {(costOfSalesData.monthly || []).slice().reverse().map(m => (
                            <SelectItem key={m.month} value={m.month}>
                              {m.month_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Period Summary */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-emerald-50 rounded-lg p-3 text-center border border-emerald-200">
                      <p className="text-xs text-emerald-600 uppercase">Total Funded</p>
                      <p className="text-lg font-bold text-emerald-700">£{(selectedBrokerBreakdown.totalFunded / 1000000).toFixed(2)}M</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center border border-amber-200">
                      <p className="text-xs text-amber-600 uppercase">Total Commissions</p>
                      <p className="text-lg font-bold text-amber-700">£{(selectedBrokerBreakdown.totalComms / 1000).toFixed(0)}k</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center border border-slate-200">
                      <p className="text-xs text-slate-600 uppercase">Total Deals</p>
                      <p className="text-lg font-bold text-slate-700">{selectedBrokerBreakdown.totalDeals}</p>
                    </div>
                  </div>
                  
                  {selectedBrokerBreakdown.data.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-100">
                            <th className="text-left py-3 px-3">#</th>
                            <th className="text-left py-3 px-3">Broker</th>
                            <th className="text-right py-3 px-3">Deals</th>
                            <th className="text-right py-3 px-3">Funded</th>
                            <th className="text-right py-3 px-3">Commissions</th>
                            <th className="text-right py-3 px-3">CoS %</th>
                            <th className="text-center py-3 px-3">Value Rating</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedBrokerBreakdown.data.map((broker, idx) => {
                            // Value rating: Low CoS = Good (renewals), High CoS = Needs attention
                            const isGoodValue = broker.cos_pct <= 6;
                            const isNeutral = broker.cos_pct > 6 && broker.cos_pct <= 8;
                            const isHighCost = broker.cos_pct > 8;
                            
                            return (
                              <tr key={broker.name} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                <td className="py-2 px-3 font-bold text-muted-foreground">{idx + 1}</td>
                                <td className="py-2 px-3 font-medium">{broker.name}</td>
                                <td className="text-right py-2 px-3">{broker.count}</td>
                                <td className="text-right py-2 px-3 text-emerald-600">£{(broker.funded / 1000).toFixed(0)}k</td>
                                <td className="text-right py-2 px-3 text-amber-600">£{(broker.comms / 1000).toFixed(0)}k</td>
                                <td className="text-right py-2 px-3">
                                  <Badge variant={isGoodValue ? 'default' : isNeutral ? 'secondary' : 'destructive'}>
                                    {broker.cos_pct}%
                                  </Badge>
                                </td>
                                <td className="text-center py-2 px-3">
                                  {isGoodValue ? (
                                    <span className="text-green-600 text-xs font-medium">✓ Good value</span>
                                  ) : isNeutral ? (
                                    <span className="text-slate-500 text-xs">Standard</span>
                                  ) : (
                                    <span className="text-red-600 text-xs font-medium">⚠ High cost</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-8 text-center text-muted-foreground">
                      <p>No broker data available for this period (min. 2 deals required)</p>
                    </div>
                  )}
                  
                  {/* Explanation note */}
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-700">
                      <strong>Understanding CoS %:</strong> Renewals pay 6% commission while new business pays 8-10%+. 
                      Brokers with lower CoS % are pushing more renewals, which improves our overall margin. 
                      High CoS brokers may be focused only on new business.
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              {/* Data info */}
              <p className="text-xs text-muted-foreground text-center">
                Data from Airtable Broker Invoices • {costOfSalesData.summary?.total_records || 0} records • Last updated: {costOfSalesData.last_updated ? new Date(costOfSalesData.last_updated).toLocaleString("en-GB") : 'N/A'}
              </p>
            </>
          ) : (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <p className="text-muted-foreground">No cost of sales data available</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Data Info */}
      <p className="text-xs text-muted-foreground mt-6 text-center">
        Data pulled from Monday.com • {data.total_items_analyzed?.toLocaleString() || 0} items analyzed
      </p>

      {/* Broker Editor Dialog */}
      <Dialog open={brokerEditorOpen} onOpenChange={setBrokerEditorOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Broker Performance Editor
            </DialogTitle>
            <DialogDescription>
              View and adjust individual broker metrics to model "what-if" scenarios. Changes update the forecast in real-time.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Summary of active overrides */}
            {Object.keys(brokerOverrides).length > 0 && (
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-sm text-purple-700">
                  <strong>{Object.keys(brokerOverrides).length}</strong> broker(s) with custom rates
                </p>
              </div>
            )}

            {/* Broker Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-100">
                    <th className="text-left py-2 px-2 font-medium">Broker</th>
                    <th className="text-center py-2 px-2 font-medium">
                      <div className="text-xs">6m Avg</div>
                      <div>Apps/mo</div>
                    </th>
                    <th className="text-center py-2 px-2 font-medium bg-blue-50">
                      <div className="text-xs">Current</div>
                      <div>Approval %</div>
                    </th>
                    <th className="text-center py-2 px-2 font-medium bg-red-50">
                      <div className="text-xs">Current</div>
                      <div>Rejection %</div>
                    </th>
                    <th className="text-center py-2 px-2 font-medium bg-green-50">
                      <div className="text-xs">Scenario</div>
                      <div>Approval %</div>
                    </th>
                    <th className="text-center py-2 px-2 font-medium bg-green-50">
                      <div className="text-xs">Scenario</div>
                      <div>Rejection %</div>
                    </th>
                    <th className="text-center py-2 px-2 font-medium bg-green-50">
                      <div className="text-xs">Scenario</div>
                      <div>Apps Growth</div>
                    </th>
                    <th className="text-center py-2 px-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.broker_stats_full || [])
                    .filter(b => b.name && b.name !== 'Direct/Unknown' && b.total_applications >= 20)
                    .sort((a, b) => (b.nb_value || 0) - (a.nb_value || 0))
                    .slice(0, 15)
                    .map((broker, idx) => {
                      const monthlyApps = Math.round((broker.total_applications || 0) / 12);
                      const currentApproval = broker.approval_rate || 0;
                      const currentRejection = broker.rejection_rate || 0;
                      const override = brokerOverrides[broker.name] || {};
                      const hasOverride = Object.values(override).some(v => v !== null && v !== undefined);
                      
                      // Calculate monthly trends for sparklines
                      const monthlyData = (broker.monthly || []).slice(-6).map((m, i) => {
                        const approvalRate = m.applications > 0 ? (m.offers / m.applications * 100) : 0;
                        const conversionRate = m.offers > 0 ? (m.funded / m.offers * 100) : 0;
                        return {
                          month: m.month,
                          apps: m.applications,
                          approvalRate: approvalRate,
                          conversionRate: conversionRate,
                          funded: m.funded
                        };
                      });
                      
                      // Calculate apps growth trend (last 3 months vs previous 3 months)
                      const recent3 = monthlyData.slice(-3);
                      const prev3 = monthlyData.slice(0, 3);
                      const recentAvg = recent3.length > 0 ? recent3.reduce((s, m) => s + m.apps, 0) / recent3.length : 0;
                      const prevAvg = prev3.length > 0 ? prev3.reduce((s, m) => s + m.apps, 0) / prev3.length : 0;
                      const appsGrowthTrend = prevAvg > 0 ? ((recentAvg - prevAvg) / prevAvg * 100) : 0;
                      
                      return (
                        <tr 
                          key={broker.name} 
                          className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${hasOverride ? 'ring-1 ring-purple-300' : ''}`}
                        >
                          <td className="py-2 px-2 font-medium">
                            <HoverCard openDelay={200}>
                              <HoverCardTrigger asChild>
                                <button className="text-left hover:text-blue-600 flex items-center gap-1 cursor-pointer">
                                  {broker.name}
                                  <Info className="w-3 h-3 text-slate-400" />
                                </button>
                              </HoverCardTrigger>
                              <HoverCardContent className="w-80 p-3" side="right">
                                <div className="space-y-3">
                                  <div className="flex justify-between items-center border-b pb-2">
                                    <span className="font-semibold text-sm">{broker.name}</span>
                                    <Badge variant="outline" className="text-[10px]">Last 6 months</Badge>
                                  </div>
                                  
                                  {/* Apps per Month Sparkline */}
                                  <div>
                                    <div className="flex justify-between items-center mb-1">
                                      <span className="text-xs text-slate-500">Apps/Month</span>
                                      <span className="text-xs font-medium">{monthlyApps} avg</span>
                                    </div>
                                    <ResponsiveContainer width="100%" height={40}>
                                      <BarChart data={monthlyData}>
                                        <Bar dataKey="apps" fill="#6366f1" radius={[2, 2, 0, 0]} />
                                        <Tooltip 
                                          contentStyle={{ fontSize: '10px', padding: '4px 8px' }}
                                          formatter={(v) => [v, 'Apps']}
                                          labelFormatter={(l) => monthlyData[l]?.month || l}
                                        />
                                      </BarChart>
                                    </ResponsiveContainer>
                                  </div>
                                  
                                  {/* Approval Rate Trend */}
                                  <div>
                                    <div className="flex justify-between items-center mb-1">
                                      <span className="text-xs text-slate-500">Approval Rate</span>
                                      <span className="text-xs font-medium">{currentApproval.toFixed(1)}%</span>
                                    </div>
                                    <ResponsiveContainer width="100%" height={40}>
                                      <LineChart data={monthlyData}>
                                        <Line 
                                          type="monotone" 
                                          dataKey="approvalRate" 
                                          stroke="#10b981" 
                                          strokeWidth={2}
                                          dot={{ r: 2, fill: '#10b981' }}
                                        />
                                        <Tooltip 
                                          contentStyle={{ fontSize: '10px', padding: '4px 8px' }}
                                          formatter={(v) => [`${v.toFixed(1)}%`, 'Approval']}
                                        />
                                      </LineChart>
                                    </ResponsiveContainer>
                                  </div>
                                  
                                  {/* Conversion Rate Trend */}
                                  <div>
                                    <div className="flex justify-between items-center mb-1">
                                      <span className="text-xs text-slate-500">Conversion Rate</span>
                                      <span className="text-xs font-medium">{broker.conversion_rate?.toFixed(1) || 0}%</span>
                                    </div>
                                    <ResponsiveContainer width="100%" height={40}>
                                      <LineChart data={monthlyData}>
                                        <Line 
                                          type="monotone" 
                                          dataKey="conversionRate" 
                                          stroke="#f59e0b" 
                                          strokeWidth={2}
                                          dot={{ r: 2, fill: '#f59e0b' }}
                                        />
                                        <Tooltip 
                                          contentStyle={{ fontSize: '10px', padding: '4px 8px' }}
                                          formatter={(v) => [`${v.toFixed(1)}%`, 'Conv.']}
                                        />
                                      </LineChart>
                                    </ResponsiveContainer>
                                  </div>
                                  
                                  {/* Apps Growth Insight */}
                                  <div className="pt-2 border-t">
                                    <div className="flex justify-between items-center">
                                      <span className="text-xs text-slate-500">Growth Trend</span>
                                      <span className={`text-xs font-bold ${appsGrowthTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {appsGrowthTrend >= 0 ? '+' : ''}{appsGrowthTrend.toFixed(0)}%
                                      </span>
                                    </div>
                                    <p className="text-[10px] text-slate-400 mt-1">
                                      {appsGrowthTrend >= 10 
                                        ? '📈 Growing fast - expect more apps'
                                        : appsGrowthTrend >= 0 
                                          ? '➡️ Stable volume'
                                          : appsGrowthTrend >= -10
                                            ? '📉 Slight decline'
                                            : '⚠️ Significant drop in volume'}
                                    </p>
                                  </div>
                                  
                                  {/* NB vs TU Summary */}
                                  <div className="pt-2 border-t grid grid-cols-2 gap-2 text-[10px]">
                                    <div className="bg-blue-50 rounded p-1.5">
                                      <div className="text-slate-500">New Business</div>
                                      <div className="font-semibold">{broker.nb_apps || 0} apps</div>
                                      <div className="text-slate-400">£{((broker.nb_value || 0)/1000).toFixed(0)}k value</div>
                                    </div>
                                    <div className="bg-purple-50 rounded p-1.5">
                                      <div className="text-slate-500">Top-ups</div>
                                      <div className="font-semibold">{broker.tu_apps || 0} apps</div>
                                      <div className="text-slate-400">£{((broker.tu_value || 0)/1000).toFixed(0)}k value</div>
                                    </div>
                                  </div>
                                </div>
                              </HoverCardContent>
                            </HoverCard>
                            {hasOverride && (
                              <Badge className="ml-2 bg-purple-500 text-[10px] px-1 py-0">Modified</Badge>
                            )}
                          </td>
                          <td className="text-center py-2 px-2 text-slate-600">{monthlyApps}</td>
                          <td className="text-center py-2 px-2 bg-blue-50/50">{currentApproval.toFixed(1)}%</td>
                          <td className="text-center py-2 px-2 bg-red-50/50">
                            <Badge className={currentRejection > 60 ? 'bg-red-500' : currentRejection > 40 ? 'bg-amber-500' : 'bg-green-500'}>
                              {currentRejection.toFixed(0)}%
                            </Badge>
                          </td>
                          <td className="text-center py-2 px-2 bg-green-50/50">
                            <Input
                              type="number"
                              min="5"
                              max="50"
                              step="0.5"
                              placeholder={currentApproval.toFixed(1)}
                              value={override.approval_rate ?? ''}
                              onChange={(e) => {
                                const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                setBrokerOverrides(prev => ({
                                  ...prev,
                                  [broker.name]: { ...prev[broker.name], approval_rate: val }
                                }));
                              }}
                              className="h-7 w-16 text-center text-sm mx-auto"
                            />
                          </td>
                          <td className="text-center py-2 px-2 bg-green-50/50">
                            <Input
                              type="number"
                              min="20"
                              max="90"
                              step="1"
                              placeholder={currentRejection.toFixed(0)}
                              value={override.rejection_rate ?? ''}
                              onChange={(e) => {
                                const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                setBrokerOverrides(prev => ({
                                  ...prev,
                                  [broker.name]: { ...prev[broker.name], rejection_rate: val }
                                }));
                              }}
                              className="h-7 w-16 text-center text-sm mx-auto"
                            />
                          </td>
                          <td className="text-center py-2 px-2 bg-green-50/50">
                            <div className="flex flex-col items-center gap-0.5">
                              <Input
                                type="number"
                                min="-10"
                                max="20"
                                step="1"
                                placeholder={Math.round(recentAvg - prevAvg) || 0}
                                value={override.apps_growth ?? ''}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                  setBrokerOverrides(prev => ({
                                    ...prev,
                                    [broker.name]: { ...prev[broker.name], apps_growth: val }
                                  }));
                                }}
                                className="h-7 w-16 text-center text-sm mx-auto"
                              />
                              <span className={`text-[9px] ${appsGrowthTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {appsGrowthTrend >= 0 ? '↑' : '↓'}{Math.abs(appsGrowthTrend).toFixed(0)}%
                              </span>
                            </div>
                          </td>
                          <td className="text-center py-2 px-2">
                            {hasOverride && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setBrokerOverrides(prev => {
                                    const updated = { ...prev };
                                    delete updated[broker.name];
                                    return updated;
                                  });
                                }}
                                className="h-6 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Quick Presets */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm font-medium text-amber-800 mb-2">Quick Presets</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newOverrides = {};
                    (data?.broker_stats_full || [])
                      .filter(b => b.name && b.name !== 'Direct/Unknown' && b.rejection_rate > 60)
                      .forEach(b => {
                        newOverrides[b.name] = { rejection_rate: 50 };
                      });
                    setBrokerOverrides(prev => ({ ...prev, ...newOverrides }));
                    toast.success(`Set rejection rate to 50% for ${Object.keys(newOverrides).length} high-rejection brokers`);
                  }}
                  className="text-xs"
                >
                  Set all high-rejection (&gt;60%) to 50%
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newOverrides = {};
                    (data?.broker_stats_full || [])
                      .filter(b => b.name && b.name !== 'Direct/Unknown' && b.approval_rate < 20)
                      .forEach(b => {
                        newOverrides[b.name] = { ...brokerOverrides[b.name], approval_rate: 25 };
                      });
                    setBrokerOverrides(prev => ({ ...prev, ...newOverrides }));
                    toast.success(`Set approval rate to 25% for ${Object.keys(newOverrides).length} low-approval brokers`);
                  }}
                  className="text-xs"
                >
                  Set all low-approval (&lt;20%) to 25%
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setBrokerOverrides({});
                    toast.success("Cleared all broker overrides");
                  }}
                  className="text-xs text-red-600 hover:text-red-700"
                >
                  Clear All Overrides
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBrokerEditorOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
